// API client for ats-api. Same contract + auth as apps/ats-frontend: relative
// `/api` base, cookie session (`credentials: "include"`), JSON body. The nginx
// vhost for newats.hrdeva.ru proxies /api to the same ats-api.

const API_BASE = "/api";

export class ApiError extends Error {
  status: number;
  /** Parsed JSON error body, when there was one (e.g. structured 409 details). */
  body: unknown;
  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(API_BASE + path, {
    credentials: "include",
    headers: { "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let detail = res.statusText;
    let body: unknown = null;
    try {
      body = await res.json();
      const d = (body as { detail?: unknown } | null)?.detail;
      if (d) detail = typeof d === "string" ? d : detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** A POST that outlives the page (pagehide): sendBeacon, or a keepalive fetch
 *  where the beacon is refused. No answer is read and nothing is thrown. */
function beacon(path: string, body: unknown): void {
  const url = API_BASE + path;
  const json = JSON.stringify(body);
  try {
    if (navigator.sendBeacon(url, new Blob([json], { type: "application/json" }))) return;
  } catch {
    /* no sendBeacon here, or it refused the blob — the fetch below */
  }
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: json,
    keepalive: true,
  }).catch(() => undefined);
}

// ---- types (mirror ats-api schemas) ----

export type VacancyStatus = "draft" | "active" | "closed";
export type VacancyStage = "running" | "paused" | "draft" | "closed";
/** Итог (состояние) вакансии рядом со статусом: В работе / Закрыта / Мы
 *  отказались / Нам отказали / Не нашли (PRI-150). */
export type VacancyOutcome = "in_work" | "closed" | "we_declined" | "they_declined" | "not_found";

export interface RecruiterBrief {
  id: string;
  full_name: string;
  has_avatar: boolean;
}

export interface VacancyListItem {
  id: string;
  // Public human-facing identifier used in the UI and URLs (/vacancies/566).
  number: number;
  title: string;
  status: VacancyStatus;
  stage: VacancyStage;
  has_project: boolean;
  company_id: string | null;
  company_name: string | null;
  company_has_logo: boolean;
  recruiter: RecruiterBrief | null;
  hh_mapping_count: number;
  applications_count: number;
  scheduled_meetings_count: number;
  days_in_work: number;
  stage_counts: Record<string, number>;
  hot_count: number;
  total_candidates: number;
  created_at: string;
  link_pending: boolean;
  outcome: VacancyOutcome;
  /** Отрасль заказчика и сфера вакансии — свободный ввод (PRI-151). */
  industry: string | null;
  sphere: string | null;
  /** Кандидатур с видео-интервью (запись встречи или видео-вложение). */
  video_count: number;
  /** Процент активности 0–100; null — не в работе или нет кандидатур. Формула:
   *  доля из последних 14 дней, когда по вакансии были отклики или движение
   *  кандидатов (ats_api/vacancy_state.py). */
  activity_pct: number | null;
}

export interface VacancyListOut {
  items: VacancyListItem[];
  /** Вакансий в каждом статусе по всей базе (draft | active | closed). */
  status_counts: Record<VacancyStatus, number>;
}

/** Вакансия для выпадающего списка — без подсчётов по кандидатам. Полная
 * выдача `/vacancies` на 563 строки стоит 315 мс и 278 КБ, а `<select>` из
 * неё читает три поля. */
export interface VacancyOption {
  id: string;
  number: number;
  title: string;
  status: VacancyStatus;
  stage: VacancyStage;
  company_name: string | null;
  link_pending: boolean;
}

export interface HhMappingOut {
  id: string;
  hh_vacancy_id: string;
  note: string | null;
  archived: boolean;
}

// Subset of ats-api VacancyDetailOut the detail page consumes.
export interface VacancyDetail {
  id: string;
  // Public human-facing identifier used in the UI and URLs (/vacancies/566).
  number: number;
  title: string;
  description: string | null;
  status: VacancyStatus;
  stage: VacancyStage;
  created_at: string;
  company_id: string | null;
  company_name: string | null;
  company_has_logo: boolean;
  recruiter: RecruiterBrief | null;
  hh_mappings: HhMappingOut[];
  customer_contact_name: string | null;
  customer_contact_role: string | null;
  customer_contact_email: string | null;
  customer_contact_phone: string | null;
  automation_mode: "off" | "shadow" | "owned";
  /** Режим, который прервала «Пауза», — к нему возвращает «Возобновить».
   *  null — вакансия не на паузе (или пауза старше этого поля). */
  automation_mode_before_pause: "shadow" | "owned" | null;
  outcome: VacancyOutcome;
  sphere: string | null;
  video_count: number;
  activity_pct: number | null;
  /** Участники подбора, кроме ответственного: кто двигал кандидатов, назначал
   *  встречи, писал вводные. */
  participants: RecruiterBrief[];
  /** Юрлицо заказчика, на которое заведена вакансия (PRI-153). */
  legal_entity_id: string | null;
  legal_entity_name: string | null;
  /** Профиль из формы создания: требования, формат, финансы допсоглашения. */
  profile: VacancyProfile;
  /** Шаги «Отклики» (Нет этапа / Вручную / Автоматически) и правила «Общей»
   *  воронки: по ним доска прячет пустую колонку пропущенного этапа. Старый
   *  бэкенд поля не отдаёт. */
  automation_steps?: AutomationSteps;
  /** Длительность интервью, минут (15 | 20 | 30 | 45 | 60 | 90): по ней
   *  нарезаются слоты записи к рекрутёру для кандидатов этой вакансии. */
  interview_duration_min: number;
  stage_counts: Record<string, number>;
  hot_count: number;
  total_candidates: number;
}

/** Что форма «Новая вакансия» сохранила в самой вакансии (не в брифе). */
export interface VacancyProfile {
  work_format: VacancyWorkFormat | null;
  requirements: Record<
    "experience" | "hard_skills" | "soft_skills" | "qualities" | "education" | "stop_factors",
    string | null
  >;
  candidate_handover: string | null;
  finance: {
    number: string | null;
    signed_on: string | null;
    total_cost: number | null;
    staff_count: number | null;
    prep_percent: number | null;
    guarantee_months: number | null;
    payment_terms: string | null;
    /** Считается на сервере: «Подготовительные работы» и «Бонус за каждого успешного», ₽. */
    prep_cost: number | null;
    bonus_per_candidate: number | null;
  };
}

export interface KanbanCandidate {
  id: string;
  person_name: string | null;
  photo_url: string | null;
  stage: string;
  status: string;
  scoring_total: number | null;
  scoring_max: number | null;
  scoring_color: "green" | "yellow" | "red" | null;
  screening_passed: boolean | null;
  flagged: boolean;
  created_at: string;
  meeting_slot_start: string | null;
  source: string | null;
  track: string | null;
  has_video: boolean;
  has_answers: boolean;
  age: number | null;
  gender: string | null;
  // Откликнулся на трафиковую публикацию hh, привязанную к этой вакансии
  // (ats_api.traffic) — на доске такие лежат группой «Трафик». У ats-api
  // старой сборки поля нет.
  from_traffic?: boolean;
  // Non-null when the candidate is parked awaiting a manual «Подтвердить/
  // Отказать» decision (stage 2 «Подумать» → bot_invite, stage 4 → meeting_confirm).
  manual_review_stage: "bot_invite" | "meeting_confirm" | null;
  // Дальше — поля карточки доски (PRI-183, PRI-186, PRI-189); у ats-api старой
  // сборки их нет, и каждое читается как «не знаем».
  /** Человек: нужен диалогу «Отказать» с плитки (чаты, куда уйдёт отказ). */
  person_id?: string | null;
  /** Непрочитанные входящие человека: синяя точка «Новые сообщения» и счёт
   *  «Требуют внимания». */
  unread?: number;
  /** Причина отказа для чипа и блока фильтров; только у status `not_passed`. */
  reject_reason?: string | null;
  /** Название резюме, опыт (месяцев) и образование — «Список», «Должность»,
   *  «Опыт, лет», «Образование». */
  resume_title?: string | null;
  experience_months?: number | null;
  education?: "higher" | "secondary" | null;
  /** Способ приглашения «Приглашённых» (блок фильтров «Способ приглашения»). */
  invite_via?: InviteVia | null;
  /** Откуда рекомендация: плашка на карточке этапа «Рекомендован». */
  recommended_from?: "hh" | "own" | null;
}

/** `board_facets.INVITE_WAYS`. `paid_hh` сервер пока не отдаёт (платных приглашений нет). */
export type InviteVia = "free_hh" | "free_base" | "paid_hh" | "recommendation";

export interface KanbanColumn {
  stage: string;
  label: string;
  candidates: KanbanCandidate[];
}

export interface KanbanOut {
  columns: KanbanColumn[];
  /** Флаг `funnels.new_stages`: «Ждут решения» и «Тестовое выполнено» можно писать.
   *  Старый сервер поля не шлёт — читается как выключенный. */
  new_stages?: boolean;
}

// ---- Вкладка вакансии «Аналитика» (ats-api analytics_tab.py) ----

export type AnalyticsUnit = "day" | "week" | "month";

/** Счётчики «Активности» за деление или за весь период. */
export interface AnalyticsCounts {
  /** отклики на трафиковые публикации hh, привязанные к вакансии (в `applied`
   *  и `invited` их нет) */
  traffic: number;
  applied: number;
  invited: number;
  /** встреч проведено */
  meetings: number;
  /** кандидатур, продвинувшихся по воронке (без повторов) */
  moved: number;
  shared: number;
  hired: number;
}

export interface AnalyticsBucket extends AnalyticsCounts {
  /** начало деления, YYYY-MM-DD */
  start: string;
  /** «Накопом» для движения: разные кандидатуры с начала периода по это
   * деление (сумма `moved` по дням посчитала бы человека дважды) */
  moved_cum: number;
}

export interface AnalyticsReason {
  reason: string;
  count: number;
}

/** Строка «Эффективности». У шагов без данных (`tracked: false`) цифры null. */
export interface AnalyticsStep {
  key: string;
  label: string;
  tracked: boolean;
  /** дошли до шага или дальше */
  count: number | null;
  traffic: number | null;
  applied: number | null;
  invited: number | null;
  /** доля от первого шага группы с данными, 0..1 */
  share: number | null;
  /** медиана дней на шаге до следующего */
  duration_days: number | null;
  rejected: number | null;
  /** все причины отказа на шаге, частые первыми */
  reasons: AnalyticsReason[];
}

export interface AnalyticsGroup {
  key: "traffic" | "applied" | "invited" | "common";
  title: string;
  rows: AnalyticsStep[];
}

/** Нормы активности — у серий, у которых есть план. */
export type AnalyticsNormKey = "invited" | "meetings" | "shared" | "hired";

/** Плановые нормы вакансии, пересчитанные на период. Нет у ats-api старой сборки. */
export interface AnalyticsPlan {
  /** сколько недель в периоде («всё время» — от создания вакансии) */
  weeks: number;
  /** нормы в неделю; null — нормы нет */
  norms: Record<AnalyticsNormKey, number | null>;
  /** норма на период — то, что «100%»; null — нормы нет */
  targets: Record<AnalyticsNormKey, number | null>;
  /** менять нормы может только ответственный за вакансию */
  can_edit: boolean;
}

export interface VacancyAnalyticsBoard {
  range: { from: string; to: string; unit: AnalyticsUnit; tz: string };
  /** день первой кандидатуры (или создания вакансии) */
  first_date: string;
  activity: { buckets: AnalyticsBucket[]; totals: AnalyticsCounts };
  efficiency: { cohort: number; groups: AnalyticsGroup[] };
  /** причины отказов периода, частые первыми — порядок цветов сегментов */
  reasons: AnalyticsReason[];
  plan?: AnalyticsPlan;
}

/** Период запроса; без from/to — «всё время». */
export interface AnalyticsQuery {
  from?: string;
  to?: string;
  tz: string;
}

function analyticsQs(q: AnalyticsQuery): string {
  const p = new URLSearchParams({ tz: q.tz });
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  return p.toString();
}

/** Выборка за цифрой аналитики (`analytics_tab.drill`): столбик дня или итог
 *  (`activity`, серии `metrics`, дни from…to), строка «Эффективности» (`step`)
 *  или её отказы (`rejected`, причины `reasons`; пусто — все). */
export type AnalyticsDrillSelection =
  | { kind: "activity"; metrics: (keyof AnalyticsCounts)[] }
  | { kind: "step"; group: string; step: string }
  | { kind: "rejected"; group: string; step: string; reasons?: string[] };

export interface AnalyticsDrillRow {
  id: string;
  person_id: string | null;
  name: string | null;
  stage: string | null;
  status: string | null;
  source: string | null;
  city: string | null;
  age: number | null;
  /** Балл скоринга в процентах от максимума; null — не оценивали. */
  score: number | null;
}

export interface AnalyticsDrill {
  total: number;
  /** Первые семь, свежие первыми. */
  rows: AnalyticsDrillRow[];
  /** Все id — только с `ids: true` (для «Открыть в «Кандидаты»»). */
  ids?: string[];
}

function drillQs(q: AnalyticsQuery, sel: AnalyticsDrillSelection, ids: boolean): string {
  const p = new URLSearchParams(analyticsQs(q));
  p.set("kind", sel.kind);
  if (sel.kind === "activity") sel.metrics.forEach((m) => p.append("metric", m));
  else {
    p.set("group", sel.group);
    p.set("step", sel.step);
    if (sel.kind === "rejected") (sel.reasons ?? []).forEach((r) => p.append("reason", r));
  }
  if (ids) p.set("ids", "true");
  return p.toString();
}

export interface VacancyUpdatePayload {
  title?: string;
  description?: string | null;
  status?: VacancyStatus;
  company_id?: string | null;
  responsible_user_id?: string | null;
  hh_mappings?: HhMappingIn[];
  automation_mode?: "off" | "shadow" | "owned";
  /** 422 на значение не из 15 | 20 | 30 | 45 | 60 | 90. */
  interview_duration_min?: number;
  outcome?: VacancyOutcome;
  /** Сфера вакансии, свободный ввод; "" очищает. */
  sphere?: string;
  customer_contact_name?: string;
  customer_contact_role?: string;
  customer_contact_email?: string;
  customer_contact_phone?: string;
  /** Профиль из формы создания (окно «Редактировать»): блок заменяет прежний
   *  целиком, null в поле обнуляет его. Юрлицо только меняется, не сбрасывается. */
  legal_entity_id?: string;
  requirements?: VacancyRequirementsPayload;
  finance?: VacancyFinancePayload;
  /** "" очищает формат. */
  work_format?: VacancyWorkFormat | "";
  /** "" очищает. */
  candidate_handover?: string;
}

/** Ответ лёгкой ручки смены статуса/итога. */
export interface VacancyStateOut {
  status: VacancyStatus;
  outcome: VacancyOutcome;
  stage: VacancyStage;
}

export interface UserOut {
  id: string;
  email: string;
  full_name: string;
  role: string;
  ui_theme?: string;
  ui_accent_hue?: number;
}

export interface HhAccount {
  id: string;
  manager_id: string | null;
  account_name: string | null;
  // Компания hh, к которой относится аккаунт (из hh при подключении). null —
  // ещё не определена (аккаунт подключён раньше): читается как основная.
  employer_id?: string | null;
  employer_name?: string | null;
  is_primary: boolean;
  resume_fetch: boolean;
  // Подключён только под трафик («Трафик»): аккаунт другой компании, не основной
  // и не в пуле; в личные списки не попадает.
  traffic_only?: boolean;
  expires_at: string;
  // hh refused to refresh the token — the account must be reconnected.
  refresh_error?: string | null;
  needs_reconnect?: boolean;
}
export interface HhOauthStatus {
  connected: boolean;
  hh_account_name?: string | null;
  expires_at?: string;
  accounts: HhAccount[];
}
/** Zoom рекрутёра (ats-api zoom.py). `configured: false` — интеграция на
 *  сервере выключена, карточку в настройках не показываем. */
export interface ZoomStatus {
  configured: boolean;
  connected: boolean;
  account_name?: string | null;
  email?: string | null;
  // Zoom больше не принимает токен (приложение удалили в Zoom) — переподключить.
  needs_reconnect?: boolean;
}
export interface HhAdminToken extends HhAccount {
  user_id: string;
  user_name: string;
  daily_view_count: number;
  quota_reset_at: string | null;
  exhausted_until: string | null;
  last_429_at: string | null;
}
/** Компания среди подключённых аккаунтов — пункт фильтра пула. Аккаунты без
 *  компании посчитаны в основной. */
export interface HhCompany {
  employer_id: string | null;
  name: string | null;
  is_main: boolean;
  accounts: number;
}

// ---- traffic publications (ats_api/traffic.py) ----

export type TrafficHhStatus = "active" | "closed";
export type TrafficDeclineMode = "none" | "all";

export interface TrafficVacancyBrief {
  id: string;
  number: number;
  title: string;
  company_name: string | null;
}

export interface TrafficPublication {
  id: string;
  hh_vacancy_id: string;
  hh_url: string;
  hh_title: string | null;
  hh_employer_name: string | null;
  hh_employer_logo_url: string | null;
  hh_status: TrafficHhStatus;
  created_at: string;
  applications_count: number;
  vacancy: TrafficVacancyBrief | null;
  // Для привязанной публикации отражает VacancyReplyAutomation.stage3_mode
  // этой вакансии, а не собственные поля — delay/template тогда всегда null.
  decline_mode: TrafficDeclineMode;
  decline_delay_hours: number | null;
  decline_template: string | null;
  hh_oauth_token_id: string | null;
  // Подключённый рабочий hh-аккаунт владеет публикацией — отклики читаются и
  // на них можно ответить. false — красный крестик «Нет доступа к публикации».
  has_access: boolean;
}

export interface TrafficResolveOut {
  hh_vacancy_id: string;
  title: string | null;
  employer_name: string | null;
  employer_logo_url: string | null;
}

export interface TrafficPublicationInput {
  hh_url?: string;
  vacancy_id?: string | null;
  decline_mode?: TrafficDeclineMode;
  decline_delay_hours?: number | null;
  decline_template?: string | null;
  hh_oauth_token_id?: string | null;
}

// ---- telegram-user-service (userbot QR-login, sysadmin only) ----

export type TgAccountState = "pending" | "active" | "disabled" | "revoked";

export interface TgAccount {
  id: string;
  label: string;
  phone: string | null;
  tg_user_id: number | null;
  state: TgAccountState;
  last_connected_at: string | null;
  disabled_at: string | null;
  created_at: string;
}

export type TgLoginStatus =
  | "idle"
  | "awaiting_scan"
  | "awaiting_password"
  | "ok"
  | "expired"
  | "error";

export interface TgLoginState {
  status: TgLoginStatus;
  qr_url: string | null;
  expires_at: number | null;
  error: string | null;
}

// ---- gateway seats (Settings → Юзерботы, sysadmin only) ----
//
// Одно место — один контейнер с личным аккаунтом в мессенджере. Мест отсюда не
// заводят: место — это контейнер (accounts.toml, порт, том, токен), а не строка
// в базе. Экран показывает то, что есть, и даёт войти и включить.

export interface SeatLive {
  reachable: boolean;
  error?: string | null;
  /** Строка состояния гейтвея: ready | qr | connecting | needs_qr … */
  state?: string;
  connected?: boolean;
  /** Вошёл ли контейнер в аккаунт мессенджера. Без этого включать нечего. */
  authenticated?: boolean;
  /** Почему доставка остановлена, если остановлена. Гейтвей при этом
   *  остаётся connected и authenticated — снаружи место выглядит здоровым. */
  blocked?: string | null;
  /** Сколько событий ждут доставки к нам. Растущая очередь — поломка, которую
   *  не видно ни по одному статусу. */
  queue_pending?: number | null;
  /** Кто именно вошёл. Место, залогиненное не тем аккаунтом, выглядит
   *  совершенно здоровым. */
  me?: { phone: string | null; name: string | null } | null;
}

export interface Seat {
  id: string;
  type: string;
  messenger: string;
  account_key: string | null;
  name: string;
  status: string;
  notes: string | null;
  configured: boolean;
  live: SeatLive;
}

export interface SeatQr {
  state: string | null;
  /** Готовая картинка от гейтвея. */
  data_url: string | null;
  /** Ссылка, если картинки нет, — рисуем сами. */
  link: string | null;
  expires_at?: string | null;
  error?: string | null;
  /**
   * Место ждёт облачный пароль (2FA) — это состояние наступает уже ПОСЛЕ
   * сканирования, и видно оно только здесь. `hint` — подсказка мессенджера.
   *
   * `rejected` — сервер отверг прошлый пароль. Отдельным полем, потому что
   * состояние при этом не меняется: телеграм проверяет пароль уже после того,
   * как ручка ответила «принято», и без флага неверный пароль выглядел бы в
   * точности как верный.
   */
  two_fa?: { hint: string | null; rejected?: boolean } | null;
}

// ---- recruiter bot notification links (Settings → Подключить TG/MAX) ----

export interface BotLink {
  channel: "telegram" | "max";
  linked: boolean;
  external_username?: string | null;
  external_full_name?: string | null;
  linked_at?: string | null;
}

export interface BotLinksList {
  telegram: BotLink;
  max: BotLink;
  telegram_bot_username: string;
  max_bot_link_base: string;
}

/** The inbox bell on a person's chat (chat-service recruiter_notify):
 *  `channels` — the viewer's connected messengers it delivers to. */
export interface ChatBellState {
  enabled: boolean;
  channels: ("telegram" | "max")[];
}

export interface BotLinkChallenge {
  token: string;
  channel: "telegram" | "max";
  deep_link: string;
  qr_payload: string;
  expires_at: string;
}

export interface BotLinkChallengeStatus {
  token: string;
  linked: boolean;
  expired: boolean;
  link: BotLink | null;
}

// ---- global candidates page (Ф1) ----

export interface CandidatesPageItem {
  /** "candidate" — кандидатура; "resume" — резюме, на которое её ещё не завели. */
  kind: "candidate" | "resume";
  id: string;
  person_id: string | null;
  hh_resume_id: string | null;
  name: string | null;
  photo_url: string | null;
  age: number | null;
  city: string | null;
  resume_title: string | null;
  vacancy_id: string | null;
  vacancy_title: string | null;
  company_id: string | null;
  company_name: string | null;
  company_has_logo: boolean;
  scoring_pct: number | null;
  scoring_color: "green" | "yellow" | "red" | null;
  stage: string | null;
  status: string | null;
  source: string | null;
  created_at: string;
  /** Ждёт решения рекрутёра — красная точка (ручная проверка или «Анкета получена»). */
  needs_decision?: boolean;
  /** Непрочитанных входящих у человека; синяя точка, пока нет красной. */
  unread_count?: number;
  /** Итог скрининга: всё пройдено / не прошли только необязательные / не пройден обязательный. */
  screening_state?: "yes" | "mid" | "no" | null;
  screening_passed?: number | null;
  screening_total?: number | null;
  /** Все фото человека для листания по плитке; пусто, если фото меньше двух. */
  photo_urls?: string[];
  /** Воронка кандидатуры; у резюме без кандидатуры её нет. Старый сервер поля не шлёт. */
  track?: FunnelTrack | null;
}

// ---- три воронки по типу кандидата (shared_db.funnels; lib/funnels.generated.json) ----

/** Воронка кандидатуры — `Candidate.track`; пусто в базе читается как `applied`. */
export type FunnelTrack = "applied" | "invited" | "traffic";

/** Пункт воронки, где кандидатура лежит сейчас. */
export interface StageColumn {
  /** `applied` / `invited` / `traffic` или `common` («Общая»). */
  group: string;
  group_label: string;
  key: string;
  label: string;
}

/** Пункт меню этапа: пройденные — `past`, текущий — `current`, впереди — `future`. */
export interface StageChainItem {
  key: string;
  group: string;
  label: string;
  /** Этап (значение CandidateStage), который ставит выбор пункта. */
  stage: string;
  /** Технический этап («Вошёл в бот», «Открыл лендинг»): отступ в меню. */
  tech: boolean;
  state: "past" | "current" | "future";
}

/** Куда ведёт стрелка «Перевести на этап «…»» — следующий не технический пункт. */
export interface NextStage {
  key: string;
  stage: string;
  label: string;
}

/** Строка счётчика рельса «Кандидаты». */
export interface FacetOption {
  /** Статус, воронка или токен этапа `<группа>.<колонка>` (`applied.qual`). */
  key: string;
  label: string;
  count: number;
  /** Статус: work / ok / rej; этап: a…f (цвет точки sd-a…sd-f эталона). */
  dot?: string | null;
  /** Только у этапа: ключ и подпись группы, технический ли этап. */
  group?: string | null;
  group_label?: string | null;
  tech?: boolean;
}

/** `GET /candidates-facets/funnels`: каждое измерение посчитано с фильтрами остальных,
 *  но без своего; `total` — под всеми сразу. */
export interface CandidatesFacets {
  status: FacetOption[];
  track: FacetOption[];
  stage: FacetOption[];
  total: number;
}

/** Значение списка флажков рельса «Кандидаты» («Добавлен как», «Вакансия», «Рекрутер»)
 *  с числом кандидатур (PRI-267). */
export interface PickerOption {
  /** Ключ источника или id вакансии / рекрутёра — то, что уходит в фильтр. */
  key: string;
  label: string;
  count: number;
  /** Вакансия: публичный номер, заказчик и логотип (`companyLogoUrl(company_id)`). */
  number?: number | null;
  company_id?: string | null;
  company_name?: string | null;
  has_logo?: boolean;
  /** Рекрутёр: аватар (`userAvatarUrl(key)`). */
  has_avatar?: boolean;
}

/** `GET /candidates-facets/pickers`: каждый список посчитан под фильтрами остальных,
 *  но без своего; выбранные значения остаются и с нулём. */
export interface CandidatesPickers {
  source: PickerOption[];
  vacancy: PickerOption[];
  recruiter: PickerOption[];
}

/** Ответ `POST /candidates/{id}/stage`. Воронка и пункт — у сервера с проектом
 *  воронок; старый шлёт только `ok`. */
export interface ChangeStageOut {
  ok: boolean;
  /** Этап, на котором кандидат оказался: действия шага, в который он вошёл, могли
   *  увести его дальше выбранного («Общения нет» — на «Ждут решения»). */
  stage?: string;
  /** Что запустил вход в этап: `invite_queued` / `think_queued` / `no_chat`. */
  entry?: string | null;
  track?: FunnelTrack;
  column?: StageColumn;
}

/** «Откуда кандидат» при ручном добавлении (shared_db ADDED_VIA; подписи — lib/addedVia). */
export type AddedVia = "sourcing" | "recommendation" | "own_base" | "hh" | "landing" | "self";

/** Куда встанет НОВАЯ кандидатура при ручном добавлении и что ещё выбрал рекрутёр.
 *  Не задано — «Приглашения», «Передан рекрутёру». «Трафик» руками выбрать нельзя.
 *  `run_screening` — скрининг и скоринг в фоне (без сообщений кандидату, вердикт
 *  только в карточку — статус и этап не меняются), только для созданной кандидатуры; у «Добавить в вакансию» из карточки человека его нет. */
export interface CandidatePlacement {
  track?: Exclude<FunnelTrack, "traffic">;
  stage?: string;
  added_via?: AddedVia;
  run_screening?: boolean;
}

export interface AttachResumeOut {
  ok: boolean;
  created: boolean;
  candidate_id: string;
  person_id: string | null;
  vacancy_id: string;
  vacancy_title: string;
  vacancy_number: number;
}

export interface CandidatesPageOut {
  items: CandidatesPageItem[];
  /** Capped server-side — see `total_capped`. An exact count cost 1.2 s per keystroke. */
  total: number;
  total_capped?: boolean;
  /** Whether another page exists. Use this for infinite scroll, not `total`. */
  has_more?: boolean;
  /** Сколько кандидатур ждут решения (число на «Требуют внимания»); только в первой странице. */
  attention_count?: number | null;
  /** Людей с кандидатурами в каждой соцсети (vk / tg / ok / ig); только в первой странице. */
  social_counts?: Record<string, number> | null;
}

// ---- global search ----

export type SearchEntity =
  | "person"
  | "resume"
  | "vacancy"
  | "company"
  | "note"
  | "message"
  | "document";

/**
 * An excerpt of a body-text hit and where the match is in it. `text` is raw
 * user input (a candidate's message, a resume) — plain text, never markup.
 * `ranges` are half-open `[start, end)` offsets in UTF-16 code units, i.e.
 * directly usable with `String.prototype.slice`.
 */
export interface SearchSnippet {
  text: string;
  ranges: [number, number][];
}

export interface SearchItem {
  id: string;
  title: string;
  subtitle: string | null;
  /** The excerpt with the match, for body-text hits. Render with `<Highlighted>`. */
  snippet: SearchSnippet | null;
  score: number;
  /** Why this row matched: name | phone | email | handle | title | text | … */
  reasons: string[];
  avatar_url: string | null;
  extra: Record<string, unknown>;
}

export interface SearchGroup {
  entity: SearchEntity;
  label: string;
  total: number | null;
  items: SearchItem[];
}

export interface SearchOut {
  q: string;
  /** Which engine answered: "postgres" or "elasticsearch". */
  backend: string;
  took_ms: number;
  /** True when a group timed out — the page is a partial answer, and says so. */
  partial: boolean;
  groups: SearchGroup[];
}

// Резюме, заведённое рекрутёром руками: файлом, текстом или формой.
export interface ManualExperience {
  company?: string | null;
  position?: string | null;
  start?: string | null;
  end?: string | null;
  description?: string | null;
}

export interface ManualEducation {
  name?: string | null;
  organization?: string | null;
  result?: string | null;
  year?: number | null;
}

export interface ManualResumeFields {
  full_name?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  telegram?: string | null;
  city?: string | null;
  age?: number | null;
  gender?: string | null;
  salary?: number | null;
  total_experience_years?: number | null;
  skills: string[];
  about?: string | null;
  experience: ManualExperience[];
  education: ManualEducation[];
}

export interface ParsedResumeOut {
  fields: ManualResumeFields;
  /** Распознанный текст — рядом с разбором, чтобы было видно, по чему ошиблись. */
  text: string;
  /** Ничего опознаваемого не нашлось: скорее всего, это не резюме. */
  empty: boolean;
}

export interface ManualResumeOut {
  hh_resume_id: string;
  person_id: string | null;
  candidate_id: string | null;
}

// ---- candidate card (Ф2) ----

export interface AiReview {
  short_summary: string;
  strengths: string[];
  risks: string[];
  recommendation: string;
}

export interface ResumeScreeningOut {
  decision: "pass" | "reject" | "uncertain";
  llm_used: boolean;
  checks: Record<string, unknown>[];
  summary: string | null;
  ai_review: AiReview | null;
  updated_at: string;
}

export interface ScreeningResultOut {
  criterion_name: string;
  is_blocking: boolean;
  passed: boolean;
  note: string | null;
  /** Откуда вердикт: резюме или ответ кандидата на вопрос анкеты. Старый сервер поля не шлёт. */
  source?: "resume" | "answer" | string;
}

/** Критерий вакансии без вердикта по кандидату («?» в скрининге кабинета, PRI-282):
 *  нет данных в резюме, ответ не получен, проверяется по анкете. У скрининга
 *  `is_blocking`, у скоринга `weight`; `note` — чего не хватает. */
export interface UnverifiedCriterionOut {
  criterion_id: string;
  criterion_name: string;
  is_blocking?: boolean;
  weight?: number | null;
  note: string | null;
}

export interface ScoringResultOut {
  criterion_name: string;
  weight: number;
  grade: "green" | "yellow" | "red";
  points: number;
  note: string | null;
  source?: "resume" | "answer" | string;
}

/** Ответ кандидата на вопрос вакансии (PRI-166) — блок «Ответы» карточки. */
export interface QuestionAnswerOut {
  id: string;
  question_text: string;
  answer_text: string | null;
  channel: string;
  source: string;
  answered_at: string | null;
  /** Критерий, к которому привязан вопрос. */
  criterion: { id: string; text: string; category: string; kind: CriterionKind } | null;
  /** Оценка по ответу: `grade` у желательного, `passed` у обязательного и стоп-фактора.
   *  null — оценки нет (не оценивается, ответ не позволил судить). */
  result: { grade: "green" | "yellow" | "red" | null; passed: boolean | null; note: string | null } | null;
}

export interface DialogMessageOut {
  id: string;
  direction: "incoming" | "outgoing";
  channel: string;
  text: string | null;
  created_at: string;
}

export interface PersonSocial {
  id: string;
  // Stored label, inconsistent across sources ("telegram", "Вконтакте", "other") —
  // render through pages/Candidates/card/socials.ts.
  platform: string;
  handle: string | null;
  url: string;
}

// Одно из фото человека (резюме hh, мессенджер, расширение…): `own` — фото
// резюме самой кандидатуры; список приходит с ним первым.
export interface CandidatePhoto {
  id: string;
  url: string;
  source: "hh" | "messenger" | "extension" | "manual" | "import" | string;
  created_at: string | null;
  own: boolean;
}

export interface CandidateDetail {
  id: string;
  person_id: string;
  person_name: string | null;
  photo_url: string | null;
  // Все фото человека; первое совпадает с photo_url. Старый сервер поля не шлёт.
  photos?: CandidatePhoto[];
  resume_hh_id: string | null;
  hh_url: string | null;
  vacancy_id: string;
  vacancy_number: number | null;
  vacancy_title: string;
  company_name: string | null;
  stage: string;
  status: string;
  created_at: string;
  contacts: Record<string, string>;
  phones: string[]; // digits, "79215550430"
  emails: string[];
  socials: PersonSocial[];
  screening: ScreeningResultOut[];
  scoring: ScoringResultOut[];
  scoring_total: number | null;
  scoring_max: number | null;
  scoring_color: "green" | "yellow" | "red" | null;
  /** Критерии без вердикта: «?» в скрининге кабинета. Старый сервер поля не шлёт. */
  screening_unverified?: UnverifiedCriterionOut[];
  scoring_unverified?: UnverifiedCriterionOut[];
  /** Ответы на вопросы вакансии; пусто, пока оценка по ответам выключена. Старый сервер поля не шлёт. */
  question_answers?: QuestionAnswerOut[];
  /** Когда оценка по ответам обновлялась в последний раз («тихая строка пересчёта»). */
  answers_evaluated_at?: string | null;
  dialog: DialogMessageOut[];
  resume_screening: ResumeScreeningOut | null;
  reject_reason: string | null;
  // The current rejection's reasons one by one (not_passed only). Absent from
  // an older ats-api; empty — a rejection without tree reasons (old, imported).
  reject_reasons?: CandidateRejectReason[];
  // There is an hh response/negotiation the reject can move to «Отказ». Absent
  // from an older ats-api — the card then falls back to `hh_url`.
  has_hh_response?: boolean;
  // See KanbanCandidate.manual_review_stage.
  manual_review_stage: "bot_invite" | "meeting_confirm" | null;
  // Latest verdict a client left on one of the candidate's share links.
  client_decision?: {
    decision: ClientDecision;
    decided_at: string | null;
    link_id: string;
    reason?: string | null;
    comment?: string | null;
  } | null;
  // Воронка, её подпись, меню этапа и стрелка «на следующий этап» — считает
  // сервер (shared_db.funnels). Старый сервер полей не шлёт.
  track?: FunnelTrack;
  funnel_label?: string | null;
  stage_column?: StageColumn | null;
  stage_chain?: StageChainItem[];
  next_stage?: NextStage | null;
  /** «Откуда кандидат» из формы ручного добавления; у остальных — null. */
  added_via?: AddedVia | null;
}

export interface ResumeView {
  full_name: string | null;
  title: string | null;
  age: number | null;
  birth_date: string | null;
  gender: string | null;
  area: string | null;
  // Optional: a card served by an older ats-api has neither.
  metro?: string | null;
  salary: string | null;
  total_experience_months: number | null;
  contacts: { type: string | null; value: string | null }[];
  experience: {
    company: string | null;
    position: string | null;
    period: string;
    description: string | null;
    // Логотип работодателя — подписанный прокси-URL; у резюме без него (импорт, бот) нет.
    company_logo?: string | null;
    company_url?: string | null;
  }[];
  // HH mapping: name = учебное заведение (ВУЗ), organization = факультет,
  // result = специальность, year = год окончания.
  education: {
    name: string | null;
    organization: string | null;
    result: string | null;
    year: number | null;
  }[];
  // Уровень образования резюме в целом («Высшее»): hh отдаёт его одним на всё резюме.
  // Нет поля — старый ats-api.
  education_level?: string | null;
  languages: string[];
  skills: string[];
  skills_text: string | null;
  // hh terms of work (relocation, business trips, schedule…), only those set.
  conditions?: { label: string; value: string }[];
}

export interface ResumeFull {
  photo_url: string | null;
  view: ResumeView;
  // Сопроводительное письмо — текст первого отклика на hh с этим резюме; нет — null
  // (или поле не приходит от старого ats-api).
  cover_letter?: string | null;
  history: {
    vacancy_name: string | null;
    vacancy_url: string | null;
    states: { state?: string | null; at?: string | null; with_message?: boolean }[];
  }[];
}

// Откуда материал — при `?scope=person` ручек карточки (файлы и записи
// человека по всем его вакансиям); без scope этих полей нет.
export interface SourceContext {
  // Кандидатура материала и вакансия, по которой он появился.
  candidate_id?: string;
  vacancy_id?: string | null;
  vacancy_title?: string | null;
  vacancy_number?: number | null;
  // Своя — с той кандидатуры, что открыта в карточке.
  own?: boolean;
}

export interface RecordingBrief extends SourceContext {
  id: string;
  recording_kind: string;
  duration_s: number | null;
  size_bytes: number | null;
  created_at: string | null;
  stream_url: string;
  /** Кадр из середины видео, подписанная ссылка (PRI-47). null — картинки нет:
   *  аудио или ещё не сделана; тогда остаётся прежняя заглушка. */
  poster_url?: string | null;
  transcript_status: string | null;
  transcript_elapsed_s: number | null;
  has_transcript: boolean;
  // How many people speak, as given on upload: null = auto, 0 = nobody speaks.
  speakers_hint?: number | null;
  /** Удалить запись может ответственный за вакансию кандидатуры и сисадмин;
   *  для остальных кнопка «Удалить» серая (сервер ответит 403). Нет поля — можно. */
  can_delete?: boolean;
}

export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  speaker?: string | null;
  role?: "candidate" | "recruiter" | "unknown" | string | null;
}

export interface TranscriptChapter {
  time: string;
  title: string;
}

// Live state of the speech job while a transcript is `processing`.
export interface TranscriptProgress {
  status: "prep" | "queued" | "running" | "done" | "failed" | "cancelled" | string | null;
  stage: "stt" | "diarize" | null;
  progress: number | null;
  stt_ready: boolean | null;
  eta_s: { stt?: number; diarize?: number; done?: number };
}

export interface RecordingTranscript {
  status: string | null;
  // While `processing`, text/segments may already hold the transcript without
  // speakers; they're replaced once speakers, roles and chapters are ready.
  text: string | null;
  segments: TranscriptSegment[];
  chapters: TranscriptChapter[];
  elapsed_s: number | null;
  language: string | null;
  transcribed_at: string | null;
  error: string | null;
  speakers_hint?: number | null;
  progress?: TranscriptProgress | null;
}

export interface AttachmentBrief extends SourceContext {
  id: string;
  kind: string;
  filename: string;
  mime?: string | null;
  size_bytes: number | null;
  created_at: string | null;
  url: string;
  /** Кадр из середины видео-вложения (PRI-47); null — не видео или картинки нет. */
  poster_url?: string | null;
}

// Комментарий рекрутёра (CandidateNote author=human): /candidates/{id}/manual-notes
// и /manual-notes/{id}, `_note_brief` в ats-api candidate_files.py.
export interface NoteBrief {
  id: string;
  text: string;
  // Виден заказчику на странице по внешней ссылке (/s/<token>), блок «Комментарии».
  visible_to_customer: boolean;
  created_at: string | null;
  updated_at: string | null;
  // `mine` — я автор; `can_edit` — я автор или администратор. Комментарии без
  // автора (перенесённые из Рекруто) правит только администратор.
  mine: boolean;
  can_edit: boolean;
  // Глазок «Видно клиенту»: у обычного комментария равен `can_edit`; у комментария
  // заказчика (`customer_verdict`) — true любому рекрутёру, хотя править его нельзя.
  can_toggle_visibility?: boolean;
  // Комментарий заказчика при одобрении / отклонении, пришедший с публичной
  // страницы: полоса слева зелёная / красная. У обычных комментариев null.
  customer_verdict?: "approved" | "rejected" | null;
  author_name: string | null;
  author_user_id: string | null;
}

// Запись ленты ИИ-наблюдателя (/candidates/{id}/notes). Лента отдаёт и
// комментарии рекрутёров (author=human) — карточка их отсюда не берёт.
export interface AiNoteBrief {
  id: string;
  text: string;
  author: string;
  importance?: string;
  source?: string | null;
  created_at: string | null;
}

export interface CandidatesFilterParams {
  q?: string;
  status?: string;
  /** Ключи источников через запятую (одно значение — как прежде). */
  source?: string;
  /** Id вакансий через запятую. */
  vacancy_id?: string;
  /** Id рекрутёров через запятую. */
  recruiter_id?: string;
  city?: string;
  gender?: string;
  age_min?: string;
  age_max?: string;
  salary_min?: string;
  salary_max?: string;
  exp_min?: string;
  exp_max?: string;
  title?: string;
  /** "1" — только ждущие решения рекрутёра. */
  attention?: string;
  metro?: string;
  /** Ключи соцсетей через запятую: vk,tg,ok,ig (хотя бы одна). */
  socials?: string;
  /** Минимум фото человека: "1"…"6" (6 — больше пяти). */
  photos_min?: string;
  /** Названия вузов через «|». */
  universities?: string;
  /** "1" — есть запись видео-интервью. */
  has_video?: string;
  /** "1" — есть резюме на hh. */
  has_hh?: string;
  /** Воронки через запятую: applied,invited,traffic. */
  track?: string;
  /** Этапы воронки — токены `<группа>.<колонка>` через запятую (`applied.qual,common.await`). */
  stage?: string;
}

/** Подсказка в поле «Вуз» рельса кандидатов. */
export interface UniversityHint {
  name: string;
  count: number;
}

export interface CompanyOut {
  id: string;
  name: string;
  has_logo: boolean;
  status: "active" | "inactive";
  industry: string | null;
  source: string | null;
  vacancies_total: number;
  vacancies_active: number;
  applications_count: number;
  /** Карточка клиента по эталону (PRI-289). */
  city: string | null;
  site: string | null;
  description: string | null;
  manager_id: string | null;
  manager_name: string | null;
  /** Процент активности 0–100: среднее по вакансиям клиента в работе, у которых
   *  есть кандидатуры (PRI-288). null — считать нечего, в списке прочерк. */
  activity_pct: number | null;
}

/** Контакт ЛПР клиента (PRI-292): лицо, принимающее решение. */
export interface CompanyContactOut {
  id: string;
  company_id: string;
  name: string;
  role: string | null;
  phone: string | null;
  email: string | null;
  note: string | null;
}

/** Тело создания и правки контакта. При правке не присланное поле не трогается,
 *  пустая строка очищает. */
export interface CompanyContactBody {
  name?: string;
  role?: string;
  phone?: string;
  email?: string;
  note?: string;
}

/** Вложение клиента (PRI-293): фото, видео, звук или файл. `url` — адрес
 *  самого файла, отдаётся с поддержкой Range (видео перематывается). */
export type CompanyMediaKind = "photo" | "video" | "audio" | "file";

export interface CompanyMediaOut {
  id: string;
  company_id: string;
  kind: CompanyMediaKind;
  filename: string;
  mime: string;
  size_bytes: number;
  created_at: string | null;
  url: string;
}

export type LegalEntityKind = "ooo" | "ip" | "ao" | "zao" | "pao";

export interface LegalEntityBody {
  company_id?: string | null;
  contract_no?: string | null;
  contract_date?: string | null;
  kind: LegalEntityKind;
  name: string;
  signatory_name?: string | null;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  inn?: string | null;
  kpp?: string | null;
  ogrn?: string | null;
  edo_id?: string | null;
  bank_account?: string | null;
  bank_name?: string | null;
  bik?: string | null;
  corr_account?: string | null;
}

export interface LegalEntityOut extends LegalEntityBody {
  id: string;
  company_name: string | null;
  company_has_logo: boolean;
}

// ---- generated legal documents ----
export type DocumentKind = "contract" | "addendum" | "act" | "invoice";

export interface DocumentOut {
  id: string;
  legal_entity_id: string;
  kind: DocumentKind;
  title: string;
  doc_date: string | null;
  updated_at: string;
  linked_document_id: string | null;
}
export interface DocumentDetailOut extends DocumentOut {
  fields: Record<string, unknown>;
}
export interface DocumentListRow extends DocumentOut {
  entity_name: string;
  entity_kind: LegalEntityKind;
  company_name: string | null;
}
export type OrgSettings = Record<string, string>;

export interface HhMappingIn {
  hh_vacancy_id: string;
  note?: string | null;
  archived?: boolean;
}

export interface VacancyCreatePayload {
  title: string;
  description?: string | null;
  status?: VacancyStatus;
  company_id?: string | null;
  new_company_name?: string | null;
  responsible_user_id?: string | null;
  hh_mappings?: HhMappingIn[];
  /** Сфера вакансии, свободный ввод. */
  sphere?: string | null;
  /** Юрлицо заказчика: должно принадлежать выбранному заказчику (PRI-153). */
  legal_entity_id?: string | null;
  /** Плановый срок закрытия, дней: уходит в бриф как «Срок закрытия» (PRI-154). */
  planned_close_days?: number | null;
  requirements?: VacancyRequirementsPayload;
  conditions?: VacancyConditionsPayload;
  selection?: VacancySelectionPayload;
  finance?: VacancyFinancePayload;
}

export type VacancyEmployment = "full" | "part" | "project" | "internship";
export type VacancyWorkFormat = "office" | "hybrid" | "remote" | "travel";
export type VacancyLevel = "intern" | "junior" | "middle" | "senior" | "lead";

/** Раздел «Требования» формы создания. */
export interface VacancyRequirementsPayload {
  experience?: string | null;
  hard_skills?: string | null;
  soft_skills?: string | null;
  qualities?: string | null;
  education?: string | null;
  stop_factors?: string | null;
}

/** Раздел «Условия»: занятость, уровень, график и адрес пишутся в бриф,
 *  обязанности — в «Для кандидата», формат — в вакансию. */
export interface VacancyConditionsPayload {
  employment?: VacancyEmployment | null;
  work_format?: VacancyWorkFormat | null;
  level?: VacancyLevel | null;
  schedule?: string | null;
  address?: string | null;
  responsibilities?: string | null;
}

/** Раздел «Отбор»: ЛПР и отбор у заказчика пишутся в бриф. */
export interface VacancySelectionPayload {
  decision_maker?: string | null;
  client_selection?: string | null;
  candidate_handover?: string | null;
}

/** Раздел «Финансы и ДС». Расчётные суммы сервер считает сам. */
export interface VacancyFinancePayload {
  number?: string | null;
  /** Дата заключения ДС, YYYY-MM-DD. */
  signed_on?: string | null;
  total_cost?: number | null;
  staff_count?: number | null;
  prep_percent?: number | null;
  guarantee_months?: 1 | 3 | 6 | 12 | null;
  payment_terms?: string | null;
}


// ---- R6-S2: external client share links ----
// Boolean flags the recruiter toggles in the card's share menu (mirrors
// ats_api.share_links.SHARE_BLOCKS). Contacts are tri-state and travel next to
// these in the `visibility` map; "decision" is not a block — it lets the client
// approve / reject on the public page.
export const SHARE_BLOCKS = [
  "photo",
  "summary",
  "experience",
  "education",
  "skills",
  "languages",
  "salary",
  "screening",
  "scoring",
  "attachments",
  "videos",
  "notes",
  "socials",
  "decision",
] as const;
export type ShareBlock = (typeof SHARE_BLOCKS)[number];
export type ContactsMode = "shown" | "hidden" | "off";

/** Статус внешней ссылки заказчику — считает сервер (ats_api.share_link_state):
 *  «Активна / Закрыта / Использована / Истекла». */
export type ShareLinkStatus = "active" | "closed" | "used" | "expired";

/** Один визит заказчика из «Истории визитов» меню ссылки. `device` — короткая
 *  подпись («iPhone · Safari»), `city` — если его назвал прокси; оба могут быть пустыми. */
export interface ShareVisit {
  link_id: string;
  at: string;
  device: string | null;
  city: string | null;
}

export interface ShareLink {
  id: string;
  token: string;
  path: string;
  visibility: Record<string, boolean | ContactsMode>;
  agency: string | null;
  active: boolean;
  /** Нет у ответа сервера постарше — тогда статус считается по `active`. */
  status?: ShareLinkStatus;
  revoked_at: string | null;
  expires_at: string | null;
  /** Выбранный срок: 0 — «Один раз», 1/3/10/30 — дней, null — бессрочная. */
  term_days?: number | null;
  single_use?: boolean;
  created_at: string | null;
  client_decision: ClientDecision | null;
  client_decided_at: string | null;
  client_reason: string | null;
  client_comment: string | null;
  // Real visits by the client (ats_api.share_view_notify: not staff, not a
  // five-second accidental open), the one still going on included, and when
  // the latest one began.
  views_count: number;
  last_viewed_at: string | null;
  // Minted by adding the candidate to the vacancy's client page — the card
  // shows it under that page, not among the links sent by hand.
  via_vacancy_link: boolean;
}

// ---- the client's page of a vacancy (/v/<token>): a list of added candidates ----

export interface VacancyShareMember {
  candidate_id: string;
  name: string;
  added_at: string | null;
  personal_link_id: string;
  client_decision: ClientDecision | null;
  client_decided_at: string | null;
}

export interface VacancyShareLink {
  id: string;
  token: string;
  path: string;
  vacancy_id: string;
  visibility: Record<string, boolean | ContactsMode>;
  agency: string | null;
  active: boolean;
  status?: ShareLinkStatus;
  revoked_at: string | null;
  expires_at?: string | null;
  term_days?: number | null;
  single_use?: boolean;
  /** Сколько раз заказчик открывал страницу и когда в последний раз. */
  visits_count?: number;
  last_visit_at?: string | null;
  created_at: string | null;
  members_count: number;
  members: VacancyShareMember[];
}

/** What the candidate card reads: an active link of its vacancy and whether the
 *  candidate is already in it. */
export type CardVacancyShareLink = Omit<VacancyShareLink, "members"> & { member: boolean };

export interface PublicVacancyCandidate {
  /** Token of the candidate's own page: `/s/<token>`. */
  token: string;
  path: string;
  name: string;
  title: string | null;
  age: number | null;
  area: string | null;
  photo_url?: string;
  /** Only when the link lets the client decide. */
  decision?: ClientDecision | null;
  decided_at?: string | null;
  /** «Передан» — когда рекрутёр добавил кандидата на страницу заказчика. */
  added_at?: string | null;
  /** Месяцев опыта — только при включённом блоке «Опыт». */
  experience_months?: number;
  /** «Соответствие», % — только при включённом блоке «Скоринг» и если он есть. */
  match_pct?: number;
}

export interface PublicVacancy {
  agency: string | null;
  vacancy_title: string;
  company_name: string | null;
  /** Логотип компании для шапки (по токену страницы); null — его нет. */
  company_logo_url?: string | null;
  decision_enabled: boolean;
  candidates: PublicVacancyCandidate[];
}

export type ClientDecision = "approved" | "rejected";

// What the public page reports while the client looks at it (mirrors
// ats_api.share_view_notify.ViewEventIn). `ref` is "rec:<recording id>" or
// "att:<attachment id>"; `played_s` — the seconds of that media actually
// played so far in this visit, a running total and not the seek position.
export type ShareViewEventType =
  | "heartbeat"
  | "media"
  | "transcript_opened"
  | "file_opened"
  | "leave";

export interface ShareViewEvent {
  type: ShareViewEventType;
  ref?: string;
  played_s?: number;
}

export interface PublicCandidate {
  name: string | null;
  title: string | null;
  age: number | null;
  area: string | null;
  agency: string | null;
  expires_at: string | null;
  vacancy_title: string | null;
  company_name: string | null;
  decision_enabled: boolean;
  decision: ClientDecision | null;
  decided_at: string | null;
  decision_reason: string | null;
  decision_comment: string | null;
  /** Only for a signed-in employee: the client never gets it. */
  candidate_id?: string;
  photo_url?: string;
  salary?: string | null;
  experience?: ResumeView["experience"];
  /** Общий стаж из резюме, месяцев — «Общий опыт» в сводке опыта. */
  total_experience_months?: number;
  /** Строка под названием резюме: «Полная занятость · Сменный график». */
  employment?: string;
  schedule?: string;
  /** «О себе» из резюме и сопроводительное письмо отклика. */
  about?: string;
  cover_letter?: string;
  /** «Высшее» / «Среднее специальное» — колонка «Уровень» таблицы образования. */
  education_level?: string;
  /** «Дополнительно»: гражданство, переезд, командировки… */
  extra?: { label: string; value: string }[];
  /** Дата рождения «1994-05-12» — только при полностью открытых контактах. */
  birth_date?: string;
  education?: ResumeView["education"];
  skills?: string[];
  languages?: string[];
  contacts?: { type: string | null; value: string | null }[];
  summary?: string;
  screening?: {
    decision: "pass" | "reject" | "uncertain" | null;
    summary: string | null;
    passed: number;
    total: number;
    items: { name: string; passed: boolean; is_blocking: boolean }[];
  };
  socials?: { platform: string; handle: string | null; url: string }[];
  scoring?: {
    items: { name: string; weight: number; grade: string; points: number; note: string | null }[];
    total: number;
    max: number;
    color: string;
  };
  attachments?: {
    id: string;
    kind: string;
    filename: string;
    mime: string;
    /** Размер файла, байт — «PDF · 1,2 МБ» в окне просмотра файла. */
    size_bytes?: number;
    url: string;
    /** Кадр из середины видео-вложения (PRI-47); null — не видео или картинки нет. */
    poster_url?: string | null;
  }[];
  // `media_kind`: "audio" | "video" — the backend infers audio from the
  // s3_key extension (since MeetingRecording doesn't store the mime type).
  videos?: {
    id: string;
    media_kind: "audio" | "video";
    duration_s: number | null;
    created_at: string | null;
    stream_url: string;
    /** Кадр из середины видео (PRI-47); null — картинки нет. */
    poster_url?: string | null;
    recording_kind?: string;
    // The page loads it on open (api.publicTranscript).
    has_transcript?: boolean;
  }[];
  notes?: { text: string; created_at: string | null }[];
}

// ---- chats inbox (Ф3, chat-service) ----

export interface ChatMessagePreview {
  channel: string | null;
  direction: string | null;
  text: string | null;
  created_at: string | null;
  // Short name of whoever wrote an OUTGOING message («Анна», «Дева»); null
  // for incoming and system rows. Absent from older chat-service builds.
  author?: string | null;
}

/** Effective chat type: a reference contact (the candidate's former manager)
 *  or a manual mark → «lpr», anyone else → «candidate». */
export type ChatKind = "candidate" | "lpr";

export interface ChatPersonItem {
  key: string;
  person_id: string | null;
  name: string;
  avatar_resume_id: string | null;
  channels: string[];
  // Incoming messages since anyone last opened the chat (shared by all
  // recruiters); `unread` = it's > 0. Optional: an older chat-service lacks it.
  unread_count?: number;
  unread: boolean;
  needs_reply: boolean;
  last_message_at: string | null;
  // NB: an OBJECT (chat-service _msg_preview), not a string.
  last_message: ChatMessagePreview | null;
  // Responsible recruiter (chat.recruiter_id); null = nobody.
  owner_id?: string | null;
  // Computed: not marked inactive, or the peer wrote after the mark.
  active?: boolean;
  kind?: ChatKind;
  // The CURRENT user has an active reminder in this chat whose time has come
  // (red mark on the row). Absent from older chat-service builds.
  reminder_due?: boolean;
  /** Открытый запрос решения / задача (chat_collab): «mine» — на том, чьё
   *  «моё решение» считает кнопка над списком, «any» — на ком-то другом. */
  attention?: "mine" | "any" | null;
}

/** Красная кнопка над списком чатов (эталон от 26.10): только чаты,
 *  требующие моего решения, ↔ все. Сервер понимает и "any" (любое решение —
 *  так было в эталоне до 26.10), интерфейс его больше не шлёт. */
export type ChatAttention = "mine" | "all";

/** Where a person came from — the same three buckets as the vacancy's
 *  «Аналитика» tab: a traffic publication on hh, the recruiter wrote first
 *  («invited»), or the person responded themselves («applied»). */
export type ChatTrack = "traffic" | "invited" | "applied";

/** «Результат» кандидатуры в фильтре чатов: в работе / выбыл (отказ, отказался
 *  сам, не дозвонились) / ждут решения заказчика / вышел на работу. */
export type ChatResult = "active" | "dropped" | "awaiting" | "hired";

/** Filter of the inbox list — the query params of /chat/people, /facets and
 *  /search/messages. Everything optional: the API default is «no filter». */
export interface ChatListFilter {
  q?: string;
  /** With `q`: search everything, ignoring every other filter. */
  global?: boolean;
  state?: "active" | "all" | "inactive";
  unread?: boolean;
  /** hh, telegram, max, telegram_userbot, max_userbot, sms, whatsapp, phone. */
  channels?: string[];
  vacancy_ids?: string[];
  kind?: ChatKind | "all";
  /** Any of these buckets; absent = all. */
  tracks?: ChatTrack[];
  /** Rows of «Этап воронки» as `group.column` (pages/Chats/list/stageFilter.ts),
   *  any of them. Stage, result, client and reject reason are conditions on ONE
   *  candidacy: the one on the picked vacancy, or any of the person's. */
  stages?: string[];
  result?: ChatResult;
  /** Clients (companies of the vacancies), any of them. */
  client_ids?: string[];
  /** Code of a node of the reject-reason tree (group, reason or detail). */
  reject_reason?: string;
  /** "all" | "me" | "none" | "iam" («Чаты, где я есть») | a user id. */
  owner?: string;
  /** Красная кнопка: "mine"; absent / "all" — не фильтр. */
  attention?: ChatAttention;
  /** «Без переписки более N дней»: the last message is older than N days. */
  silent_days?: number;
}

export interface ChatPeopleOut {
  items: ChatPersonItem[];
  /** Keyset cursor of the next page; null = this was the last one. */
  next_cursor: string | null;
  /** Entries under every filter. */
  total: number;
  /** Entries under every filter EXCEPT `unread` that have unread messages. */
  unread_chats: number;
  /** Под всеми фильтрами, кроме красной кнопки: чаты с открытым запросом
   *  решения / задачей на «мне» (или на рекрутёре, чьи чаты смотрят) и на ком
   *  угодно. Absent from older chat-service builds. */
  attention_mine?: number;
  attention_any?: number;
}

/** Facets computed on request only (`include` of /chat/people/facets). */
export type ChatFacetPart = "stages" | "clients";

/** Counts for the pickers; each facet ignores its own dimension. */
export interface ChatPeopleFacets {
  total: number;
  unread_chats: number;
  channels: { channel: string; count: number }[];
  /** `client_id` / `has_logo`: the vacancy's client and whether it has a logo
   *  (`companyLogoUrl`); absent from chat-service builds that predate the logo. */
  vacancies: {
    id: string;
    title: string;
    count: number;
    client_id?: string | null;
    has_logo?: boolean;
  }[];
  /** By chat.recruiter_id; names come from /users. */
  owners: { id: string; count: number }[];
  unassigned_count: number;
  /** «Чаты, где я есть»: ответственный, писал или добавлен. Absent from older
   *  chat-service builds. */
  member_count?: number;
  kinds: Record<ChatKind, number>;
  /** Absent from chat-service builds that predate the track filter. */
  tracks?: Record<ChatTrack, number>;
  /** People per `stages` row; a missing row is 0. Only with `include=stages`. */
  stages?: Record<string, number>;
  /** Clients with at least one matching person, biggest first. Only with `include=clients`. */
  clients?: { id: string; name: string; has_logo: boolean; count: number }[];
}

/** A message found by the inbox search («Сообщения» section). */
export interface ChatMessageHit {
  message_id: string;
  thread_id: string;
  key: string;
  person_id: string | null;
  name: string;
  avatar_resume_id: string | null;
  channel: string;
  sent_at: string;
  author: string | null;
  /** Plain text (never HTML) around the first match. */
  snippet: string;
  /** [start, end) ranges in `snippet` to highlight. */
  highlights: [number, number][];
}

export interface ChatMessageHitsOut {
  items: ChatMessageHit[];
  next_cursor: string | null;
  total: number;
  /** `total` stopped counting at the cap (1000). */
  total_capped: boolean;
}

function chatFilterParams(f: ChatListFilter, p = new URLSearchParams()): URLSearchParams {
  const q = f.q?.trim();
  if (q) p.set("q", q);
  if (f.global) p.set("global", "true");
  if (f.state) p.set("state", f.state);
  if (f.unread) p.set("unread", "true");
  if (f.channels?.length) p.set("channels", f.channels.join(","));
  if (f.vacancy_ids?.length) p.set("vacancy_ids", f.vacancy_ids.join(","));
  if (f.kind) p.set("kind", f.kind);
  if (f.tracks?.length) p.set("tracks", f.tracks.join(","));
  if (f.stages?.length) p.set("stages", f.stages.join(","));
  if (f.result) p.set("result", f.result);
  if (f.client_ids?.length) p.set("client_ids", f.client_ids.join(","));
  if (f.reject_reason) p.set("reject_reason", f.reject_reason);
  if (f.owner) p.set("owner", f.owner);
  if (f.attention && f.attention !== "all") p.set("attention", f.attention);
  if (f.silent_days) p.set("silent_days", String(f.silent_days));
  return p;
}

// S5: what a transport can do (Provider.capabilities) — the UI reads these
// instead of hardcoding channel names.
//
// The full matrix from 30-target-model.md §3, which is also what the S1 seed
// writes for every provider. It used to declare seven of these, and the gap was
// not cosmetic: `can_send_video_note` has been seeded since S1 and the UI could
// not feature-detect it at all, so a round video was unreachable on every
// channel — the one capability nobody could see was the one nobody implemented.
// `tests/capabilities.test.ts` fails if the seed grows a key this forgets.
export interface ProviderCapabilities {
  // Send
  can_send_text?: boolean;
  can_send_media?: boolean; // photo / video, NOT voice and not a round video
  can_send_voice?: boolean; // PTT
  can_send_video_note?: boolean; // TG round video / MAX кружок
  can_send_file?: boolean;
  can_send_sticker?: boolean;

  // Receive
  can_receive_text?: boolean;
  can_receive_media?: boolean;
  can_receive_voice?: boolean;
  can_receive_video_note?: boolean;
  can_receive_file?: boolean;
  can_receive_sticker?: boolean;

  // Edit / delete. `*_out` is about OUR messages; `can_receive_*` is whether
  // the transport tells us the peer edited or deleted theirs.
  can_edit_message_out?: boolean;
  can_delete_message_out?: boolean;
  can_receive_edit?: boolean;
  can_receive_delete?: boolean;

  // Status
  supports_delivery_status?: boolean;
  supports_read_status?: boolean;
  supports_typing?: boolean;

  // Formatting (S7 phase 4)
  can_format_text_markdown?: boolean;
  can_format_text_html?: boolean;
  can_format_text_entities?: boolean; // TG-style, UTF-16 offsets

  // Chat control
  can_start_chat?: boolean; // may we write first to someone who never wrote us
  can_call?: boolean;
  can_receive_call?: boolean;

  // Author attribution
  reports_out_the_system?: boolean; // events about sends that bypassed us
  reports_actor?: boolean;

  // Backfill
  supports_pull_backfill?: boolean;

  // Deliberately open: a provider may carry keys this build predates, and a
  // capability the UI does not know must not become a type error.
  [key: string]: unknown;
}

export interface ChatProviderRef {
  id: string;
  type: string;
  name: string;
  is_default: boolean;
  eligible: boolean;
  capabilities?: ProviderCapabilities;
}

export interface ChatThreadBrief {
  thread_id: string;
  channel: string;
  subtitle: string | null;
  unread: boolean;
  needs_reply: boolean;
  last_message_at: string | null;
  can_send: boolean;
  bot: boolean;
  bot_active: boolean | null;
  hrdeva_pending: boolean;
  hrdeva_draft: { text: string; question?: string } | null;
  // S8: the other end for the bubble caption — hh vacancy, userbot @username.
  peer_label?: string | null;
  // S3.g1: Providers eligible for this thread's Channel + default pick id.
  // Новый chat-service отдаёт здесь только места типа канала треда; старый —
  // все места мессенджера вперемешку (бот и юзерботы), фронт фильтрует сам.
  providers: ChatProviderRef[];
  default_provider_id: string | null;
  // S5: capabilities of the transport behind this thread.
  capabilities?: ProviderCapabilities;
  // ── Выбор канала отправки (поля ниже — только у новых сборок chat-service;
  //    без них выбор строится по-старому: peer_label и providers). ──
  /** Собеседник: @username и телефон, что известно. */
  peer?: ChatThreadPeer | null;
  /** Наше место, через которое тред отправит сейчас (привязанное к треду). */
  send_provider?: ChatSendProvider | null;
  /** Юзербот-треды: гейтвей-места этого мессенджера — через какое писать
   *  (включённые, плюс привязанное к треду, даже выключенное). У остальных
   *  тредов null. */
  seats?: ChatSeat[] | null;
  /** Непрочитанные входящие (то же правило, что у списка чатов — read_at). */
  unread_count?: number;
  /** Сообщений в треде без скрытых и удалённых. */
  message_count?: number;
  /** Только у hh-тредов: что это за переписка по публикации. */
  hh?: ChatThreadHh | null;
}

export interface ChatThreadPeer {
  /** Ник без «@». */
  username: string | null;
  /** Канон 7XXXXXXXXXX. */
  phone: string | null;
}

export interface ChatSendProvider {
  id: string;
  type: string;
  /** Подпись места: «Анна · +7 911 … · @login» у юзербота, «@бот» у бота,
   *  hh-аккаунт смотрящего у hh. */
  label: string;
}

/** Наше место (аккаунт юзербота), через которое можно писать в тред. */
export interface ChatSeat {
  id: string;
  label: string;
  name?: string | null;
  phone?: string | null;
  username?: string | null;
  /** Тред сейчас привязан к нему — по умолчанию пишет оно. */
  bound: boolean;
  /** Может достучаться до собеседника; нет — `reason` (код) и `reason_text`
   *  (готовая подпись) объясняют почему. */
  eligible: boolean;
  reason: string | null;
  reason_text?: string | null;
}

export interface ChatThreadHh {
  /** Вакансия (публикация на hh). */
  title: string | null;
  city: string | null;
  kind: "response" | "invitation" | null;
  closed: boolean;
  /** Первое сообщение переписки. */
  started_at: string | null;
}

export interface ChatParticipant {
  id: string;
  name: string;
  /** Ответственный за чат (★ в меню «В чате»). */
  owner: boolean;
  /** Добавлен руками («+ Добавить в чат»), а не только писал. */
  added: boolean;
}

/** «Запросить решение» / «Задача» из меню скрепки (chat_collab). */
export type ChatRequestKind = "decision" | "task";

export interface ChatRequest {
  id: string;
  kind: ChatRequestKind;
  type: string;
  /** «Запрос решения: Скрининг анкеты» / «Задача: Собрать документы». */
  title: string;
  type_label: string;
  note: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string | null;
  /** null — открыт: карточка внизу ленты и красный счётчик над списком. */
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolved_by_name: string | null;
}

export interface ChatPersonThreads {
  person_id: string | null;
  name: string;
  has_phone: boolean;
  phones: string[];
  // S8: a phone to dial AND an active Provider with can_call (voice softphone).
  // Absent from older chat-service builds.
  can_call?: boolean;
  /** Человек заведён входящим от незнакомца и не имеет ничего своего.
   *  Угадать это на фронте нельзя: `person_id` не пустой, имя обычно есть
   *  (никнейм из мессенджера), а `candidate` равен null у ЛЮБОГО не-hh треда.
   *  Отсутствует у старых сборок chat-service — тогда кнопки просто нет. */
  placeholder?: boolean;
  /** Каналы, в которых прямо сейчас можно написать первым: есть рабочее
   *  место и оно не запрещено данными. Отвечает про место, а НЕ про то, есть
   *  ли этот человек в мессенджере — второе стоит живого запроса в контейнер.
   *  Отсутствует у старых сборок chat-service. */
  can_start?: string[];
  threads: ChatThreadBrief[];
  /** Адреса почты человека — на будущее (канала Email пока нет). Отсутствует
   *  у старых сборок chat-service. */
  emails?: string[];
  /** Ответственный рекрутёр (chat.recruiter_id); null — ничей. */
  owner_id?: string | null;
  owner_name?: string | null;
  /** Кто в чате (стопка аватарок в шапке), ответственный первым: он сам, все,
   *  кто писал из системы, и добавленные руками. Absent from older builds. */
  participants?: ChatParticipant[];
  /** Вычислено: не помечен неактивным или собеседник написал после отметки. */
  active?: boolean;
  /** Эффективный тип и ручная отметка (null — определяется автоматически). */
  kind?: ChatKind;
  kind_manual?: ChatKind | null;
  avatar_resume_id?: string | null;
  /** Подписи «чей номер» у телефонов: канон 7XXXXXXXXXX → «личный». Только
   *  непустые; у сироты `{}`. Нет поля — старая сборка chat-service. */
  phone_labels?: Record<string, string>;
}

/** Контакт прежнего руководителя кандидата — у кого можно о нём спросить. */
export interface ReferenceContact {
  id: string;
  full_name: string | null;
  position: string | null;
  /** Как написал кандидат: «ООО «ТЕНТОНН»». */
  company: string | null;
  /** Канон для сопоставления с местом работы. Считает сервер. */
  company_norm: string | null;
  /** Канон 7XXXXXXXXXX — для звонка. */
  phone: string | null;
  /** Как написал кандидат: «+7(952)287-36-97» — это и показываем. */
  phone_raw: string | null;
  /** Ник без «@», в нижнем регистре. */
  telegram: string | null;
  comment: string | null;
  /** "ai" — распознано моделью, стоит перепроверить. */
  source: string;
  /** Сообщение, из которого контакт вытащили: по нему лента вешает блок.
   *  null — заведён вручную, такие собираются блоком в конце ленты. */
  source_message_id: string | null;
  /** Когда взяли рекомендацию. Непустое — контакт закрыт: плашка в ленте
   *  остаётся (с ней и переписка с ЛПР), но с отметкой «ОС получена». Без
   *  `include_done` сервер закрытых не отдаёт вовсе. */
  feedback_received_at: string | null;
  /** Появляется, когда с этим человеком завели переписку. */
  person_id: string | null;
  created_at: string | null;
  /** Переписка с ЛПР — для свёрнутой плашки в ленте кандидата (счётчик
   *  непрочитанных), без запроса на каждый контакт. null — у контакта ещё нет
   *  своего человека (`person_id`), переписки быть не может. Нет поля —
   *  старая сборка ats-api: счётчика просто не будет. */
  chat?: ReferenceChatSummary | null;
}

/** Сводка переписки с ЛПР — считает ats-api тем же правилом, что и список
 *  чатов (входящие, не скрытые, не удалённые, после отметки «прочитано»). */
export interface ReferenceChatSummary {
  threads: number;
  unread: number;
  last_message_at: string | null;
}

/** Место работы из резюме — чтобы разложить по ним контакты. */
export interface ReferenceExperience {
  index: number;
  company: string | null;
  company_norm: string | null;
  position: string | null;
  /** Сырая строка «как в резюме»: «2022-03-01 — 2026-06-01» или свободный
   *  текст. Показывать — через lib/period (formatWorkPeriod по start/end). */
  period: string;
  /** Логотип компании — подписанный прокси-URL (как company_logo в резюме);
   *  null — у места работы нет работодателя hh с логотипом. Необязательные три
   *  поля — у старых сборок ats-api их нет. */
  logo?: string | null;
  /** Начало и конец как в резюме: «YYYY-MM-01» у hh и расширения, свободный
   *  текст у ручных. Конец null — по настоящее время. */
  start?: string | null;
  end?: string | null;
}

export interface ReferenceIn {
  full_name?: string;
  position?: string;
  company?: string;
  phone?: string;
  telegram?: string;
  comment?: string;
}

/** Строка в подсказке поиска кандидата. */
export interface CandidateSearchHit {
  candidate_id: string;
  person_id: string;
  person_name: string | null;
  vacancy_title: string | null;
  stage: string;
  status: string;
  created_at: string;
}

/** «Новые»: неопознанный отправитель — тред заглушки без кандидатуры
 *  (chat-service unidentified.py). */
export interface ChatUnidentifiedItem {
  thread_id: string;
  person_id: string;
  channel: string;
  /** Имя заглушки, если оно у неё есть. */
  name: string | null;
  /** Подпись треда (номер СМС, имя из профиля мессенджера). */
  title: string | null;
  username: string | null;
  phone: string | null;
  /** Последнее входящее (или «Голосовое»/«Фото» для вложения). */
  text: string;
  at: string | null;
}

export interface ChatUnidentifiedOut {
  items: ChatUnidentifiedItem[];
  total: number;
}

// S3.g1: send responses echo the Provider that actually delivered.
export interface ChatSendResult {
  ok: boolean;
  used_provider_id: string | null;
}

// Subset of ats-api PersonCard the tguser-start modal reads for autofill.
// `contacts.telegram` holds numeric TELEGRAM_USER_ID identifiers; `socials`
// holds @-handles under `platform="telegram"`.
export interface PersonCardBrief {
  id: string;
  full_name: string | null;
  contacts: {
    phones: string[];
    emails: string[];
    telegram: string[];
    max: string[];
    hh_resume_ids: string[];
  };
  socials: PersonSocial[];
}

export interface ChatAttachment {
  // image | video | voice | video_note | audio | file | sticker | animation —
  // the vocabulary is `ATTACHMENT_KINDS` in shared_db/chat_attachments.py.
  // `voice` vs `audio` and `video_note` vs `video` are stated by the sender,
  // never guessed from the mime: a container cannot tell a voice note from a
  // music file, nor a round video from an ordinary one.
  kind?: string;
  name?: string;
  url?: string; // already "/api/chat/media/{key}" for incoming
  key?: string; // MinIO key for outgoing (recruiter-sent) media
  mime?: string;
  size?: number | null;
  // Seconds. The server has had it all along for voice and video-notes; the
  // UI ignored it, which is why a voice bubble could never show its length.
  duration?: number | null;
  // Pixel size when the messenger / bot-service knows it (TG always, MAX
  // from the image header) — the album lays out without waiting for the file.
  width?: number | null;
  height?: number | null;
  // Folds an album into one bubble.
  group_id?: string | null;
  // The bytes never reached the bucket. `url` is then null, and the UI must
  // show the attachment as unavailable rather than drop it — a dropped row
  // claims the candidate sent nothing (S7-4.5).
  error?: string | null;
  // Where the original lives when we could not get the bytes — the hh chat
  // for a file hh would not give us (PRI-48).
  source_url?: string | null;
}

/** Resolve a chat attachment to a servable URL (chat-service media route). */
export function chatMediaUrl(a: ChatAttachment): string | null {
  if (a.url) return a.url;
  if (a.key) return `/api/chat/media/${encodeURIComponent(a.key)}`;
  return null;
}

/** The MinIO object key of a chat attachment (for server-side copy). */
export function chatMediaKey(a: ChatAttachment): string | null {
  if (a.key) return a.key;
  if (a.url) {
    const m = a.url.match(/\/api\/chat\/media\/(.+)$/);
    if (m) return decodeURIComponent(m[1]);
  }
  return null;
}

/** Значки и цвета шаблона в меню чата — набор эталона. */
export type TemplateIcon = "circle" | "square" | "triangle" | "plus" | "cross" | "excl";
export type TemplateColor = "red" | "green" | "blue" | "yellow" | "grey";
/** Цель и канал шаблона (эталон: «Приглашение · hh.ru») — подпись и фильтры списка. */
export type TemplateGoal = "invite" | "push" | "remind" | "confirm" | "reject" | "thanks" | "revive";
export type TemplateChannel = "bot_tg" | "bot_max" | "tg" | "max" | "hh" | "sms" | "call";

export interface MessageTemplateItem {
  id: string;
  name: string;
  body: string;
  position: number;
  is_public: boolean;
  /** Вакансия шаблона; нет — «общий». Старый ats-api полей ниже не шлёт. */
  vacancy_id?: string | null;
  vacancy_title?: string | null;
  /** Значок и цвет; нет — значок по умолчанию (серый круг). */
  icon?: TemplateIcon | null;
  color?: TemplateColor | null;
  /** Цель и канал; нет — не указано. На отправку не влияют. */
  goal?: TemplateGoal | null;
  channel?: TemplateChannel | null;
  /** Владелец личного шаблона (у общего — null). */
  owner_name?: string | null;
}

export interface TemplateInput {
  name: string;
  body: string;
  is_public: boolean;
  position?: number;
  /** Не передано — сервер оставляет прежнее; null — снять привязку / значок. */
  vacancy_id?: string | null;
  icon?: TemplateIcon | null;
  color?: TemplateColor | null;
  goal?: TemplateGoal | null;
  channel?: TemplateChannel | null;
}

export interface LandingFormOut {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  created_at: string | null;
  has_video: boolean;
  fields: { key: string; value: string | number | boolean }[];
}

export type MessageAuthorType = "user" | "hrdeva" | "external" | "system";

/** Снимок для раскрытия строки скрининга / скоринга резюме (state
 *  "screening_done" / "scoring_done", ats-api screening_feed): критерии и баллы
 *  на момент проверки. */
export type FeedEventDetail =
  | {
      kind: "screening";
      title: string;
      /** «3/4» — закрыто критериев. */
      sum: string;
      /** ok — пройден, no — не пройден, unk — нужен разбор. */
      tone: "ok" | "no" | "unk";
      rows: { text: string; ok: boolean | null; note: string | null }[];
    }
  | {
      kind: "scoring";
      title: string;
      /** Итог в процентах строкой — для плитки. */
      sum: string;
      pct: number;
      /** `pts` — баллы критерия (вес × очки оценки), null — данных нет. */
      rows: { text: string; pts: number | null; grade: string | null; note: string | null }[];
    };

/** Визит на лендинг за строкой «Открыл лендинг» (GET
 *  /chat/messages/{id}/landing-visit, chat-service landing_visit.py). */
export interface LandingVisit {
  device: { no: number; total: number; label: string; kind: string | null };
  code: string | null;
  /** Первые 8 знаков id сессии. */
  session: string;
  screen: string | null;
  lang: string | null;
  referrer: string | null;
  from: string;
  /** null — закрытие страницы не зафиксировано. */
  to: string | null;
  truncated: boolean;
  events: { ts: string; name: string; label: string; value: string; key: boolean }[];
  outcome: { kind: "submitted" | "left" | "open"; label: string };
}

/** Чья строка-событие ленты (chat-service system_rows): кандидата, рекрутёра,
 *  Девы (вся автоматика HRDeva) или стороны работодателя на hh. */
export const FEED_ACTOR_KINDS = ["candidate", "user", "deva", "external"] as const;
export interface FeedActor {
  kind: (typeof FEED_ACTOR_KINDS)[number];
  /** Рекрутёр — его id (аватарка). */
  user_id: string | null;
  /** Рекрутёр или сотрудник на hh; кандидата и Деву фронт знает сам. */
  name: string | null;
}
export type MessageSource = "in_the_system" | "automatic" | "out_the_system";

export interface ChatMessageItem {
  id: string;
  direction: "incoming" | "outgoing";
  // Legacy free-form sender ('applicant'|'employer'|'system'|'hrdeva'|raw name).
  // Kept while ats-frontend still reads it; newats reads author_type/*.
  sender: string | null;
  // S2 attribution (chat refactor). Nullable while backfill/rollout finishes.
  author_type: MessageAuthorType | null;
  author_user_id: string | null;
  // JOIN result on app_user.full_name — null when author_type != 'user' or the
  // recruiter row was removed (FK ON DELETE SET NULL).
  author_full_name: string | null;
  source: MessageSource | null;
  // Outgoing external rows only: the name the provider shows for whoever
  // sent it outside HRDeva (hh manager on hh.ru, hh's AI assistant).
  external_author_name?: string | null;
  // hh sender role from /common/chats ('EMPLOYER' | 'BOT' | …); null elsewhere.
  hh_role?: string | null;
  // S8: the Provider that carried it (bot / userbot / hh integration) and,
  // for hh outgoing, the connected hh account it went out from.
  provider_id?: string | null;
  via_account?: string | null;
  text: string | null;
  attachments: ChatAttachment[];
  state: string | null;
  state_name: string | null;
  call_recording_id: string | null;
  call_outcome: CallOutcome | null;
  // Входящий, который никто не взял: плашка без записи, с «Перезвонить».
  call_missed?: boolean;
  // Комментарий заказчика без права решения (state "client_comment", ats-api
  // client_comment): текст, который строка ленты раскрывает по клику.
  client_comment?: string | null;
  // Номер, с которого звонили (пропущенный): «Перезвонить» набирает его.
  call_number?: string | null;
  // Строки скрининга / скоринга резюме: критерии и баллы, которые строка
  // раскрывает по клику (снимок на момент проверки). У остальных — нет.
  event_detail?: FeedEventDetail | null;
  // Строки о встрече (meeting_booked / rescheduled / cancelled / held /
  // no_show) — её id: по строке итога карточка «Встреча прошла?» уходит.
  meeting_id?: string | null;
  // Видео проведённой встречи (state "meeting_recording"): плашка «Встреча
  // проведена · запись 22 мин · …» с плиткой видео. Остальное из `raw` сервер
  // не отдаёт. Нет поля (запись удалили, старый chat-service) — строка
  // рисуется обычной системной плашкой по `state_name`.
  meeting_recording?: ChatMeetingRecording | null;
  /** Строка-событие (отклик, бот, лендинг, этап, встреча, звонок…): вакансия
   *  её кандидатуры. null — не про вакансию или не узнали; у обычных
   *  сообщений — null. */
  vacancy_title?: string | null;
  /** Строка-событие — чьё это действие; null — ничьё или неизвестно. */
  actor?: FeedActor | null;
  created_at: string | null;
  deleted: boolean;
  // Edited from the inbox — previous texts, oldest first (tg-gallery §3.7).
  edited?: boolean;
  prev_versions?: string[];
  // S7-6.5: speech-to-text of a voice note or a video-note. Null on every
  // other message — that is what "nothing to transcribe here" looks like, and
  // the bubble then renders nothing at all. The failure REASON is not sent:
  // it holds hostnames and tracebacks a recruiter can do nothing with.
  transcript?: {
    status: "processing" | "ready" | "failed";
    text: string | null;
    language: string | null;
  } | null;
  // Our own delivered message the channel can address → «Изменить» /
  // «Удалить» in the bubble's context menu.
  can_modify?: boolean;
  // Outgoing messages in hh, TG/MAX bot and userbot threads; null for
  // everything else and when unknown. 'read' is inferred, not a receipt: the
  // candidate wrote in the thread after this message (no current channel
  // reports reads).
  delivery?: "pending" | "sent" | "read" | "failed" | null;
}

/** Запись встречи в ленте (chat-service meetings.py по строке
 *  "meeting_recording", которую пишет ats-api). Длина и статус расшифровки —
 *  из самой записи: плашка, пока расшифровка идёт, перечитывает ленту. */
export interface ChatMeetingRecording {
  meeting_id: string;
  recording_id: string;
  /** Кандидатура встречи: у человека их бывает несколько. */
  candidate_id?: string | null;
  /** Длина в секундах: из браузера при загрузке или из результата
   *  расшифровки; null — не узнали. */
  duration_s: number | null;
  /** null — транскрибацию не запускали («Никто не говорит»). */
  transcript_status: "processing" | "ready" | "failed" | string | null;
  /** Кто проводил встречу (кто её назначил). */
  recruiter_name: string | null;
  slot_start: string | null;
  vacancy_title: string | null;
}

/** Строка ленты, к которой привязана карточка, ждущая решения в конце ленты
 *  («Дева предлагает сменить этап», «Встреча прошла?»): «К сообщению» и клик
 *  по карточке ставят ленту к ней. Строку называет сервер — фронт её не
 *  угадывает: она бывает старше загруженной истории. */
export interface FeedJump {
  message_id: string;
  thread_id: string;
  /** sent_at строки как есть — курсор прыжка ленты (ChatFocus.sentAt). */
  sent_at: string;
}

/** Прошедшая встреча без итога (GET /chat/people/{key}/meeting-prompts):
 *  карточка «Встреча прошла?» в ленте. Сервер отдаёт только SCHEDULED,
 *  закончившиеся больше 15 минут назад и не старше 14 дней, без записи. */
export interface MeetingPrompt {
  meeting_id: string;
  candidate_id: string;
  vacancy_title: string | null;
  slot_start: string | null;
  slot_end: string | null;
  booked_via: string | null;
  created_by_name: string | null;
  /** Строка ленты об этой встрече: новейшая «назначена» / «перенесена», нет
   *  её — любая. null — в ленте о встрече ничего; поля нет — старый
   *  chat-service. Тогда карточка никуда не ведёт. */
  jump?: FeedJump | null;
  /** Запись этой встречи уже лежит в карточке кандидата: загружена там после
   *  начала встречи, а не кнопкой «Загрузить запись» — осталось нажать «Уже
   *  загружена» (`meetingLinkUploaded`). */
  uploaded?: boolean;
}

export type MeetingOutcome = "held" | "no_show";

/** Ответ «Уже загружена» (POST /meetings/{id}/link-uploaded): какая запись
 *  теперь у встречи. */
export interface MeetingLinkUploaded {
  meeting_id: string;
  recording_id: string;
}

/** Поля формы upload-recording: сколько говорит (null — автоматически), к
 *  какой встрече запись и её длина по браузеру (сервер берёт 1..86400 с). */
export function recordingUploadFields(o: {
  speakers?: number | null;
  meetingId?: string | null;
  durationS?: number | null;
}): Record<string, string> | undefined {
  const f: Record<string, string> = {};
  if (o.speakers != null) f.speakers = String(o.speakers);
  if (o.meetingId) f.meeting_id = o.meetingId;
  if (o.durationS != null) f.duration_s = String(o.durationS);
  return Object.keys(f).length ? f : undefined;
}

/** Поток записи встречи. Вошедшему хватает cookie (ats-api `optional_user`),
 *  подпись `sig` нужна только ссылке, открытой без входа. */
export const recordingStreamUrl = (id: string) => `${API_BASE}/recordings/${id}/stream`;

/** Части записи (камеры и показы экрана отдельными файлами). Тот, кто может
 *  смотреть мастер, видит и их: вошедшему хватает cookie. */
export const recordingPartsUrl = (id: string) => `${API_BASE}/recordings/${id}/parts`;

/** То же на странице заказчика: по ссылке, без входа, и только записи, что она показывает. */
export const publicRecordingPartsUrl = (token: string, id: string) =>
  `${API_BASE}/public/candidate/${token}/recordings/${id}/parts`;

/** Ответ …/parts: файлы-источники на шкале мастера. Звук играет только мастер. */
export interface RecordingPartOut {
  id: string;
  kind: "camera" | "screen";
  role: "candidate" | "recruiter" | null;
  label: string | null;
  // Где первый кадр файла стоит на шкале мастера, секунды.
  offset_s: number;
  duration_s: number | null;
  width: number | null;
  height: number | null;
  // Подписанный адрес потока (с Range), живёт как адрес мастера.
  stream_url: string;
  // Уменьшенная копия для плитки (PRI-67) — null, если её не сделали
  // (старая часть или не вышло перекодировать); плеер берёт stream_url.
  thumb_stream_url: string | null;
}

export interface CallTranscriptSegment {
  role: "recruiter" | "candidate";
  start: number | null;
  end: number | null;
  text: string;
}
export interface CallRecordingOut {
  id: string;
  number: string;
  direction: string;
  duration_s: number;
  started_at: string | null;
  has_audio: boolean;
  transcript_status: "processing" | "ready" | "failed";
  transcript: CallTranscriptSegment[];
  outcome: CallOutcome | null;
}

/** Кандидатура, в карточку которой запись звонка уже добавлена
 *  (GET /call-recordings/{id}/attachments) — начальное состояние кнопки
 *  «Добавить запись кандидату». */
export interface CallAttachment {
  candidate_id: string;
  vacancy_title: string | null;
  /** Аудиозапись в карточке (MeetingRecording), а не сам звонок. */
  recording_id: string;
  /** Встреча-держатель записи (booked_via="manual_upload"). */
  meeting_id: string;
}

/** Ответ «Добавить запись кандидату» (POST /call-recordings/{id}/attach). */
export interface CallAttachOut {
  recording_id: string;
  candidate_id: string;
  /** Встреча-держатель записи (booked_via="manual_upload"). */
  meeting_id: string;
  vacancy_title: string | null;
  /** false — эту пару «звонок — кандидатура» уже добавляли: та же запись. */
  created: boolean;
}

// Чем кончился звонок. Считается по звуку плеч, а не по расшифровке: на одних
// гудках whisper выдаёт «Номер не отвечает», которого никто не произносил.
// null — звонок записан до появления поля и ещё не прошёл бэкфил.
export type CallOutcome =
  | "conversation"
  | "no_answer"
  | "voicemail"
  | "operator"
  | "test"
  | "unknown";

// Входящий звонок через GSM-шлюз, пока звонящий в голосовом меню (и после
// перехвата). Форма одна и та же в SSE (`{kind: "inbound_call", call}`) и в
// REST — её держит chat-service (inbound_calls.py), здесь только зеркало.
export type InboundCallState = "ringing" | "taken" | "ended";
export type InboundIvrStep = "client" | "candidate" | "partner" | "other";
export interface InboundCallUser {
  user_id: string;
  name: string;
}
export interface InboundCallPerson {
  id: string;
  name: string | null;
  avatar_resume_id: string | null;
  candidate_id: string | null;
}
export interface InboundCall {
  // Uniqueid канала звонящего на АТС («1727000000.123»).
  id: string;
  // Канонический 7XXXXXXXXXX; null — номер скрыт или не разобрался.
  number: string | null;
  // Как его прислал шлюз — показываем, когда канонического нет.
  number_raw: string;
  started_at: string;
  state: InboundCallState;
  ivr_step: InboundIvrStep | null;
  person: InboundCallPerson | null;
  taken_by: InboundCallUser | null;
  listeners: InboundCallUser[];
  // На АТС стоит план набора HRDeva и AMI-сессия с ней жива: без этого звонок
  // видно, но ни слушать, ни перехватить нельзя (503). Меняется в обе стороны
  // вместе со связью — бекенд переиздаёт все живые звонки.
  intercept_ready: boolean;
}
export interface InboundCallsOut {
  // Слушатель АМИ включён на этом стенде. false — звонков не будет вовсе.
  enabled: boolean;
  // Процесс chat-service уже сверился с АТС (первый вход в AMI после старта).
  // До этого список пуст или неполон, и отсутствие в нём звонка ничего не
  // значит — «кончился» из него выводить нельзя.
  resynced: boolean;
  calls: InboundCall[];
}

/** Разобранная ошибка listen/take: бекенд отдаёт `detail: {code, taken_by?}`,
 * а ApiError.message для объектного detail — это просто statusText. */
export interface InboundCallError {
  status: number;
  // not_found | ended | taken | not_listening | listen_lost | not_your_listener | pbx_dialplan_missing | pbx_error | …
  code: string | null;
  taken_by: InboundCallUser | null;
}
export function inboundCallError(e: unknown): InboundCallError | null {
  if (!(e instanceof ApiError)) return null;
  const detail = (e.body as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === "object") {
    const d = detail as { code?: unknown; taken_by?: unknown };
    const by = d.taken_by as InboundCallUser | null | undefined;
    return {
      status: e.status,
      code: typeof d.code === "string" ? d.code : null,
      taken_by: by && typeof by.user_id === "string" ? by : null,
    };
  }
  // Строковый detail (или его нет) — код ошибки тогда в message.
  return { status: e.status, code: typeof detail === "string" ? detail : null, taken_by: null };
}

export interface ChatThreadMessages {
  thread_id: string;
  channel: string;
  subtitle: string | null;
  person_id: string | null;
  candidate: { id: string; decision: string | null; summary: string | null } | null;
  hh_resume_id: string | null;
  messages: ChatMessageItem[];
  // Queued-for-later sends of THIS thread, pending ones only, oldest fire time
  // first. Older backends don't send the field at all.
  scheduled?: ScheduledChatMessage[];
  // Paged loads only: older messages exist before the first one returned.
  has_more?: boolean;
}

/** A send the recruiter queued for later (chat-service `scheduling.py`). Only
 *  pending rows reach the UI — a sent one is an ordinary message by then. */
export interface ScheduledChatMessage {
  id: string;
  thread_id: string;
  author_user_id: string | null;
  author_name: string | null;
  text: string | null;
  // Same shape as an outgoing attachment: addressed by `key`, not `url`.
  attachment: ChatAttachment | null;
  scheduled_at: string;
  // «Отменить отправку, если кандидат написал» — the backend withdraws the row
  // as soon as the candidate answers in ANY of their channels.
  cancel_on_reply: boolean;
  // «За час» / «За день» до встречи кандидата (PRI-260): встреча, к которой
  // привязана отправка, и за сколько минут до неё она уходит (60 / 1440).
  // Время `scheduled_at` сервер подтягивает к текущему слоту встречи сам.
  // Нет полей — старый chat-service или отправка в заданное время.
  meeting_id?: string | null;
  before_meeting_min?: number | null;
  status: "pending" | "sent" | "cancelled" | "failed";
  // Why the last send attempt didn't go out. A pending row can carry one: an
  // urgent send that failed stays queued rather than losing the text.
  error: string | null;
  sent_at: string | null;
  cancelled_at: string | null;
  // Автоматическое сообщение («Дева отправит …»: отказ, приглашение в бот,
  // сообщение бота, «спасибо за анкету») — строка очереди `auto_messages`, а не
  // отложенное рекрутёра. Нет поля — обычная отложенная отправка (и старый
  // chat-service).
  auto?: AutoQueueMeta | null;
}

/** Что известно об автоматической строке очереди (chat-service `scheduling.py`). */
export interface AutoQueueMeta {
  // reject | invite | think | stage3_reject | traffic_decline | landing_thanks | bot_intro | bot_ack
  kind: string;
  // «отказ», «приглашение в бот» — в предложении «Дева отправит …».
  label: string;
  // Можно ли поправить текст (у «перевода отклика» текста нет).
  editable: boolean;
  // Прямо сейчас уходит — править и отменять поздно.
  sending: boolean;
  attempts: number;
  // Текст поправил рекрутёр: уйдёт как есть, шаблон заново не собирается.
  text_edited: boolean;
  // По какой вакансии сообщение: у человека с двумя откликами строки «Дева
  // отправит отказ» иначе неотличимы. Нет — вакансия строки неизвестна
  // (приветствие бота без кандидатуры) или старый chat-service.
  vacancy?: { number: number; title: string } | null;
  // Что произойдёт вместе с сообщением: {type, to} (hh_move, candidate_status…).
  actions: { type: string | null; to: string | null }[];
}

/** Напоминание в чате человека (chat-service `reminders.py`, одна сериализация
 *  на API и фронт). Предлагает его LLM по звонку (`source: "llm"`, сперва
 *  `proposed`) или заводит рекрутёр кнопкой «Напомнить» (`manual`, сразу
 *  `active`). С включённой настройкой «Напоминания из звонков» предложение
 *  LLM ставится само — сразу `active` с `decision: "auto"`, владельцу звонка
 *  или чата. `superseded` в ленту не приходит: предложение без решения,
 *  которое заменило более новое. */
export type ChatReminderStatus =
  | "proposed"
  | "active"
  | "done"
  | "dismissed"
  | "cancelled"
  | "superseded";

/** Откуда LLM взяла напоминание: дословная фраза, как было сказано время
 *  («завтра после обеда»), зачем, и насколько срок точный
 *  (exact | approximate | default). Всё может отсутствовать. */
export interface ChatReminderEvidence {
  quote?: string | null;
  when_said?: string | null;
  reason?: string | null;
  time_basis?: string | null;
}

export interface ChatReminder {
  id: string;
  chat_id: string;
  person_id: string | null;
  thread_id: string | null;
  call_recording_id: string | null;
  source: "llm" | "manual";
  status: ChatReminderStatus;
  // Итоговые текст и срок (ISO со смещением).
  text: string;
  due_at: string;
  // Исходное предложение LLM — не меняется (у ручных null).
  proposed_text: string | null;
  proposed_due_at: string | null;
  evidence: ChatReminderEvidence | null;
  // Кого уведомлять: принявший предложение или автор ручного; у поставленного
  // автоматически — звонивший рекрутёр или владелец чата; у предложения null.
  user_id: string | null;
  user_name: string | null;
  // Автор ручного; null — предложила LLM.
  created_by_user_id: string | null;
  created_by_name: string | null;
  // "auto" — поставлено без рекрутёра (decided_by_user_id тогда null).
  decision: "accepted" | "accepted_edited" | "dismissed" | "auto" | null;
  decided_at: string | null;
  decided_by_user_id: string | null;
  decided_by_name: string | null;
  // Срок наступил и уведомление ушло; перенос срока сбрасывает в null.
  fired_at: string | null;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolved_by_name: string | null;
  created_at: string;
  updated_at: string | null;
  // active и срок уже наступил — на момент ответа сервера.
  due: boolean;
  // После принятия (у предложения — после создания) кандидат писал / был
  // ещё звонок: напоминание само не снимается, карточка об этом говорит.
  peer_wrote_after: boolean;
  called_after: boolean;
  // Только в GET /chat/reminders/mine — для всплывашки вне чата.
  person_name?: string | null;
}

/** SSE `reminder` (текста в нём нет: событие уходит во все вкладки). */
export type ChatReminderAction =
  | "proposed"
  | "superseded"
  | "accepted"
  | "auto_accepted"
  | "dismissed"
  | "created"
  | "updated"
  | "done"
  | "cancelled"
  | "due";

/** Настройки → «Напоминания из звонков» (app_settings `call_reminders`).
 *  `auto_accept` — предложение LLM после звонка ставится сразу, без «Принять»;
 *  строки ещё нет — включено. Кто и когда менял — null, пока не меняли. */
export interface ReminderSettings {
  auto_accept: boolean;
  updated_at: string | null;
  updated_by_name: string | null;
}

/** Настройки → «Автоматика: время и задержки» (app_settings `automation_timing`),
 *  только сисадмин. Один параметр: подпись, границы, умолчание из кода и значение,
 *  по которому автоматика работает сейчас. `overridden` — значение задано
 *  вручную (можно сбросить к умолчанию). `wired: false` — код, читающий параметр,
 *  ещё не выкачен («в разработке»). `flag` — переключатель, значение 0 или 1. */
export interface AutomationTimingParam {
  key: string;
  label: string;
  description: string;
  unit: string;
  kind: "int" | "float" | "flag";
  default: number;
  minimum: number;
  maximum: number;
  value: number;
  overridden: boolean;
  note: string | null;
  wired: boolean;
  changed_at: string | null;
  changed_by: string | null;
}

export interface AutomationTimingGroup {
  key: string;
  title: string;
  description: string;
  params: AutomationTimingParam[];
}

export interface AutomationTiming {
  groups: AutomationTimingGroup[];
}

/** Настройки → «Этапы по переписке» (app_settings `stage_suggestions`).
 *  `auto_apply` — предложение Девы применяется сразу, без «Применить»; строки
 *  ещё нет — выключено. Кто и когда менял — null, пока не меняли. */
/** Страница «Расширение для браузера» → «Поведение у всех рекрутеров
 *  компании» (ats-api extension_settings.py). Читают все, меняет сисадмин;
 *  расширение получает то же в bootstrap. */
export interface ExtensionBehavior {
  /** счётчик «в базе | нет» на иконке на страницах выдачи */
  badge_counter: boolean;
  /** окно расширения раскрывается само на странице резюме */
  auto_open: boolean;
  /** режим «Контактные данные вручную» в окне */
  manual_contacts: boolean;
  /** значок ▶ у кандидатов с видеоинтервью */
  video_badge: boolean;
}

export interface ExtensionSettings extends ExtensionBehavior {
  updated_at: string | null;
  updated_by_name: string | null;
}

export interface StageSuggestSettings {
  auto_apply: boolean;
  updated_at: string | null;
  updated_by_name: string | null;
}

/** Предложение Девы сменить этап и/или статус кандидатуры (chat-service
 *  `stage_suggestions`): LLM прочитала переписку и звонки человека, рекрутёр
 *  решает карточкой в ленте — «Применить», «Другой этап», «Не нужно». Сама
 *  система меняет кандидатуру, только когда сисадмин включил «Применять
 *  сразу» (`StageSuggestSettings`): тогда предложение сразу `auto_applied`, а
 *  на карточке — «Вернуть».
 *
 *  `superseded` — по той же кандидатуре пришло предложение новее; `stale` —
 *  кандидатуру уже поменяли иначе (руками, автоматикой), и предложение про
 *  прежние этап и статус; `reverted` — применённое само вернули как было. */
export type StageSuggestionStatus =
  | "proposed"
  | "applied"
  | "applied_edited"
  | "auto_applied"
  | "reverted"
  | "dismissed"
  | "superseded"
  | "stale";

/** На чём держится предложение: дословные фразы из ленты (для этапа и для
 *  статуса — у каждого своя) и время их строк, объяснение модели и, у
 *  повторного отклика на ту же вакансию, кандидатура, с которой статус
 *  скопирован. Всё может отсутствовать. */
export interface StageSuggestionEvidence {
  stage_quote?: string | null;
  stage_quote_at?: string | null;
  status_quote?: string | null;
  status_quote_at?: string | null;
  reason?: string | null;
  duplicate_of?: string | null;
}

export interface StageSuggestion {
  id: string;
  person_id: string;
  chat_id: string | null;
  candidate_id: string;
  vacancy_title: string | null;
  // Строка ленты с цитатой: сообщение или звонок (id записи — как
  // `call_recording_id` плашки звонка). Обе null — цитата не из сообщения
  // (например, «пять дней без ответа»).
  anchor_message_id: string | null;
  anchor_call_id: string | null;
  // Та же строка для прыжка ленты: сообщение с цитатой или плашка звонка.
  // null — цитата не из сообщения или строки уже нет; поля нет — старый
  // chat-service. Тогда карточка никуда не ведёт.
  jump?: FeedJump | null;
  status: StageSuggestionStatus;
  // Этап и статус кандидатуры, которые видела модель.
  snapshot_stage: string;
  snapshot_status: string;
  // Исходное предложение — не меняется; null — эту часть не трогать.
  proposed_stage: string | null;
  proposed_status: string | null;
  proposed_reason: string | null;
  evidence: StageSuggestionEvidence | null;
  // Что поставил рекрутёр (у applied и auto_applied — то же, что предложено).
  final_stage: string | null;
  final_status: string | null;
  final_reason: string | null;
  // Кто решил: у auto_applied — никто (null), у reverted — кто вернул.
  decided_by_user_id: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
  created_at: string;
  // «Вернуть» ещё сработает: применено само, и кандидатура с тех пор та же,
  // что поставила Дева (сервер сверяет при каждом чтении). У остальных — false.
  revertable: boolean;
  // Подписи этапов и статусов с сервера (shared_db); нет — фронт подпишет сам.
  snapshot_stage_label?: string | null;
  snapshot_status_label?: string | null;
  proposed_stage_label?: string | null;
  proposed_status_label?: string | null;
  final_stage_label?: string | null;
  final_status_label?: string | null;
}

/** «Применить» с правкой («Другой этап»): обе части явно, null — эту часть
 *  не менять; причина — для отказа и «Кандидат отказался». Без правки тело
 *  пустое: применить как предложено. */
export interface StageSuggestionApply {
  stage?: string | null;
  status?: string | null;
  reason?: string | null;
}

/** SSE `stage_suggestion` — только id (событие уходит во все вкладки). */
export type StageSuggestionAction =
  | "proposed"
  | "superseded"
  | "stale"
  | "applied"
  | "applied_edited"
  | "auto_applied"
  | "reverted"
  | "dismissed";

export interface ChatHistoryPage {
  limit?: number;
  // Cursor = the oldest loaded message (created_at + id — album items share
  // a timestamp, so the id breaks ties).
  before?: string;
  beforeId?: string;
  // Jump to a message: everything from (since, sinceId) up to the newest,
  // plus `context` older ones. Not combined with limit/before. 422 `too_far`
  // when that is more than the server cap.
  since?: string;
  sinceId?: string;
  context?: number;
}


// ---- interviews calendar (Ф4) ----

// Личное расписание рекрутёра (ats-api schedule.py). Оно у рекрутёра одно:
// недельный график с несколькими интервалами в день, параметры записи и
// диапазоны на конкретные даты — «доп. время» и «вырезать». Минуты — от
// полуночи по Москве. Длительность встречи — свойство вакансии
// (`VacancyDetail.interview_duration_min`), в расписании её нет.

/** Интервал времени внутри дня: минуты кратны 5, начало раньше конца. */
export interface ScheduleRange {
  start_min: number;
  end_min: number;
}

/** День недельного графика; `weekday` 0 — понедельник. Интервалы выключенного
 *  дня хранятся: включили день — часы на месте. */
export interface ScheduleDay {
  weekday: number;
  on: boolean;
  ranges: ScheduleRange[];
}

/** Строка «Доп. время» или «Вырезать» — диапазон на одну дату. */
export interface ScheduleDateRange {
  date: string; // YYYY-MM-DD
  start_min: number;
  end_min: number;
}

/** Уже назначенная будущая встреча, которая не попадает в сохранённое рабочее
 *  время. Справочно: сохранение расписания встреч не отменяет. */
export interface ScheduleConflict {
  meeting_id: string;
  start: string;
  end: string;
  candidate_name: string | null;
}

/** Ответ GET и PUT /schedule. `configured: false` — расписание ещё ни разу не
 *  сохраняли: в полях умолчания формы, слотов по нему нет. */
export interface RecruiterSchedule {
  user_id: string;
  user_name: string;
  configured: boolean;
  gap_min: number;
  close_before_min: number;
  open_for_days: number;
  skip_inactive_days: boolean;
  week: ScheduleDay[];
  extra: ScheduleDateRange[];
  cut: ScheduleDateRange[];
  updated_at: string | null;
  /** Кто сохранил последним (расписание коллеги может править любой вошедший). */
  updated_by: string | null;
  conflicts: ScheduleConflict[];
  /** Тип графика: «week» — еженедельный, «flex» — гибкий (недельной сетки нет). */
  kind: ScheduleKind;
  /** «general» — общее расписание, «vacancy» — под вакансию (`vacancy_id`). */
  scope: "general" | "vacancy";
  vacancy_id: string | null;
  /** Только в режиме вакансии: true — «Настраиваемое» (своё на вакансии),
   *  false — «Стандартное» (показано общее). */
  has_override: boolean;
  /** Вырезы общего расписания; на вакансии действуют всегда. Пусто вне режима вакансии. */
  base_cut: ScheduleDateRange[];
}

/** Тип графика расписания. */
export type ScheduleKind = "week" | "flex";

/** Тело PUT /schedule — расписание целиком. Сервер читает его с
 *  `extra="forbid"`: лишний ключ — 422. */
export interface RecruiterScheduleIn {
  gap_min: number;
  close_before_min: number;
  open_for_days: number;
  skip_inactive_days: boolean;
  week: ScheduleDay[];
  extra: ScheduleDateRange[];
  cut: ScheduleDateRange[];
  /** Не прислан — остаётся прежний тип графика. */
  kind?: ScheduleKind;
}

/** Строка меню «Выбрать сотрудника». */
export interface SchedulePerson {
  id: string;
  name: string;
  avatar_url: string | null;
  is_me: boolean;
  configured: boolean;
  /** «Рекрутёр», «Руководитель поиска», «Администратор». */
  role_label: string;
  /** Общий график: тип и неделя (для строки «Пн–Пт · 09:00–18:00»); не настроен — пусто. */
  kind: ScheduleKind;
  week: ScheduleDay[];
}

/** Свободные слоты рекрутёра под кандидатуру; длительность — из её вакансии.
 *  `configured: false` — расписание не настроено, слотов нет. */
export interface ScheduleSlots {
  configured: boolean;
  duration_min: number;
  slots: { id: string; start: string; end: string; label: string }[];
}

/** Рекрутёр вакансии: чьё расписание даёт слоты записи (ats-api schedule.py). */
export interface VacancyRecruiter {
  user_id: string;
  name: string;
  avatar_url: string | null;
  role: string;
  /** «Рекрутёр», «Руководитель поиска», «Администратор». */
  role_label: string;
  /** Ответственный по вакансии: в списке всегда, убрать нельзя. */
  is_responsible: boolean;
  /** Есть расписание (общее или под вакансию); нет — слотов от него нет. */
  configured: boolean;
  /** На этой вакансии у него своё расписание («Настраиваемое»). */
  has_override: boolean;
  kind: ScheduleKind;
  /** Недельный график, по которому он принимает запись на вакансию. */
  week: ScheduleDay[];
}

/** Ответ GET и PUT /vacancies/{id}/recruiters. */
export interface VacancyRecruiters {
  vacancy_id: string;
  items: VacancyRecruiter[];
  /** Запись кандидата в пул включена на вакансии: ссылки бота и автоответов
   *  ведут на общий календарь этих рекрутёров, а не на ответственного. Нет
   *  поля — старый ats-api, пул выключен. */
  pool_enabled?: boolean;
}

/** Ответ GET /vacancies/{id}/schedule: рекрутёры и объединённая неделя. */
export interface VacancyScheduleOverview {
  vacancy_id: string;
  title: string;
  duration_min: number;
  recruiters: VacancyRecruiter[];
  /** Объединение недель рекрутёров (еженедельный график). */
  week: ScheduleDay[];
  /** Сколько рекрутёров вакансии настроили расписание. */
  configured_count: number;
  /** См. `VacancyRecruiters.pool_enabled`. */
  pool_enabled?: boolean;
}

/** Текст сообщения кандидату при записи на встречу: общий и, если есть, свой у
 *  вакансии (PRI-194, окно «Расписание вакансии»). */
export interface VacancyMessage {
  key: string;
  label: string;
  /** Свой текст вакансии; null — кандидат получает общий. */
  body: string | null;
  /** Общий текст из «Настройки → Шаблоны». */
  default_body: string;
}

/** Ответ GET и PUT /vacancies/{id}/schedule-messages. */
export interface VacancyMessages {
  vacancy_id: string;
  items: VacancyMessage[];
  /** Кнопки подстановок: {ИМЯ}, {ВАКАНСИЯ}, {ДАТА}, {ВРЕМЯ}, {ССЫЛКА}. */
  placeholders: { token: string; label: string }[];
}

/** Путь расписания: без `userId` — своё. */
const schedulePath = (userId?: string): string =>
  userId ? `/schedule?user_id=${encodeURIComponent(userId)}` : "/schedule";

/** Путь расписания рекрутёра на вакансии (`vacancy_id` — переопределение). */
const vacancySchedulePath = (vacancyId: string, userId?: string): string => {
  const p = new URLSearchParams({ vacancy_id: vacancyId });
  if (userId) p.set("user_id", userId);
  return `/schedule?${p.toString()}`;
};

export interface CompanyMeeting {
  id: string;
  candidate_id: string;
  /** Человек кандидатуры: по нему ящик встречи открывает переписку. */
  person_id?: string | null;
  candidate_name: string | null;
  vacancy_id: string | null;
  vacancy_title: string | null;
  company_name: string | null;
  recruiter_id: string | null;
  recruiter_name: string | null;
  slot_start: string | null; // MSK ISO
  slot_end: string | null;
  status: string;
  /** Ссылка кандидата в видеокабинет встречи; null — кабинет ещё не
   *  открывали или его комната уже закончилась. */
  video_url?: string | null;
  /** Оценка встречи (meeting_feedback.py); у ручек, что её не знают, поля нет. */
  feedback?: MeetingFeedback;
  /** Кандидат приходил в видеокабинет этой встречи: только там его и спрашивают,
   *  как она прошла (meeting_feedback.candidate_arrived). Нет — оценки от него
   *  ждать неоткуда. */
  candidate_arrived?: boolean;
  /** Имя без фамилии (для быстрых реплик ящика); null — не разобрано. */
  candidate_first_name?: string | null;
  /** Запись ждёт подтверждения рекрутёра: красная точка, «Ждёт подтверждения»,
   *  «Подтвердить» (PRI-272). Нет поля — не ждёт. */
  awaiting_confirmation?: boolean;
  /** Напоминания кандидату, положенные встрече: «24h» и/или «1h»; пусто —
   *  запись впритык, напоминаний не будет (shared_db.meeting_reminders). */
  reminders?: string[];
  /** Есть ли у кандидата бот-чат (Telegram / MAX), куда уходят напоминания.
   *  false — не уйдёт ни одно (кандидат только с hh). Нет поля — старый ats-api. */
  reminders_reachable?: boolean;
  /** Почему `reminders` пуст: tight — записали впритык; held — пропали, пока
   *  запись ждала подтверждения; not_queued — задач нет (запись до выката
   *  правила). null / нет поля — причина не известна или напоминания есть. */
  reminders_reason?: RemindersReason | null;
  /** Встреча из пула вакансии, которую ещё никто не забрал («нужно забрать»):
   *  рекрутёра у неё нет. Нет поля — обычная встреча. */
  needs_claim?: boolean;
  /** Её может забрать тот, кто смотрит: она предложена ему и он свободен. */
  can_claim?: boolean;
  /** Заказчик вакансии: его значок стоит у подписи «Компания. Должность»
   *  карточки (`companyLogoUrl(company_id)`, когда `company_has_logo`). Нет
   *  поля — старый ats-api. */
  company_id?: string | null;
  company_has_logo?: boolean;
  /** hh id резюме с лучшим фото кандидата: `/api/resume/{id}/photo`. Плитка режима
   *  «Галерея» (PRI-273); нет — на плитке инициалы. */
  avatar_resume_id?: string | null;
}

export type RemindersReason = "tight" | "held" | "not_queued";

/** Исход сообщения кандидату о переносе или отмене: sent — ушло; no_thread —
 *  у человека нет чата; failed / unknown — не вышло / не знаем; skipped —
 *  служба сообщений не настроена. */
export type CandidateNotice = "sent" | "no_thread" | "failed" | "unknown" | "skipped";

/** Причина отмены встречи (закрытый список ящика, ats-api meeting_actions). */
export type MeetingCancelReason =
  | "candidate_asked_move"
  | "candidate_cant_time"
  | "customer_moved"
  | "no_contact"
  | "declined_vacancy";

/** Оценка одной стороны: 1–5 звёзд и необязательный отзыв. `at` — когда
 *  поставлена или изменена последний раз (ISO). */
export interface MeetingFeedbackSide {
  rating: number;
  comment: string | null;
  at: string;
}

export interface MeetingFeedbackRecruiter extends MeetingFeedbackSide {
  /** Кто оценил; null — пользователя уже нет. */
  by: string | null;
  /** Оценка — моя; иначе чужая (рекрутёр встречи или самый свежий коллега). */
  mine: boolean;
}

export interface MeetingFeedback {
  recruiter: MeetingFeedbackRecruiter | null;
  candidate: MeetingFeedbackSide | null;
}

/** Вход рекрутёра в видеокабинет встречи (ats-api video_room.py): комната
 *  LiveKit, ссылка кандидата и то, что кабинет показывает рядом с видео. */
/** Ответ «Удалить / вернуть кандидата»: какие кандидатуры затронуты. */
export interface CandidateDeleteOut {
  person_id: string | null;
  candidate_ids: string[];
}

export interface VideoRoomJoin {
  meeting_id: string;
  short_code: string;
  /** Ссылка для кандидата — `meet.hrdeva.ru/v/<код>`, живёт до `expires_at`. */
  candidate_url: string;
  expires_at: string | null;
  livekit_ws_url: string;
  livekit_token: string;
  ice_servers: { urls: string[]; username?: string | null; credential?: string | null }[];
  /** Кандидат уже соглашался на запись в этой комнате. */
  recording_consent_granted_at: string | null;
  /** Запись идёт прямо сейчас (кабинет открыли заново посреди неё). */
  recording: boolean;
  candidate_id: string;
  person_id: string | null;
  candidate_name: string | null;
  vacancy_id: string | null;
  vacancy_title: string | null;
  company_name: string | null;
  recruiter_name: string;
  slot_start: string | null;
  slot_end: string | null;
  /** Комната ожидания включена (сохранённый выбор этого рекрутёра). Нет поля —
   *  ats-api старый: ожидания нет. */
  waiting_room?: boolean;
  /** meet-service умеет комнату ожидания; false — переключатель прячем. */
  waiting_room_supported?: boolean;
}

/** Подсказка вопроса HRDeva в кабинете (ats-api video_room_hints.py): LLM
 *  предлагает вопросы по резюме и вакансии. `asked` — рекрутёр отметил «спросил»,
 *  `added` — добавил в вопросы вакансии. */
export interface VideoRoomHint {
  id: string;
  tag: string | null;
  text: string;
  created_at: string;
  asked: boolean;
  added: boolean;
}

/** Вход сисадмина в идущую встречу слушателем (ats-api video_room_listen.py):
 *  токен скрытого участника LiveKit — ни его, ни звука от него в комнате нет —
 *  и то, что экран слушателя пишет о встрече. Ссылки кандидата здесь нет. */
export interface VideoRoomListen {
  meeting_id: string;
  short_code: string;
  livekit_ws_url: string;
  livekit_token: string;
  ice_servers: { urls: string[]; username?: string | null; credential?: string | null }[];
  candidate_name: string | null;
  vacancy_title: string | null;
  company_name: string | null;
  /** Чья встреча (рекрутёр, который её назначил). */
  recruiter_name: string | null;
  slot_start: string | null;
  slot_end: string | null;
}

/** Встреча, которая идёт прямо сейчас: в её кабинете кто-то есть. */
export interface LiveVideoRoom {
  meeting_id: string;
  /** Сколько человек в кабинете (рекрутёр, кандидат, ждущий в комнате ожидания). */
  participants: number;
  /** Имена сисадминов, которые её сейчас слушают. */
  listeners: string[];
}

/** Вопрос интервью вакансии: список ведут в видеокабинете. */
export interface InterviewQuestion {
  id: string;
  text: string;
  group: string | null;
  comment: string | null;
}
/** Вопрос на сохранение: без `id` — новый, сервер выдаст. */
export type InterviewQuestionIn = Omit<InterviewQuestion, "id"> & { id?: string };

// ---- Вопросы вакансии (вкладка «Вопросы», окно «Добавить вопрос») ----

/** Канал вопроса: чат-бот, анкета лендинга или видеоинтервью. */
export type QuestionChannel = "bot" | "form" | "interview";
/** Тип ответа: единый для трёх каналов (у анкеты — по типу поля формы). */
export type QuestionAnswerType =
  | "single"
  | "multi"
  | "yesno"
  | "number"
  | "text"
  | "oral"
  | "date"
  | "file";

export interface VacancyQuestion {
  id: string;
  channel: QuestionChannel;
  position: number;
  text: string;
  answer_type: QuestionAnswerType;
  options: string[] | null;
  required: boolean;
  /** Виден только рекрутёру. */
  comment: string | null;
  group_label: string | null;
  /** Критерий отбора, по которому оценивается ответ. */
  criterion_id: string | null;
  /** Анкета — имя поля формы, интервью — прежний id вопроса. */
  ext_key: string | null;
  /** Анкета: конфигурация поля как она лежит в форме лендинга. */
  field: FormFieldConfig | null;
}

// ---- Тесты и проверки кандидата (PRI-171) ----------------------------------

export type CheckKind = "test" | "task" | "refcheck" | "docs" | "security" | "medical";
export type CheckDecision = "in_progress" | "passed" | "failed";
export type CheckStatus = "sent" | "done" | CheckDecision;

/** Настройки пункта — у каждого свои поля (ats_api.vacancy_checks.SETTINGS). */
export type CheckSettings = {
  questions?: string;
  text?: string;
  who?: "managers" | "colleagues" | "both" | null;
  count?: number | null;
  documents?: string;
  checker?: "recruiter" | "client" | "partner" | null;
  days?: number | null;
  clinic?: string;
  payer?: "client" | "candidate" | null;
};

export type VacancyCheck = {
  kind: CheckKind;
  label: string;
  enabled: boolean;
  as_stage: boolean;
  /** У пункта есть свой этап воронки (тест и тестовое задание). */
  has_stage: boolean;
  settings: CheckSettings;
};

export type VacancyCheckIn = {
  kind: CheckKind;
  enabled: boolean;
  as_stage: boolean;
  settings: CheckSettings;
};

export type CandidateCheckResult = {
  id: string;
  status: CheckStatus;
  /** Относительный адрес `/t/<token>`; только пока кандидат не ответил. */
  link: string | null;
  content: { title?: string; questions?: string[]; task?: string } | null;
  answer: { answers?: string[]; text?: string } | null;
  comment: string | null;
  sent_at: string | null;
  done_at: string | null;
  decided_at: string | null;
};

export type CandidateCheck = {
  kind: CheckKind;
  label: string;
  enabled: boolean;
  as_stage: boolean;
  has_stage: boolean;
  can_send: boolean;
  result: CandidateCheckResult | null;
};

export type PublicCheck = {
  kind: CheckKind;
  label: string;
  vacancy_title: string;
  questions: string[];
  task: string | null;
  answered: boolean;
};

export interface VacancyQuestionsOut {
  bot: VacancyQuestion[];
  form: VacancyQuestion[];
  interview: VacancyQuestion[];
  landing: {
    present: boolean;
    /** Ключ лендинга в админском API (`hrdeva:<id>`). */
    key: string | null;
    slug: string | null;
    form_mode: "injected" | "off" | "embedded" | null;
  };
}

/** Вопрос на сохранение. Поле, которого нет в запросе, сервер не меняет
 *  (у нового вопроса — значение по умолчанию); явный `null` очищает. Анкета:
 *  `ext_key` называет поле формы, меняются только `criterion_id` и `comment`. */
export interface VacancyQuestionIn {
  id?: string | null;
  ext_key?: string | null;
  text?: string;
  answer_type?: QuestionAnswerType;
  options?: string[] | null;
  required?: boolean;
  comment?: string | null;
  group_label?: string | null;
  criterion_id?: string | null;
}

// ---- endpoints ----

// ---- Landings (admin API on landings-service, proxied at /api/admin/landings) ----

export type LandingFieldType =
  | "text"
  | "textarea"
  | "tel"
  | "email"
  | "number"
  | "date"
  | "range"
  | "select"
  | "radio"
  | "chips"
  | "chips_multi"
  | "checkbox"
  | "file"
  | "image"
  | "video"
  | "audio";

export interface FormFieldConfig {
  name: string;
  type: LandingFieldType;
  label?: string | null;
  required?: boolean;
  role?: "name" | "phone" | "email" | null;
  options?: string[] | null;
  accept?: string | null;
  max_size_mb?: number | null;
  max_length?: number | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  pattern?: string | null;
  placeholder?: string | null;
  hint?: string | null;
  // chips_multi carries a list of picked values; range carries a number.
  default?: string | number | string[] | null;
  // range — unit printed next to the live value ("лет", "₽")
  unit?: string | null;
  // choice types — «Другое» escape hatch, answered in the `<name>_other` field
  allow_other?: boolean;
  other_label?: string | null;
  other_placeholder?: string | null;
  // chips_multi — how many options may be picked
  min_select?: number | null;
  max_select?: number | null;
}

export interface FormConfig {
  mode: "injected" | "off";
  placement: "top" | "bottom";
  heading?: string | null;
  subtitle?: string | null;
  submit_label?: string | null;
  /** Оформление из окна «Просмотр анкеты»: `#rrggbb` и скругление 0–24 px.
   *  Нет (null) — оформление самого лендинга. */
  button_color?: string | null;
  bg_color?: string | null;
  radius?: number | null;
  fields: FormFieldConfig[];
}

/** Обложка превью ссылки: объект лежит в хранилище лендинга под `key`. */
export interface LandingCover {
  key: string;
  size: number;
  content_type: string;
  width: number;
  height: number;
  updated_at: string;
}

/** Превью ссылки в мессенджерах (`meta.share`). Нет поля — сервер ничего не
 *  подставляет, и мессенджер берёт то, что есть на самой странице. */
export interface LandingShare {
  title?: string;
  description?: string;
  cover?: LandingCover;
}

/** Медиа-файл лендинга, который сейчас лежит в хранилище. */
export interface LandingMediaFile {
  size: number;
  content_type: string;
  /** Как файл сюда попал: приехал в архиве или положен в слот. */
  source?: "archive" | "slot";
}

/** Файл архива, который сервер не взял, и почему. */
export interface LandingSkippedFile {
  name: string;
  why: string;
}

/** Сведения о загруженном лендинге — пишутся при заливке, поэтому вкладка
 *  видит их и после перезагрузки страницы. Всё, что ниже `filename`, появилось
 *  вместе с загрузкой архивом: у лендингов, залитых раньше, этих полей нет. */
export interface LandingContentInfo {
  size: number;
  uploaded_at: string;
  media_keys: string[];
  filename?: string | null;
  /** Чем загружали: одним index.html или архивом с медиа. */
  kind?: "html" | "zip";
  /** Что реально лежит в хранилище: из архива, оставшееся от прошлой версии
   *  или загруженное в слот. */
  media_files?: Record<string, LandingMediaFile>;
  /** На что index.html ссылается, а файла нет. */
  media_missing?: string[];
  skipped?: LandingSkippedFile[];
}

export interface LandingMeta {
  form?: FormConfig | null;
  share?: LandingShare | null;
  content?: LandingContentInfo | null;
  [k: string]: unknown;
}

export interface LandingDetail {
  vacancy_id: string;
  public_slug: string;
  public_url: string;
  storage_prefix: string;
  deactivated_at: string | null;
  meta: LandingMeta;
  created_at: string;
  updated_at: string;
  /** Лендинг вакансии ещё лежит под ключом старой CRM (`crm:555`, связь —
   *  `landing_ref` вакансии). Замена переносит его на ключ вакансии. */
  legacy_key?: string | null;
  /** Тот же `crm:`-лендинг указан у нескольких вакансий и работает на другую
   *  (самую новую) — её номер. Менять его отсюда сервис не даст. */
  legacy_owner_number?: number | null;
}

export interface ContentUploadResult {
  vacancy_id: string;
  public_slug: string;
  public_url: string;
  uploaded_at: string;
  warnings: string[];
  media_keys: string[];
  /** Form config Claude Design baked into the HTML, when the page carried one. */
  form?: FormConfig | null;
  /** applied — saved (there was no form yet); proposed — a form exists, the UI
   *  asks before replacing it; invalid — rejected, reason is in `warnings`. */
  form_status?: "applied" | "proposed" | "invalid" | "absent";
  /** What was uploaded: a single index.html or an archive with its media. The
   *  four fields below mirror `meta.content` (see LandingContentInfo). */
  kind: "html" | "zip";
  media_files: Record<string, LandingMediaFile>;
  media_missing: string[];
  skipped: LandingSkippedFile[];
}

/** Answer of `PUT /admin/landings/{id}/media/{key}` — one file put into a slot. */
export interface LandingMediaUploadResult {
  key: string;
  size: number;
  content_type: string;
}

/** A row of `GET /admin/landings`. `public_slug` is the number of the vacancy
 *  the landing is published under — how the copy picker tells which vacancies
 *  already have one. */
export interface LandingListItem {
  vacancy_id: string;
  public_slug: string;
  public_url: string;
  title: string | null;
  deactivated_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Answer of `POST /admin/landings/copy`. */
export interface LandingCopyResult {
  vacancy_id: string;
  public_slug: string;
  public_url: string;
  /** What the copy had to change in the page or settings — shown under the tab. */
  warnings: string[];
}

// Vacancy briefing: «Вводные» chat + «Формализованные данные».
export interface BriefFieldChange {
  field_key?: string;
  label?: string;
  old?: string;
  new?: string;
  // Правка критерия, которую сделал разбор вводных (`type: "criteria_change"`):
  // поля те же, что у критерия; `criteria_suggestion` в ленте не показывается.
  type?: string;
  action?: string;
  category?: string;
  kind?: string;
  text?: string;
  old_text?: string;
}
export interface BriefNote {
  id: string;
  note_type: "input" | "system";
  author: string | null;
  author_user_id: string | null;
  text: string | null;
  material_kind: string; // text | document | audio | video | image | question
  filename: string | null;
  mime: string | null;
  has_file: boolean;
  // Подписи на плитках материалов: размер файла и длительность записи (по
  // расшифровке — пока её нет, null).
  size_bytes?: number | null;
  duration_sec?: number | null;
  extract_status: string; // none | pending | ready | failed
  // Transcript / parsed text of the attached material (audio → whisper,
  // docs/images → Unstructured). Present only when extract_status === "ready".
  extracted_text: string | null;
  // Backend-generated one-line summary of the attached material (audio/video/
  // document/image), rendered under the filename. Null while the LLM hasn't
  // produced one yet or the material is empty.
  material_summary: string | null;
  // Кадр из середины видео-материала (PRI-47), подписанная ссылка. null — не
  // видео или картинки ещё нет: показывается прежняя заглушка.
  poster_url?: string | null;
  // LLM-formatted paragraphs over the raw extracted text/whisper segments:
  // [{start: number|null, text: string}, ...]. `start` is the first segment
  // timestamp (seconds) for AV, null for documents/images. Rendered in the
  // transcript modal with clickable timecode chips that seek the player.
  transcript_paragraphs: { start: number | null; text: string }[] | null;
  changes: BriefFieldChange[] | null;
  trigger_note_id: string | null;
  created_at: string;
}
export interface FormalizedField {
  group: string;
  key: string;
  label: string;
  value: string;
  source_note_id: string | null;
  history_count: number;
}
export interface Brief {
  notes: BriefNote[];
  fields: FormalizedField[];
}
export interface FieldRevision {
  value: string;
  source_note_id: string | null;
  source_preview: string | null;
  created_at: string;
}

// ---- Вакансия → Информация/Публикация: рынок и текст вакансии (PRI-163, PRI-174) ----

/** Последний расчёт «Аналитики рынка». `calculated: false` — не считали. */
export interface VacancyMarket {
  calculated: boolean;
  calculated_at: string | null;
  candidates: number | null;
  vacancies: number | null;
  salary_asked: number | null;
  salary_offered: number | null;
  /** Вакансий на одного соискателя. */
  competition: number | null;
  competition_note: string | null;
  query: string | null;
  /** Какую половину расчёта hh не дал: candidates | vacancies -> текст. */
  errors: Record<string, string>;
}

export interface PublicationStyle {
  formality: number;
  openness: number;
  structure: number;
  length: number;
}
export type PublicationBlockKey = "title" | "company" | "duties" | "requirements" | "conditions";
export interface PublicationBlock {
  key: PublicationBlockKey;
  title: string;
  text: string;
  /** Правили руками. */
  edited: boolean;
}
/** Статус публикации у hh: опубликована / в архиве / не найдена / не знаем. */
export type HhPublicationStatus = "published" | "archived" | "not_found" | "unknown";
export interface HhPublication {
  id: string;
  hh_vacancy_id: string;
  url: string;
  status: HhPublicationStatus;
  note: string | null;
}
export interface Publication {
  style: PublicationStyle;
  blocks: PublicationBlock[];
  generated_at: string | null;
  has_text: boolean;
  hh: HhPublication[];
}

// ---- Автоматизация → Отклики (VacancyReplyAutomation) ----

export type ReplyStage1Mode =
  | "nobody"
  | "all"
  | "screening_failed"
  | "screening_scoring_failed"
  // Только «Скоринг» (без «Скрининга»): отказ, если балл ниже порога.
  | "scoring_failed";
export type ReplyStage2Mode = "nothing" | "to_bot" | "to_bot_via_landing" | "to_think";
/** Единица окна отложенного отказа («в течение N минут / часов / дней»). */
export type RejectDelayUnit = "minutes" | "hours" | "days";

export type ReplyStage3Mode =
  | "nobody"
  | "screening_failed"
  | "screening_scoring_failed"
  | "all";
export type ReplyStage4Mode = "auto" | "manual_review";
export type ReplyReanimMode =
  | "nobody"
  | "all"
  | "screening_passed"
  | "screening_scoring_passed";

export interface AutomationRepliesConfig {
  stage1_mode: ReplyStage1Mode;
  stage1_template_key: string | null;
  stage1_score_threshold: number | null;
  stage2_mode: ReplyStage2Mode;
  stage2_bot_template_key: string | null;
  stage2_landing_template_key: string | null;
  stage2_think_reject_template_key: string | null;
  stage2_think_confirm_template_key: string | null;
  stage3_mode: ReplyStage3Mode;
  stage3_template_key: string | null;
  stage3_score_threshold: number | null;
  /** «Отказать в течение N …»: отказ уходит в случайный момент внутри окна,
   *  а не ровно в его конце — иначе все отказы вакансии приходят минута
   *  в минуту. Окно шире 60 дней бэкенд не принимает. */
  stage3_delay_value: number;
  stage3_delay_unit: RejectDelayUnit;
  stage4_mode: ReplyStage4Mode;
  stage4_auto_template_key: string | null;
  stage4_manual_confirm_template_key: string | null;
  stage4_manual_reject_template_key: string | null;
  answer_questions: boolean;
  handle_objections: boolean;
  reanim_mode: ReplyReanimMode;
  reanim_score_threshold: number | null;
  reanim_channels: string[];
  /** «Нет этапа» у «Квалифицирован» / «Пригласили в бот» (false). Это НЕ
   *  отключение оценки: скрининг и скоринг идут всегда, флаг убирает этап и его
   *  автоматику. Сервер отдаёт их всегда; в PUT применяются, только если ключ
   *  передан, поэтому поля необязательные. */
  qual_enabled?: boolean;
  invite_enabled?: boolean;
  /** Шаг «Пригласили в бот» → «Что отправляем в боте?»: `bot_extra` — «Ещё»,
   *  `bot_extra_landing` / `bot_extra_form` — галочки «Лендинг» и «Анкета» (обе —
   *  лендинг со встроенной формой). Три ключа — шаблоны сообщения со ссылкой и ответов
   *  бота на «Открыл лендинг» / «Заполнил анкету». Как и «Нет этапа», в PUT
   *  применяются, только если ключ передан. */
  bot_extra?: boolean;
  bot_extra_landing?: boolean;
  bot_extra_form?: boolean;
  bot_link_template_key?: string | null;
  bot_landing_opened_template_key?: string | null;
  bot_form_received_template_key?: string | null;
  /** Первое сообщение бота в Telegram/MAX после приветствия: "tpl:<id>" —
   *  выбран здесь, иной текст — «сырой» текст из старого интерфейса, null — ничего. */
  first_bot_message_template: string | null;
}

/** Шаблон в выпадающем списке «Автоматизация → Отклики». Это шаблоны чата
 *  (страница «Шаблоны»): `key` = "tpl:<id>", его и хранят настройки.
 *  `kind: "system"` — системное сообщение, выбранное до переезда на шаблоны
 *  чата; в списке оно есть, только пока настройка на него ещё ссылается. */
export interface SystemMessageBrief {
  key: string;
  label: string;
  category: string;
  body: string;
  kind: "chat" | "system";
  /** Шаблон чата: id, общий ли он и место в списке — для редактора. */
  template_id: string | null;
  is_public: boolean;
  position: number;
  /** false — личный шаблон другого рекрутёра: выбрать можно, править нет. */
  editable: boolean;
}

/** Почему вакансия может не разбирать отклики, хотя шаги на вкладке выбраны.
 *  Условия живут не в настройках шагов: критерии отбора, связка с hh и то,
 *  дошла ли оценка до уже собранных откликов. */
export interface AutomationHealth {
  criteria_total: number;
  /** Критериев, проверяемых по резюме. Ноль — вердикта не будет ни у кого. */
  criteria_by_resume: number;
  hh_linked: boolean;
  candidates_total: number;
  candidates_unscreened: number;
  /** Без вердикта и без резюме в кэше — таких не оценить даже прогоном. */
  candidates_without_resume: number;
}

export interface AutomationRepliesOut {
  config: AutomationRepliesConfig;
  templates: SystemMessageBrief[];
  /** Ссылки из настроек, по которым шаблона больше нет: шаг шлёт пустоту. */
  missing_templates: string[];
  /** Условия вне вкладки: без них выбранные шаги не срабатывают. Старый
   *  бэкенд поля не отдаёт — тогда разбор не показываем. */
  health: AutomationHealth | null;
  /** Показывать ли экран «Отклики» тремя шагами эталона. Выключается без
   *  выката (app_settings); старый бэкенд поля не отдаёт — тогда true. */
  ui_steps?: boolean;
  /** Флаг `funnels.new_stages`: этап «Ждут решения» уже есть в воронках — шаг
   *  «Квалифицирован» называет им, куда уходит кандидат при «Общения нет».
   *  Старый бэкенд поля не отдаёт — читается как выключенный. */
  new_stages?: boolean;
}

/** Состояние шага «Отклики»: нет этапа / вручную / автоматически. */
export type AutomationStepState = "none" | "manual" | "auto";

/** Шаги «Отклики» вакансии и правила «Общей» воронки (VacancyDetail). */
export interface AutomationSteps {
  qual: AutomationStepState;
  invite: AutomationStepState;
  general: {
    /** «Кандидат заполнил анкету → отказ» (прежний «Отказ в боте после
     *  анкеты»): auto — отказ уходит, none — нет. */
    after_form_reject: "none" | "auto";
  };
}

/** Кандидаты, которых автоматика пропустила (не ретроактивна) — см.
 *  ats_api.backfill. excluded_touched — рекрутёр уже вёл их вручную,
 *  бэкафилл их не трогает. */
export interface BackfillAuditOut {
  needs_screening: number;
  needs_action: number;
  excluded_touched: number;
}

export interface BackfillRunOut {
  screened: number;
  rejected_sent: number;
  skipped_touched: number;
  errors: string[];
}

/** Вакансия → «Для кандидата»: презентация и FAQ — единственное, из чего
 *  Дева отвечает кандидату. Строка FAQ без ответа хранится как напоминание,
 *  в ответы она не попадает. */
export interface VacancyFaqItem {
  question: string;
  answer: string;
}

export interface VacancyCandidateInfoIn {
  intro_conditions: string;
  intro_responsibilities: string;
  intro_about_company: string;
  faq: VacancyFaqItem[];
}

export interface VacancyCandidateInfo extends VacancyCandidateInfoIn {
  /** null — блок ни разу не сохраняли (проекта подбора у вакансии ещё нет). */
  updated_at: string | null;
}

/** Тестовый чат вкладки «Для кандидата»: что сделал бы гейт автоответа с этим
 *  вопросом. `route`: "none" — вопроса не увидел (уйдёт шаблон как есть),
 *  "answer" — отвечает (при `sensitivity: "sensitive"` ответ ляжет черновиком
 *  рекрутёру на подтверждение), "handoff" — промолчит и передаст рекрутёру. */
export interface VacancyQuestionTest {
  route: "none" | "answer" | "handoff" | string;
  question: string | null;
  sensitivity: "trivial" | "sensitive" | null;
  reason: string | null;
  answer: string | null;
  full_reply: string | null;
  /** Гейт не доехал (инференс недоступен): в бою это «шаблон как есть». */
  error: string | null;
  /** false — блок «Для кандидата» пуст, отвечать не из чего. */
  has_facts: boolean;
}

/** Вакансия → «Отбор»: сетка критериев 7 категорий × 3 уровня. */
export type CriterionCategory =
  | "experience"
  | "education"
  | "hards"
  | "softs"
  | "motivation"
  | "personality"
  | "conditions"
  // «Дискриминация» (PRI-165): только группировка и классификация отказов,
  // на логику скрининга не влияет; в ссылке заказчика не показывается.
  | "discrimination";
/** must — обязательный, nice — желательный (скоринг), stop — стоп-фактор. */
export type CriterionKind = "must" | "nice" | "stop";

export interface VacancyCriterion {
  id: string;
  category: CriterionCategory;
  kind: CriterionKind;
  text: string;
  /** Вес в процентах; только у желательных, в сумме по вакансии 100. */
  weight: number | null;
  /** Чем проверяется: кодом по полям резюме, Девой по тексту или никак
   *  (по резюме не проверить). null — ещё не разобран. */
  check_method: "code" | "llm" | "skipped" | null;
  /** pending — разбирается моделью; failed — не разобран и НЕ проверяется. */
  formalize_status: "pending" | "ready" | "failed";
  /** Как система поняла критерий («возраст от 20 до 26»). */
  rule_summary: string | null;
  /** Сколько живых вопросов (бот, анкета, интервью) привязано к критерию. */
  question_count?: number;
  questions?: { id: string; channel: QuestionChannel; text: string }[];
  /** Критерий оценивается по ответам кандидата на привязанные вопросы анкеты:
   *  правила по резюме у него нет, а оценка по ответам для вакансии включена. */
  scored_by_answers?: boolean;
}

export interface VacancyCriterionIn {
  /** id существующей строки; у новой — нет. */
  id?: string | null;
  category: CriterionCategory;
  kind: CriterionKind;
  text: string;
  weight?: number | null;
}

/** Черновик модели — в базу не пишется, пока рекрутёр не сохранит сетку. */
export interface VacancyCriterionDraft {
  category: CriterionCategory;
  kind: CriterionKind;
  text: string;
  weight: number | null;
}

/** Запуск «оценить кандидатов по критериям». */
export interface ScreeningRun {
  id: string;
  status: "running" | "done" | "cancelled" | "failed";
  total: number;
  processed: number;
  passed: number;
  rejected: number;
  failed: number;
  last_error: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface ScreeningRunState {
  /** Последний запуск вакансии в любом статусе; null — не запускали. */
  run: ScreeningRun | null;
  /** Сколько кандидатур возьмёт запуск, начатый сейчас. */
  eligible: number;
  /** …из них вообще без оценки. */
  unscreened: number;
  /** Критериев, проверяемых по резюме; 0 — оценивать нечем. */
  checkable: number;
}

export interface ManualReviewOut {
  ok: boolean;
  stage: "bot_invite" | "meeting_confirm";
  decision: "confirm" | "reject";
  reply_sent: boolean;
  hh_moved: string | null;
  errors: string[];
}

/** Пара «похоже, что один человек» из очереди разбора. */
export interface DuplicatePairSide {
  id: string;
  full_name: string | null;
  avatar_resume_id: string | null;
  avatar_url?: string | null;
}

export interface DuplicatePair {
  /** Расстояние между аватарками; null — пару нашли по общему контакту. */
  distance: number | null;
  confidence: number | null;
  /** Что совпало у контактной пары: «телефон 79215004314». */
  reason: string | null;
  a: DuplicatePairSide;
  b: DuplicatePairSide;
}

// ---- Правая панель чатов (эталон ats.recruto.ru, 2026-09-29) ----
// Кандидатуры человека с историей этапов, встречи, файлы кандидатур (ats-api)
// и вложения/события его переписки (chat-service). Формы — ровно по
// контракту RP-CONTRACT §2.1–§3.

/** Строка истории кандидатуры (новейшие первыми). */
export interface PanelHistoryItem {
  /** created | stage | status | bot_started | landing_opened | landing_submitted |
   *  meeting_booked | meeting_held | meeting_cancelled | shared | client_decision |
   *  screening — неизвестный вид показываем по `label`. */
  kind: string;
  label: string;
  at: string;
  tone: "blue" | "red" | null;
  /** Кто сменил — только у строк журнала этапов (stage / status); у
   *  восстановленных по другим таблицам и у старого ats-api полей нет.
   *  `source` — источник события (manual, slots, observer…), `user_name` —
   *  автор-человек, `auto` — сменилось без человека, `source_label` —
   *  источник словами («кандидат записался на слот»). */
  source?: string | null;
  user_name?: string | null;
  auto?: boolean | null;
  source_label?: string | null;
  /** Строка ленты о смене этапа / статуса: клик по записи в истории
   *  прокручивает ленту к ней. Нет — строки нет (запись шумная или со своей
   *  строкой), и старый ats-api. */
  jump?: FeedJump | null;
}

export type CandidateStatusValue =
  | "active"
  | "not_reached"
  | "no_consent"
  | "self_declined"
  | "not_passed"
  | "passed";

/** Статусы, которые рекрутёр ставит руками через POST /candidates/{id}/status. */
export type ManualCandidateStatus = "self_declined" | "not_reached" | "passed";

/** Смена этапа / статуса кандидатуры для всплывашки — GET
 *  /chat/stage-events/{id} (chat-service). SSE `stage` несёт только id, текст
 *  приходит отсюда. `from`/`to` — значения enum, `*_label` — они словами;
 *  `actor_*` пусты у автоматической смены (`auto`). */
export interface StageEventSnapshot {
  id: string;
  person_id: string | null;
  person_name: string | null;
  candidate_id: string;
  vacancy_title: string | null;
  kind: "stage" | "status" | string;
  from: string | null;
  to: string;
  from_label: string | null;
  to_label: string | null;
  /** Строка ленты склеила этап и статус одним действием: статус рядом с
   *  этапом (`kind` тогда "stage"); причина его отказа — в `reason`. */
  status_to?: string | null;
  status_to_label?: string | null;
  reason: string | null;
  source: string | null;
  source_label: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  auto: boolean;
  /** Чья смена: рекрутёра, кандидата или Девы — у всплывашки её аватарка. */
  actor?: "user" | "candidate" | "deva";
  created_at: string;
  vacancy_id?: string | null;
  /** Строка ленты об этой смене; null — ещё не написана или не будет. */
  feed_message_id?: string | null;
}

/** Кандидатура человека на вакансию — карточка `.cp-vac` панели. */
export interface PanelCandidature {
  candidate_id: string;
  vacancy_id: string;
  vacancy_title: string;
  vacancy_number: number | null;
  company_id: string | null;
  company_name: string | null;
  company_has_logo: boolean;
  stage: string;
  stage_label: string;
  /** С какого момента на этапе: stage_changed_at, иначе новейшее событие
   *  истории, иначе создание. */
  stage_since: string | null;
  status: CandidateStatusValue | string;
  reject_reason: string | null;
  /** Причины текущего отказа по одной (только у not_passed). Нет поля —
   *  старый ats-api; пусто — отказ без причин из дерева (старый, импорт). */
  reject_reasons?: CandidateRejectReason[];
  /** Когда закрыли (последнее status-событие не active); иначе null. */
  closed_at: string | null;
  track: FunnelTrack | null;
  /** Меню «Дальше» и окно отказа: цепочка этапов воронки кандидатуры, с сервера
   *  (флаг new_stages знает только он). Нет полей — старый ats-api, тогда цепочку
   *  строит lib/funnels по `track` и `from_traffic`. */
  stage_chain?: StageChainItem[];
  next_stage?: NextStage | null;
  /** Пришёл с трафиковой публикации hh: «откуда» кандидатуры — Трафик, а не
   *  Отклик/Приглашение (плашка в карточке). Нет поля — старый ats-api. */
  from_traffic?: boolean;
  source: string | null;
  created_at: string;
  /** Балл 0..100; null — скоринга не было. */
  scoring_pct: number | null;
  hh_resume_id: string | null;
  /** Есть отклик/переговоры на hh, которые можно перевести в отказ. */
  has_hh_response: boolean;
  history: PanelHistoryItem[];
}

export interface PanelMeetingRecording {
  id: string;
  stream_url: string;
  /** Кадр из середины видео (PRI-47); null — картинки нет. */
  poster_url?: string | null;
  duration_s: number | null;
  transcript_status: string | null;
}

/** Встреча любой кандидатуры человека, в том числе отменённая. */
export interface PanelMeeting {
  meeting_id: string;
  candidate_id: string;
  vacancy_title: string | null;
  slot_start: string | null;
  slot_end: string | null;
  status: string;
  /** rescheduled — отменена при переносе (есть встреча, на которую перенесли). */
  state: "upcoming" | "past" | "cancelled" | "rescheduled";
  title: string;
  /** Кто проводит (created_by_user_id). */
  interviewer: { user_id: string; name: string; has_avatar: boolean } | null;
  /** Первая готовая запись встречи. */
  recording: PanelMeetingRecording | null;
  booked_via: string | null;
  /** Когда начался звонок и когда встречу завели: у видео-интервью без слота
   *  `slot_start` пуст — дату строке даёт одно из них. Нет полей — старый
   *  ats-api. */
  started_at?: string | null;
  created_at?: string | null;
}

/** Файл кандидатуры (CandidateAttachment). */
export interface PanelFile {
  id: string;
  candidate_id: string;
  kind: "file" | "photo" | "audio" | string;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  created_at: string | null;
  /** Подписанная ссылка на сам файл — как в /candidates/{id}/attachments. */
  url: string;
  /** Кадр из середины видео-вложения (PRI-47); null — не видео или картинки нет. */
  poster_url?: string | null;
  uploaded_by: "candidate" | "recruiter";
}

/** Звонок человека в панели «Звонки» карточки (GET /persons/{id}/calls). */
export interface PersonCall {
  /** id записи звонка; у пропущенного — id строки ленты. */
  id: string;
  kind: "outgoing" | "incoming" | "missed";
  at: string | null;
  /** null — у пропущенного. */
  duration_s: number | null;
  /** conversation | no_answer | voicemail | operator | test | unknown. */
  outcome: string | null;
  /** Кто набирал / принял; метки может не быть. */
  recruiter_name: string | null;
  number: string | null;
  recording_id: string | null;
}

export interface PersonCalls {
  /** Новые первыми. */
  items: PersonCall[];
  /** Пропущенные входящие, после которых никто не говорил с человеком. */
  missed_unanswered: number;
}

/** Поля карточки, которые правят руками («Редактировать»). Лежат отдельно от
 *  резюме hh и показываются поверх него. */
export interface PersonManualFields {
  /** ГГГГ-ММ-ДД. */
  birth_date?: string;
  area?: string;
  experience_months?: number;
  title?: string;
  /** Рублей в месяц. */
  salary?: number;
}

export interface PersonProfile {
  person_id: string;
  full_name: string | null;
  last_name: string;
  first_name: string;
  middle_name: string;
  manual: PersonManualFields;
  /** Что говорит резюме — подсказка в форме и значение при «сбросить». */
  resume: {
    birth_date: string | null;
    area: string | null;
    experience_months: number | null;
    title: string | null;
    salary: number | null;
  };
}

/** Правка из диалога: не присланное не трогаем, пустое (null) у полей резюме
 *  сбрасывает правку. */
export interface PersonProfilePatch {
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  birth_date?: string | null;
  area?: string | null;
  experience_months?: number | null;
  title?: string | null;
  salary?: number | null;
  /** Карточка, из которой правят: туда ляжет комментарий «что изменено». */
  candidate_id?: string;
}

export interface PersonPanelData {
  person_id: string;
  /** Ручные поля карточки («Редактировать»); старый сервер не шлёт. */
  manual?: PersonManualFields;
  /** Новейшие первыми. */
  candidatures: PanelCandidature[];
  meetings: PanelMeeting[];
  files: PanelFile[];
}

// ---- Раздел «Люди» (ats_api/people_page.py) ----

/** Строка резюме или кандидатуры человека. Кандидатура — с оценкой, вакансией
 *  и заказчиком; резюме, на которое кандидатуры нет, — «не участвует в
 *  подборе». История этапов в строку не входит: её раскрытая строка берёт из
 *  панели человека (`getPersonPanel`). */
export interface PeopleRow {
  kind: "candidacy" | "resume";
  key: string;
  candidate_id: string | null;
  /** Ключ резюме (`real_id`), общий для разных id одного резюме. */
  resume_key: string | null;
  resume_title: string | null;
  /** Чем резюме ставят на вакансию (ключ кэша резюме). */
  hh_resume_id: string | null;
  vacancy_id: string | null;
  vacancy_title: string | null;
  vacancy_number: number | null;
  company_id: string | null;
  company_name: string | null;
  company_has_logo: boolean;
  stage: string | null;
  stage_label: string | null;
  status: string | null;
  active: boolean;
  reject_reason: string | null;
  /** Оценка 0..100; null — скоринга не было. */
  score_pct: number | null;
  created_at: string;
}

/** Видео человека для миниатюры (готовая запись встречи, не звонок). */
export interface PeopleVideo {
  id: string;
  candidate_id: string;
  duration_s: number | null;
  poster_url: string | null;
  stream_url: string;
  transcript_status: string | null;
  vacancy_title: string | null;
  company_name: string | null;
  started_at: string | null;
}

export interface PeopleItem {
  id: string;
  full_name: string | null;
  age: number | null;
  city: string | null;
  avatar_url: string | null;
  added_at: string;
  last_activity_at: string;
  in_work: boolean;
  resume_count: number;
  candidacy_count: number;
  rows: PeopleRow[];
  /** Сколько строк не поместилось в список (остальное — в карточке). */
  rows_hidden: number;
  videos: PeopleVideo[];
  videos_total: number;
}

export interface PeoplePageOut {
  items: PeopleItem[];
  /** «Людей в базе» — вся база, без фильтров. */
  total_base: number;
  /** «Показано» — по текущим фильтрам; `shown_capped` — сервер не считал дальше. */
  shown: number;
  shown_capped: boolean;
  has_more: boolean;
}

export type PeopleSort = "name" | "activity" | "added";
export type PeopleHasKey = "video" | "social" | "docs" | "contacts";
export type PeopleActivityKey = "week" | "month" | "old";

/** Счётчики рельса — по всей базе, а не по текущей выдаче. */
export interface PeopleFacets {
  total: number;
  has: Record<PeopleHasKey, number>;
  was: { yes: number; no: number };
  activity: Record<PeopleActivityKey, number>;
  /** Частые названия резюме — список «Должность». */
  titles: string[];
}

export interface PersonCardResume {
  /** Совпадает с `PeopleRow.resume_key`. */
  key: string;
  hh_resume_id: string;
  hh_url: string | null;
  title: string | null;
  source: string | null;
  updated_at: string;
  salary: { amount: number; currency: string } | null;
  area: string | null;
  about: string | null;
  /** Первое сообщение отклика на hh; null — отклика с текстом не было. */
  cover_letter: string | null;
  experience_total_months: number | null;
  experience: {
    company: string | null;
    company_logo: string | null;
    position: string | null;
    period: string | null;
    description: string | null;
  }[];
  education: {
    name: string | null;
    organization: string | null;
    result: string | null;
    year: number | null;
  }[];
  /** Уровень образования резюме в целом («Высшее»); у отдельных заведений hh его не даёт. */
  education_level: string | null;
  languages: string[];
  skills: string[];
  /** «Дополнительно»: командировки, гражданство, разрешение на работу… */
  extra: { label: string; value: string }[];
}

export interface PersonCardLink {
  kind: string;
  label: string;
  url: string;
  meta: string | null;
}

export interface PersonCardOut {
  id: string;
  full_name: string | null;
  last_name: string | null;
  first_name: string | null;
  middle_name: string | null;
  age: number | null;
  city: string | null;
  birth_date: string | null;
  phones: { value: string; label: string | null }[];
  emails: string[];
  socials: PersonSocial[];
  avatar_url: string | null;
  photos: { url: string; source: "resume" | "attachment" }[];
  links: PersonCardLink[];
  source: string | null;
  source_label: string | null;
  added_at: string;
  last_activity_at: string;
  in_work: boolean;
  rows: PeopleRow[];
  resumes: PersonCardResume[];
  /** Готовые записи встреч человека (не звонки), новейшие первыми. */
  videos: PeopleVideo[];
}

/** Кто инициатор отказа: рекрутёр, заказчик или сам кандидат. */
export type RejectInitiator = "rec" | "cust" | "cand";

/** Деталь причины — третий уровень дерева («Нечестность → приписал опыт»). */
export interface RejectTreeDetail {
  code: string;
  name: string;
  /** Подсказка под «?»; переводы строк показываются как есть. */
  hint: string | null;
}

export interface RejectTreeReason {
  code: string;
  name: string;
  hint: string | null;
  details: RejectTreeDetail[];
}

export interface RejectTreeGroup {
  code: string;
  name: string;
  /** Каким инициаторам группа видна. */
  initiators: RejectInitiator[];
  /** «Родная» группа инициатора («Решение заказчика», «Причины кандидата»):
   *  в пути причины её название не пишется. */
  own_initiator: RejectInitiator | null;
  reasons: RejectTreeReason[];
}

/** Дерево причин отказа: группа → причина → деталь (GET /reject-reasons/tree). */
export interface RejectTree {
  initiators: { key: RejectInitiator; label: string }[];
  groups: RejectTreeGroup[];
}

/** Узел справочника причин для редактора («Настройки» → «Причины отказа»):
 *  и выключенный, со счётчиком применений в текущих отказах. */
export interface RejectDictNode {
  id: string;
  code: string;
  name: string;
  hint: string | null;
  /** Показывается ли в окне отказа («в шторке»). */
  is_active: boolean;
  /** Заведён системой — не стирается, а скрывается. */
  builtin: boolean;
  /** Сколько текущих отказов называют узел (у группы — сумма по причинам). */
  count: number;
}

export interface RejectDictReason extends RejectDictNode {
  details: RejectDictNode[];
}

export interface RejectDictGroup extends RejectDictNode {
  /** Чьи вкладки в окне отказа показывают группу. */
  initiators: RejectInitiator[];
  /** «Родная» группа инициатора: вкладки не меняются. */
  own_initiator: RejectInitiator | null;
  reasons: RejectDictReason[];
}

/** GET /reject-dictionary и ответ любой правки. */
export interface RejectDictionary {
  /** Менять может сисадмин; остальные видят справочник на чтение. */
  can_edit: boolean;
  initiators: { key: RejectInitiator; label: string }[];
  groups: RejectDictGroup[];
  totals: { groups: number; reasons: number };
  /** Только в ответе на удаление: стёрт узел или скрыт (уже применялся). */
  result?: { deleted: boolean; hidden: boolean };
}

export interface RejectDictNodeCreate {
  kind: "group" | "reason" | "detail";
  /** Группа — для причины, причина — для детали. */
  parent_id?: string | null;
  name: string;
  hint?: string | null;
  initiators?: RejectInitiator[];
}

export interface RejectDictNodePatch {
  name?: string;
  hint?: string | null;
  is_active?: boolean;
  /** Перенести причину в другую группу. */
  parent_id?: string;
  initiators?: RejectInitiator[];
}

/** Причина в теле отказа: узел дерева (`node` с `details`) или своя (`custom`).
 *  Заметка — для своих, кандидату не уходит. */
export interface RejectReasonIn {
  initiator: RejectInitiator;
  node?: string;
  details?: string[];
  custom?: string;
  note: string | null;
}

/** Причина записанного отказа — как её отдают карточка и панель. */
export interface CandidateRejectReason {
  initiator: RejectInitiator;
  initiator_label: string;
  /** «Опыт → мало опыта»; у своей причины — её текст. */
  path: string;
  /** Код узла дерева; null — своя причина. */
  node: string | null;
  details: { code: string; name: string }[];
  note: string | null;
  /** manual | screening | extension | automation | import. */
  source: string;
  /** Кто отказал; null — автоматика. */
  by: string | null;
  at: string | null;
}

/** Шаблон сообщения об отказе (системное сообщение категории rejection). */
export interface RejectTemplate {
  key: string;
  title: string;
  /** Сырой текст с плейсхолдерами — подставляет сервер (rejectPreview). */
  text: string;
  /** style — три стиля эталона («Вежливый отказ»…), template — шаблоны из
   *  «Системных сообщений». Нет поля — старый ats-api (только шаблоны). */
  kind?: "style" | "template";
}

export interface RejectMessage {
  template_key: string | null;
  /** Кто написал текст: Дева (шаблон как есть) или рекрутёр (поправил сам). */
  author?: "deva" | "self";
  /** Готовый текст: превью шаблона или правка рекрутёра. null — сервер
   *  подставит шаблон сам. */
  text: string | null;
  /** Треды этого человека, куда отправить. */
  thread_ids: string[];
}

export interface RejectCandidateBody {
  /** Причины из дерева и свои — по инициаторам. */
  reasons?: RejectReasonIn[];
  /** Причина одной строкой — прежняя форма тела (сервер её по-прежнему
   *  принимает); при `reasons` не нужна. */
  reason?: string | null;
  hh_discard?: boolean;
  /** Комментарий для команды — ляжет ручной заметкой кандидатуры. */
  comment?: string | null;
  /** null / нет — отказать без сообщения кандидату. */
  message?: RejectMessage | null;
}

export interface RejectSendResult {
  thread_id: string;
  ok: boolean;
  error: string | null;
  /** chat-service не ответил вовремя: сообщение, возможно, уже ушло (он
   *  доводит отправку сам). Не «не отправлено» — проверить чат. */
  unknown?: boolean;
}

export interface RejectCandidateResult {
  ok: boolean;
  hh_moved: boolean;
  hh_error: string | null;
  /** По треду: ушло ли сообщение. Нет поля — сообщения не было (или старый
   *  ats-api). Отказ в любом случае уже записан. */
  sent?: RejectSendResult[];
  /** Причины одной строкой («Опыт → мало опыта; …») — то, что легло в
   *  `reject_reason` кандидатуры, и они же по одной. */
  reject_reason?: string | null;
  reject_reasons?: CandidateRejectReason[];
}

export type PersonAttachmentBucket = "files" | "media";

/** Вложение из переписки человека (все его треды). */
export interface PersonAttachment {
  message_id: string;
  thread_id: string;
  /** sent_at сообщения строкой как есть — для прыжка ленты (ChatFocus.sentAt). */
  sent_at: string;
  /** Номер вложения в сообщении. */
  index: number;
  kind: "document" | "photo" | "video" | "round" | string;
  name: string | null;
  mime: string | null;
  size: number | null;
  url: string | null;
  direction: "in" | "out";
}

export interface PersonAttachments {
  counts: { files: number; media: number };
  items: PersonAttachment[];
  /** Курсор следующей страницы (старше); null — это всё. */
  next_before: string | null;
}

/** Событие переписки, к которому панель прокручивает ленту. */
export type PersonEvent =
  | {
      kind: "landing_submitted";
      message_id: string;
      thread_id: string;
      sent_at: string;
      has_video: boolean;
    }
  | {
      kind: "meeting";
      meeting_id: string;
      /** null — неизвестное событие (сервер отдаёт booked|rescheduled|cancelled,
       *  а с итогом встречи — ещё held|no_show). */
      meeting_event: "booked" | "rescheduled" | "cancelled" | "held" | "no_show" | string | null;
      message_id: string;
      thread_id: string;
      sent_at: string;
    };

// ---- Поиск под вакансию (sourcing) ----
// Страница «Поиск»: параметры поиска (ats-api sourcing_params.SearchParams),
// карточка выдачи (sourcing_cards.Card) и ответы ручек /api/sourcing/*. Поля —
// как в JSON сервера: snake_case, uuid и даты строками. Сервер отдаёт модели
// целиком (со значениями по умолчанию), поэтому необязательных полей здесь нет.

export type SourcingSource = "own" | "hh";
/** Итог скрининга карточки; «быстрый» он или сохранённый — `screening_source`. */
export type SourcingScreening = "passed" | "partial" | "unknown" | "failed";
/** `age` и `salary` — по возрастанию: моложе и дешевле выше. */
export type SourcingSort = "match" | "age" | "salary" | "score" | "experience" | "updated";

/** Значение справочника hh (регион, проф. роль, навык, страна): id и подпись,
 *  с которой его выбрали, — чтобы плашку не приходилось искать в справочнике. */
export interface SourcingRef {
  id: string;
  name: string;
}

export interface SourcingLanguage {
  id: string;
  name: string;
  /** a1..c2 | l1 (родной); null — уровень не важен. */
  level: string | null;
}

/** Один текстовый запрос; имена и значения — как у hh (`text.logic`, …). */
export interface SourcingQuery {
  text: string;
  logic: "all" | "any" | "phrase" | "except";
  field:
    | "everywhere"
    | "title"
    | "education"
    | "skills"
    | "experience"
    | "experience_company"
    | "experience_position"
    | "experience_description";
  /** За какой срок опыта искать — имеет смысл только у полей `experience*`. */
  period: "all_time" | "last_year" | "last_three_years" | "last_six_years";
}

/** «Своя база — дополнительно»: что у человека есть в нашей системе. Те же
 *  ключи — у отметок карточки (`SourcingCard.has`) и счётчиков `facets.has`. */
export interface SourcingExtras {
  video: boolean;
  voice: boolean;
  socials: boolean;
  documents: boolean;
  screened: boolean;
  call: boolean;
  applied: boolean;
  comments: boolean;
}

export interface SourcingParams {
  version: number;
  own: boolean;
  hh: boolean;
  // Выдача под вакансию — применяется после поиска к карточкам обоих источников.
  /** Пусто — все. */
  screening: SourcingScreening[];
  score_from: number | null;
  score_to: number | null;
  exp_years_from: number | null;
  exp_years_to: number | null;
  hide_in_funnel: boolean;
  show_dismissed: boolean;
  sort: SourcingSort;
  // Параметры резюме — оба источника, имена как у hh.
  /** Не больше шести непустых. */
  queries: SourcingQuery[];
  /** 1 | 3 | 7 | 30 | 365; null — за всё время. */
  period_days: number | null;
  /** «Резюме за период — Интервал»: даты `YYYY-MM-DD`. Заданы — `period_days`
   *  не действует. */
  date_from: string | null;
  date_to: string | null;
  areas: SourcingRef[];
  relocation: "living_or_relocation" | "living" | "living_but_relocation" | "relocation" | null;
  job_search_status: string[];
  /** «Без статуса поиска» — только своя база. */
  no_job_search_status: boolean;
  business_trip_readiness: string[];
  age_from: number | null;
  age_to: number | null;
  only_with_age: boolean;
  only_with_photo: boolean;
  employment_form: string[];
  work_format: string[];
  experience: string[];
  professional_roles: SourcingRef[];
  gender: "male" | "female" | null;
  /** Рубли. */
  salary_from: number | null;
  salary_to: number | null;
  only_with_salary: boolean;
  citizenship: SourcingRef[];
  work_ticket: SourcingRef[];
  exclude_viewed_by_me: boolean;
  exclude_viewed_by_company: boolean;
  only_in_responses: boolean;
  education_levels: string[];
  /** «Учебное заведение»: id из подсказок hh. */
  universities: SourcingRef[];
  skills: SourcingRef[];
  /** «Подтверждённые навыки»: отдельной отметки нет ни у hh, ни у нас — ищем
   *  среди ключевых навыков. */
  verified_skills: SourcingRef[];
  driver_license_types: string[];
  only_with_vehicle: boolean;
  languages: SourcingLanguage[];
  /** Только своя база. */
  extras: SourcingExtras;
}

/** Одна проверка скрининга или скоринга в карточке. */
export interface SourcingCheck {
  name: string;
  kind: "screening" | "scoring";
  blocking: boolean;
  /** null — проверить не удалось (нужна LLM или поля нет в краткой карточке). */
  passed: boolean | null;
  grade: "green" | "yellow" | "red" | null;
  note: string | null;
  /** code — посчитано на лету; llm — ждёт полного скрининга; skipped — не по
   *  резюме (анкетный критерий); full — из сохранённого вердикта. */
  method: "code" | "llm" | "skipped" | "full";
}

export interface SourcingExperience {
  company: string | null;
  position: string | null;
  /** «YYYY-MM-DD», как у hh. */
  start: string | null;
  /** null — по настоящее время. */
  end: string | null;
}

export interface SourcingCard {
  /** `${source}:${resume_id}` — ключ строки выдачи. */
  key: string;
  source: SourcingSource;
  resume_id: string;
  /** Один и тот же у резюме в своей базе и на hh — по нему склеиваются
   *  источники (mergeCards). null — резюме не с hh. */
  canon: string | null;
  person_id: string | null;
  /** ФИО; null — скрыто или нет. */
  name: string | null;
  /** Желаемая должность. */
  title: string | null;
  age: number | null;
  area: string | null;
  updated_at: string | null;
  photo_url: string | null;
  last_position: string | null;
  last_company: string | null;
  experience_months: number | null;
  /** До пяти последних мест. */
  experience: SourcingExperience[];
  salary: number | null;
  currency: string | null;
  /** Уровень образования словами. */
  education: string | null;
  /** Статус поиска словами — только у hh. */
  job_search_status: string | null;
  /** Ссылка на hh.ru, если резюме открывается нашим рекрутёрам. */
  hh_url: string | null;
  /** hh: резюме уже есть в своей базе; own: всегда true. */
  in_base: boolean;
  /** Кандидатура на ЭТОЙ вакансии. */
  candidate_id: string | null;
  candidate_stage: string | null;
  candidate_status: string | null;
  /** Кандидатуры человека на других вакансиях. */
  other_candidacies: number;
  dismissed: boolean;
  screening: SourcingScreening;
  /** fast — быстрый, без LLM; llm — оценка Девы по кнопке «Оценить
   *  показанных»; full — сохранённый вердикт кандидатуры. */
  screening_source: "fast" | "llm" | "full";
  /** Оценка Девы в очереди или идёт. */
  evaluating: boolean;
  /** Оценка Девы сделана по краткой карточке hh, а не по полному резюме. */
  eval_brief: boolean;
  /** Оценка Девы неполная: по части критериев модель не ответила. Балл по
   *  такой занижен, и «Оценить показанных» берёт карточку ещё раз. */
  eval_partial: boolean;
  score: number | null;
  screen_checked: number;
  screen_total: number;
  checks: SourcingCheck[];
  has: SourcingExtras;
  /** Позиция в выдаче источника: меньше — выше. */
  rank: number;
}

/** Счётчики рельса — по набору до фильтров выдачи: отметил «Прошёл скрининг»
 *  — у остальных статусов числа остаются. Сервер отдаёт все ключи, в том числе
 *  нулевые. */
export interface SourcingFacets {
  screening: Record<SourcingScreening, number>;
  in_funnel: number;
  dismissed: number;
  has: Record<keyof SourcingExtras, number>;
  /** Карточек до фильтров выдачи. */
  total: number;
}

/** Сохранённый поиск вакансии — общий для команды. */
export interface SourcingSavedSearch {
  id: string;
  vacancy_id: string;
  name: string;
  params: SourcingParams;
  /** llm — настроила модель; rules — собран из формальных критериев; manual —
   *  завёл рекрутёр. */
  origin: "llm" | "rules" | "manual";
  is_default: boolean;
  setup_id: string | null;
  /** Что Дева поставила сама и почему — строки для полосы под вакансией. */
  notes: string[];
  created_by: string | null;
  updated_by: string | null;
  updated_at: string;
  last_counts: { own?: number; hh?: number } | null;
  last_run_at: string | null;
}

/** Строка экрана выбора вакансии. */
export interface SourcingVacancyRow {
  id: string;
  number: number;
  title: string;
  company_id: string | null;
  company_name: string | null;
  company_has_logo: boolean;
  /** Кандидатуры в работе. */
  in_funnel: number;
  searches: number;
  /** null — поиск ещё не запускали. */
  found: number | null;
  last_run_at: string | null;
}

/** Сколько критериев у вакансии и сколько из них проверяется кодом — для
 *  подсказки «быстрый скрининг: N из M». */
export interface SourcingCriteria {
  screening: number;
  scoring: number;
  code_screening: number;
  code_scoring: number;
}

/** hh того, кто ищет: expired — токен обновляется, dead — подключать заново,
 *  none — не подключён. */
export interface SourcingHhStatus {
  connected: boolean;
  state: "ok" | "expired" | "dead" | "none";
  account_name: string | null;
}

export interface SourcingVacancyState {
  vacancy: {
    id: string;
    number: number;
    title: string;
    company_name: string | null;
    status: VacancyStatus;
    in_funnel: number;
  };
  searches: SourcingSavedSearch[];
  criteria: SourcingCriteria;
  hh: SourcingHhStatus;
  /** Сколько резюме скрыто из поиска этой вакансии («Не подходит»). */
  dismissed: number;
}

export interface SourcingSetupOut {
  setup_id: string;
  /** rules — модель не ответила, параметры собраны из формальных критериев. */
  origin: "llm" | "rules";
  params: SourcingParams;
  notes: string[];
  /** Заведённый поиск «Автонастройка»; null — не просили сохранять или у
   *  вакансии поиски уже есть. */
  search: SourcingSavedSearch | null;
}

/** Отказ источника: приходит в теле ответа 200, а не кодом ошибки, — чтобы
 *  упавший hh не ронял выдачу своей базы. `message` — по-русски, для показа. */
export interface SourcingError {
  code: string;
  message: string;
}

export interface SourcingRunOut {
  source: SourcingSource;
  items: SourcingCard[];
  page: number;
  has_more: boolean;
  /** null — источник выключен или упал. */
  found: number | null;
  /** Своя база: найдено не меньше `found` — счёт упёрся в потолок. */
  found_capped: boolean;
  /** Своя база: сколько прошло фильтры выдачи; у hh — null (он считает сам). */
  shown: number | null;
  /** Своя база — по набору, hh — по загруженной странице. */
  facets: SourcingFacets | null;
  total_base: number | null;
  backend: string | null;
  approximate: boolean;
  error: SourcingError | null;
}

export interface SourcingCountOut {
  own: {
    found: number | null;
    capped: boolean;
    total_base: number | null;
    error: SourcingError | null;
  };
  hh: {
    found: number | null;
    error: SourcingError | null;
  };
}

/** Числа справа от значений в ящике «Расширенный поиск» — только своя база:
 *  API hh их не отдаёт. Поле → значение → сколько людей выборки так указали. */
export interface SourcingFacetCounts {
  /** Людей в выборке; 0 — считать нечего или отказ (`error`). */
  total: number;
  /** Подошло больше, чем в выборке: числа — по первым найденным. */
  capped: boolean;
  /** Люди без статуса поиска — число у «Без статуса поиска». */
  no_status: number;
  counts: Record<string, Record<string, number>>;
  error: SourcingError | null;
}

export interface SourcingRecommendOut {
  ok: boolean;
  /** false — кандидатура этого человека на вакансии уже была: сервер её не
   *  двигал и не открывал заново. */
  created: boolean;
  candidate_id: string;
  person_id: string | null;
  /** Этап и статус, на которых кандидатура стоит на самом деле: у
   *  существовавшей раньше это не «Рекомендовано» и не обязательно «в работе». */
  stage: string | null;
  status: string | null;
}

/** Что «Оценить показанных» поставило в очередь. `skipped.reason`: expired —
 *  краткая карточка hh устарела (перезапустить поиск), not_found — резюме нет. */
export interface SourcingEvaluateOut {
  queued: number;
  cached: number;
  skipped: { resume_id: string; reason: string }[];
  /** Сколько оценок этой вакансии сейчас в очереди. */
  pending: number;
}

/** Состояние оценки одного резюме — им страница обновляет карточку на месте. */
export interface SourcingEvaluationRow {
  resume_id: string;
  source: SourcingSource;
  status: "pending" | "running" | "done" | "failed" | "none";
  screening: SourcingScreening | null;
  score: number | null;
  checks: SourcingCheck[];
  detail: string | null;
  eval_brief: boolean;
  /** Оценка готова, но модель ответила не по всем критериям (`detail` — по
   *  скольким): «Оценить показанных» берёт такое резюме ещё раз. */
  eval_partial: boolean;
}

export interface SourcingEvaluationsOut {
  items: SourcingEvaluationRow[];
  pending: number;
}

/** Строка справочника подсказок: у регионов есть `path`, у проф. ролей —
 *  `category`. */
export interface SourcingDictItem {
  id: string;
  name: string;
  /** «Россия / Ленинградская область / Мурино». */
  path?: string;
  category?: string;
}

export interface SourcingSearchBody {
  vacancy_id: string;
  name: string;
  params: SourcingParams;
  setup_id?: string | null;
  is_default?: boolean;
}

export interface SourcingSearchPatch {
  name?: string;
  params?: SourcingParams;
  is_default?: boolean;
  setup_id?: string | null;
}

export interface SourcingRunBody {
  vacancy_id: string;
  params: SourcingParams;
  source: SourcingSource;
  page?: number;
  /** Быстрый поиск по загруженному: слова в имени, должности, месте, регионе. */
  quick?: string;
  /** Сохранённый поиск, чьи счётчики обновить (только на первой странице). */
  search_id?: string | null;
  /** «Показать ещё» своей базы: ключи карточек, которые страница уже держит, —
   *  сервер отдаёт следующих из тех, кого она ещё не видела. */
  seen?: string[];
}

/** Резюме выдачи, как его называет сервер: вакансия + источник + ключ. */
export interface SourcingCardRef {
  vacancy_id: string;
  source: SourcingSource;
  resume_id: string;
}

export const api = {
  me: () => request<UserOut>("/me"),
  login: (email: string, password: string) =>
    request<UserOut>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  updateMyName: (full_name: string) =>
    request<UserOut>("/me", { method: "PATCH", body: JSON.stringify({ full_name }) }),

  // hh.ru OAuth — per-recruiter seats (chat replies) + admin resume pool.
  hhStatus: () => request<HhOauthStatus>("/hh/oauth/status"),
  hhStart: (pool = false, traffic = false) =>
    request<{ authorize_url: string }>(
      `/hh/oauth/start?next=newats${pool ? "&pool=1" : ""}${traffic ? "&traffic=1" : ""}`,
    ),
  hhSetPrimary: (accountId: string) =>
    request<{ status: string }>("/hh/oauth/primary", {
      method: "POST",
      body: JSON.stringify({ account_id: accountId }),
    }),
  hhDisconnect: (accountId: string) =>
    request<{ status: string }>("/hh/oauth/disconnect", {
      method: "POST",
      body: JSON.stringify({ account_id: accountId }),
    }),
  hhAdminTokens: (employerId?: string | null) =>
    request<{
      tokens: HhAdminToken[];
      daily_limit: number;
      main_employer_ids?: string[];
      companies?: HhCompany[];
    }>(`/hh/oauth/tokens${employerId ? `?employer_id=${encodeURIComponent(employerId)}` : ""}`),
  // Назвать компанию уже подключённого аккаунта по данным hh (один запрос под его токеном).
  hhRefreshEmployer: (tokenId: string) =>
    request<{ status: string; employer_id: string; employer_name: string | null }>(
      `/hh/oauth/tokens/${tokenId}/refresh-employer`,
      { method: "POST" },
    ),

  // Zoom — свой аккаунт рекрутёра: {zoom_link} в шаблонах создаёт встречу на нём.
  zoomStatus: () => request<ZoomStatus>("/zoom/oauth/status"),
  zoomStart: () => request<{ authorize_url: string }>("/zoom/oauth/start"),
  zoomDisconnect: () => request<{ status: string }>("/zoom/oauth/disconnect", { method: "POST" }),

  botLinks: () => request<BotLinksList>("/me/bot-links"),
  botLinkChallenge: (channel: "telegram" | "max") =>
    request<BotLinkChallenge>(`/me/bot-links/challenge?channel=${channel}`, { method: "POST" }),
  botLinkStatus: (token: string) =>
    request<BotLinkChallengeStatus>(`/me/bot-links/challenge/${encodeURIComponent(token)}`),
  botLinkDisconnect: (channel: "telegram" | "max") =>
    request<{ ok: boolean; was_linked: boolean }>(`/me/bot-links/${channel}`, { method: "DELETE" }),

  hhSetTrafficOnly: (tokenId: string, enabled: boolean) =>
    request<{ status: string }>(`/hh/oauth/tokens/${tokenId}/traffic-only`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),
  hhSetResumeFetch: (tokenId: string, enabled: boolean) =>
    request<{ status: string }>(`/hh/oauth/tokens/${tokenId}/resume-fetch`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),

  // telegram-user-service — sysadmin QR-login for the MTProto userbot.
  // Path lives at /api/tguser (nginx strips the prefix before proxying).
  tguserList: () =>
    request<{ accounts: TgAccount[] }>("/tguser/accounts"),
  tguserCreate: (label: string) =>
    request<TgAccount>("/tguser/accounts", {
      method: "POST",
      body: JSON.stringify({ label }),
    }),
  // Места гейтвеев. Живут в chat-service, потому что там же живёт адаптер,
  // который ходит в контейнеры, — а спрашивать состояние места умеет только он.
  /** Очередь разбора дубликатов: общий контакт (сверху) и похожие аватарки. */
  listDuplicates: () => request<{ items: DuplicatePair[] }>("/persons/duplicates"),
  /** Вердикт по паре: "same" сливает merge_id в keep_id, "different" — только помнит. */
  mergePersons: (keep_id: string, merge_id: string, decision: "same" | "different") =>
    request<{ status: string }>("/persons/merge", {
      method: "POST",
      body: JSON.stringify({ keep_id, merge_id, decision }),
    }),
  /** Подсказка кандидатов для привязки чата. Живёт в ats-api. */
  searchCandidates: (q: string) =>
    request<{ items: CandidateSearchHit[] }>(`/chat/candidates/search?q=${encodeURIComponent(q)}`),
  /** «Новые»: неопознанные входящие, свежие сверху. `limit: 0` — только число. */
  chatUnidentified: (limit?: number, signal?: AbortSignal) =>
    request<ChatUnidentifiedOut>(
      `/chat/unidentified${limit === undefined ? "" : `?limit=${limit}`}`,
      { signal },
    ),
  /** «Спам» у неопознанного отправителя (false — снять метку). */
  setThreadSpam: (threadId: string, spam: boolean) =>
    request<{ ok: boolean; thread_id: string; spam: boolean }>(
      `/chat/threads/${encodeURIComponent(threadId)}/spam`,
      { method: "PUT", body: JSON.stringify({ spam }) },
    ),
  /** Имя человека («Фамилия Имя Отчество» по частям). */
  renamePerson: (
    personId: string,
    name: { last_name: string; first_name: string; middle_name: string },
  ) =>
    request<{ full_name: string }>(`/persons/${encodeURIComponent(personId)}/name`, {
      method: "PATCH",
      body: JSON.stringify(name),
    }),
  /** «Это вот он»: привязать чат незнакомца к кандидату. */
  linkThreadPerson: (threadId: string, body: { candidate_id?: string; person_id?: string }) =>
    request<{
      ok: boolean;
      person_id: string;
      prev_person_id: string | null;
      changed: boolean;
    }>(`/chat/threads/${threadId}/link-person`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  seatList: () => request<{ seats: Seat[] }>("/chat/settings/userbots"),
  seatQr: (seatId: string) => request<SeatQr>(`/chat/settings/userbots/${seatId}/qr`),
  /** Пароль уходит насквозь в контейнер: у нас он нигде не сохраняется. */
  seat2fa: (seatId: string, password: string) =>
    request<{ ok?: boolean }>(`/chat/settings/userbots/${seatId}/2fa`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  seatSetStatus: (seatId: string, status: "active" | "disabled") =>
    request<Seat>(`/chat/settings/userbots/${seatId}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),
  seatResume: (seatId: string) =>
    request<{ resumed: boolean; was: string | null; queued: number }>(
      `/chat/settings/userbots/${seatId}/resume`,
      { method: "POST" },
    ),
  seatLogout: (seatId: string) =>
    request<Seat>(`/chat/settings/userbots/${seatId}/logout`, { method: "POST" }),

  tguserQrStart: (accountId: string) =>
    request<TgLoginState>(`/tguser/accounts/${accountId}/qr/start`, { method: "POST" }),
  tguserQrState: (accountId: string) =>
    request<TgLoginState>(`/tguser/accounts/${accountId}/qr/state`),
  tguserQrPassword: (accountId: string, password: string) =>
    request<TgLoginState>(`/tguser/accounts/${accountId}/qr/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  tguserDisable: (accountId: string) =>
    request<TgAccount>(`/tguser/accounts/${accountId}/disable`, { method: "POST" }),

  /** Таблица вакансий. `stage` отбирает на сервере — пустой значит «все».
   * `withPending` добавляет автосозданные с hh, ждущие решения, при любом
   * этапе: их показывает баннер над таблицей. */
  listVacancies: (
    opts: {
      stage?: VacancyStage | "";
      statuses?: VacancyStatus[];
      withPending?: boolean;
      /** Только вакансии этого клиента (карточка клиента). */
      companyId?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.stage) params.set("stage", opts.stage);
    if (opts.companyId) params.set("company_id", opts.companyId);
    // Статусы эталона (Заявка / Активна / Архив) отбирает сервер; пусто — все.
    if (opts.statuses?.length) params.set("statuses", opts.statuses.join(","));
    if (opts.withPending) params.set("with_pending", "true");
    const qs = params.toString();
    return request<VacancyListOut>(`/vacancies${qs ? `?${qs}` : ""}`);
  },
  listVacancyOptions: () => request<{ items: VacancyOption[] }>("/vacancies/options"),

  // Раздел «Трафик» (ats_api/traffic.py).
  listTraffic: () => request<{ items: TrafficPublication[] }>("/traffic"),
  resolveTraffic: (hhUrl: string) =>
    request<TrafficResolveOut>("/traffic/resolve", {
      method: "POST",
      body: JSON.stringify({ hh_url: hhUrl }),
    }),
  createTraffic: (body: TrafficPublicationInput) =>
    request<TrafficPublication>("/traffic", { method: "POST", body: JSON.stringify(body) }),
  updateTraffic: (id: string, body: TrafficPublicationInput) =>
    request<TrafficPublication>(`/traffic/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteTraffic: (id: string) =>
    request<{ deleted: boolean }>(`/traffic/${id}`, { method: "DELETE" }),

  linkVacancyExisting: (pendingId: string, targetVacancyId: string) =>
    request<{ ok: boolean; moved: number; dropped: number; target_id: string }>(
      `/vacancies/${pendingId}/link-existing`,
      { method: "POST", body: JSON.stringify({ target_vacancy_id: targetVacancyId }) },
    ),
  keepVacancyNew: (id: string) =>
    request<{ ok: boolean }>(`/vacancies/${id}/keep-new`, { method: "POST" }),

  changeCandidateStage: (candidateId: string, stage: string) =>
    request<ChangeStageOut>(`/candidates/${candidateId}/stage`, {
      method: "POST",
      body: JSON.stringify({ stage }),
    }),

  // Отказ: причины по инициаторам, сообщение кандидату (или без него),
  // перевод отклика hh, комментарий для команды. Один диалог на карточку
  // кандидата и панель чатов (components/reject).
  rejectCandidate: (candidateId: string, body: RejectCandidateBody) =>
    request<RejectCandidateResult>(`/candidates/${candidateId}/reject`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  reactivateCandidate: (candidateId: string) =>
    request<{ ok: boolean; status: string }>(`/candidates/${candidateId}/reactivate`, {
      method: "POST",
    }),

  // Закрыть кандидатуру не отказом: «Кандидат отказался» (причина — по
  // желанию, до 500 символов), «Не дозвонились», «Вышел на работу». Тот же
  // статус — 409 `same_status`. Отказ — rejectCandidate, возврат —
  // reactivateCandidate (у «Вышел на работу» он 409).
  setCandidateStatus: (candidateId: string, status: ManualCandidateStatus, reason?: string | null) =>
    request<{ ok: boolean; status: string }>(`/candidates/${candidateId}/status`, {
      method: "POST",
      body: JSON.stringify(reason ? { status, reason } : { status }),
    }),
  vacancyDetail: (id: string) => request<VacancyDetail>(`/vacancies/${id}`),
  vacancyCandidates: (id: string) => request<KanbanOut>(`/vacancies/${id}/candidates`),
  // Несколько фото для плиток вида «Плитки» (по порции показанных): {id кандидатуры:
  // адреса}. Тех, у кого фото меньше двух, в ответе нет. До 100 id за раз.
  candidateTilePhotos: (candidateIds: string[]) =>
    request<{ photos: Record<string, string[]> }>(`/candidates/tile-photos`, {
      method: "POST",
      body: JSON.stringify({ candidate_ids: candidateIds }),
    }),
  vacancyAnalytics: (id: string, q: AnalyticsQuery) =>
    request<VacancyAnalyticsBoard>(`/vacancies/${id}/analytics/board?${analyticsQs(q)}`),
  // Плановые нормы активности (на неделю); меняет только ответственный (403
  // остальным). Нулевая и пустая норма — «нормы нет».
  setVacancyNorms: (id: string, norms: Record<AnalyticsNormKey, number | null>) =>
    request<{ norms: Record<AnalyticsNormKey, number | null> }>(
      `/vacancies/${id}/analytics/norms`,
      { method: "PUT", body: JSON.stringify(norms) },
    ),
  // Сводка модели по тем же цифрам; text null — модель не ответила.
  vacancyAnalyticsDrill: (id: string, q: AnalyticsQuery, sel: AnalyticsDrillSelection, ids = false) =>
    request<AnalyticsDrill>(`/vacancies/${id}/analytics/drill?${drillQs(q, sel, ids)}`),
  vacancyAnalyticsSummary: (id: string, q: AnalyticsQuery) =>
    request<{ text: string | null }>(`/vacancies/${id}/analytics/summary?${analyticsQs(q)}`),

  // Landing admin (landings-service via nginx /api/admin/landings prefix).
  getLanding: async (vacancyId: string): Promise<LandingDetail | null> => {
    try {
      return await request<LandingDetail>(`/admin/landings/${encodeURIComponent(vacancyId)}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  /** One door for both shapes: a `.zip` (index.html + its media) or a single
   *  `.html` — the server sniffs the payload. An archive runs to hundreds of
   *  megabytes, hence XHR: `onProgress` reports the real upload percentage. */
  uploadLandingContent: (
    vacancyId: string,
    file: File,
    publicSlug?: string,
    onProgress?: (pct: number) => void,
  ) => {
    const form = new FormData();
    form.append("vacancy_id", vacancyId);
    // Public path on m.recruto.ru — hrdeva vacancies publish under their numeric
    // `number` so /566 renders (vacancy_id stays the opaque hrdeva:<uuid> key).
    if (publicSlug) form.append("public_slug", publicSlug);
    form.append("content", file);
    return xhrUpload<ContentUploadResult>("/admin/landings/content", form, onProgress);
  },
  setLandingForm: (vacancyId: string, cfg: FormConfig) =>
    request<LandingDetail>(`/admin/landings/${encodeURIComponent(vacancyId)}/form`, {
      method: "PUT",
      body: JSON.stringify(cfg),
    }),
  /** Превью ссылки: заголовок и описание. Пустая строка убирает текст. */
  setLandingShare: (vacancyId: string, text: { title: string; description: string }) =>
    request<LandingDetail>(`/admin/landings/${encodeURIComponent(vacancyId)}/share`, {
      method: "PUT",
      body: JSON.stringify(text),
    }),
  /** Обложка превью: PNG или JPG до 5 МБ; сервер проверяет сами байты. */
  uploadLandingCover: (vacancyId: string, file: File) =>
    rawUpload<LandingDetail>(
      `/admin/landings/${encodeURIComponent(vacancyId)}/cover`,
      fileForm(file),
      "PUT",
    ),
  deleteLandingCover: (vacancyId: string) =>
    request<LandingDetail>(`/admin/landings/${encodeURIComponent(vacancyId)}/cover`, {
      method: "DELETE",
    }),
  /** «Удалить»: лендинг снимается с публикации (по адресу — «вакансия закрыта»),
   *  файлы остаются. На его место можно загрузить, скопировать или создать новый. */
  deleteLanding: (vacancyId: string) =>
    request<void>(`/admin/landings/${encodeURIComponent(vacancyId)}`, { method: "DELETE" }),
  /** All landings. The copy picker matches them to vacancies by `public_slug`. */
  listLandings: () => request<{ items: LandingListItem[] }>("/admin/landings"),
  /** A simple landing: the vacancy's own title and description plus a form of
   *  name and phone. Server-side, no upload; 409 when the vacancy has one. */
  createDefaultLanding: (vacancyId: string) =>
    request<ContentUploadResult>("/admin/landings/default", {
      method: "POST",
      body: JSON.stringify({ vacancy_id: vacancyId }),
    }),
  /** A copy of another vacancy's landing — page, media and form. 409 when the
   *  target has a landing and `overwrite` is not set. */
  copyLanding: (sourceVacancyId: string, targetVacancyId: string, overwrite = false) =>
    request<LandingCopyResult>("/admin/landings/copy", {
      method: "POST",
      body: JSON.stringify({
        source_vacancy_id: sourceVacancyId,
        target_vacancy_id: targetVacancyId,
        overwrite,
      }),
    }),
  /** URL исходного загруженного HTML. Отдаётся как attachment: открывать
   *  чужой HTML в origin ATS — значит исполнить его скрипты рядом с сессией. */
  landingContentUrl: (vacancyId: string) =>
    `${API_BASE}/admin/landings/${encodeURIComponent(vacancyId)}/content`,
  /** The Claude Design brief — plain markdown, same for every vacancy. Served
   *  by landings-service so it cannot drift from the validators it describes. */
  getLandingDesignPrompt: async (): Promise<string> => {
    const res = await fetch(`${API_BASE}/admin/landings/design-prompt`, {
      credentials: "include",
    });
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return res.text();
  },
  uploadLandingMedia: (
    vacancyId: string,
    key: string,
    file: File,
    publicSlug?: string,
    onProgress?: (pct: number) => void,
  ) => {
    const form = fileForm(file);
    // Must match the slug `uploadLandingContent` used, else media lands under a
    // different prefix in MinIO than the html and preview 404s (hrdeva-native
    // vacancies: publish under numeric `number`, not the opaque uuid).
    if (publicSlug) form.append("public_slug", publicSlug);
    // The key is a path (`img/a/b.png`): slashes stay, each segment is escaped —
    // a key of an older landing may carry anything but quotes and spaces.
    const keyPath = key.split("/").map(encodeURIComponent).join("/");
    return xhrUpload<LandingMediaUploadResult>(
      `/admin/landings/${encodeURIComponent(vacancyId)}/media/${keyPath}`,
      form,
      onProgress,
      "PUT",
    );
  },
  updateVacancy: (id: string, payload: VacancyUpdatePayload) =>
    request<VacancyDetail>(`/vacancies/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  /** Сменить статус и/или итог — меню в строке списка и пилюли в шапке. Ответ
   *  короткий, не вся карточка. */
  changeVacancyState: (id: string, payload: { status?: VacancyStatus; outcome?: VacancyOutcome }) =>
    request<VacancyStateOut>(`/vacancies/${id}/state`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  listCandidates: (
    params: CandidatesFilterParams,
    limit: number,
    offset: number,
    signal?: AbortSignal,
  ) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && String(v).trim() !== "") p.set(k, String(v).trim());
    }
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return request<CandidatesPageOut>(`/candidates?${p.toString()}`, { signal });
  },

  // Счётчики рельса «Кандидаты»: статусы, типы воронок, этапы. Параметры те же, что у
  // listCandidates (без limit/offset); каждое измерение считается без своего фильтра.
  listCandidateFacets: (params: CandidatesFilterParams, signal?: AbortSignal) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && String(v).trim() !== "") p.set(k, String(v).trim());
    }
    return request<CandidatesFacets>(`/candidates-facets/funnels?${p.toString()}`, { signal });
  },

  // Значения и числа списков «Добавлен как», «Вакансия», «Рекрутер» рельса (PRI-267).
  // Параметры те же; значения трёх списков в них — через запятую.
  listCandidatePickers: (params: CandidatesFilterParams, signal?: AbortSignal) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && String(v).trim() !== "") p.set(k, String(v).trim());
    }
    return request<CandidatesPickers>(`/candidates-facets/pickers?${p.toString()}`, { signal });
  },

  // Подсказки поля «Вуз» в рельсе кандидатов: вузы людей с кандидатурами, частые вперёд.
  listUniversityHints: (q: string, signal?: AbortSignal) => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    return request<UniversityHint[]>(`/candidates-facets/universities?${p.toString()}`, { signal });
  },

  /**
   * Global search. `signal` is required by convention here: every caller goes
   * through `useLiveSearch`, which aborts the previous request on each
   * keystroke — without that, a slow answer overwrites a newer one.
   */
  search: (
    q: string,
    opts: { signal?: AbortSignal; entities?: SearchEntity[]; limit?: number } = {},
  ) => {
    const p = new URLSearchParams({ q });
    if (opts.entities?.length) p.set("entities", opts.entities.join(","));
    if (opts.limit) p.set("limit", String(opts.limit));
    return request<SearchOut>(`/search?${p.toString()}`, { signal: opts.signal });
  },

  // Разбор и сохранение — разные ручки: разбор долгий и ошибается, и рекрутёр
  // должен увидеть его результат до того, как он попадёт в базу.
  parseManualResume: (input: File | string) => {
    const form = new FormData();
    if (typeof input === "string") form.append("text", input);
    else form.append("file", input);
    return rawUpload<ParsedResumeOut>("/resumes/manual/parse", form);
  },
  // Резюме нашлось поиском, но кандидатуры на нём нет — завести её.
  // `placement` — воронка и этап НОВОЙ кандидатуры; без него «Приглашения», «Передан рекрутёру».
  attachResumeToVacancy: (hhId: string, vacancyId: string, placement?: CandidatePlacement) =>
    request<AttachResumeOut>(`/resume/${encodeURIComponent(hhId)}/attach/${vacancyId}`, {
      method: "POST",
      ...(placement ? { body: JSON.stringify(placement) } : {}),
    }),
  // Завести кандидатуру на вакансии из карточки человека (шаблон сообщения из ящика).
  addPersonCandidate: (personId: string, vacancyId: string, placement?: CandidatePlacement) =>
    request<{
      candidate_id: string;
      vacancy_id: string;
      vacancy_title: string;
      stage: string;
      status: string;
    }>(`/persons/${encodeURIComponent(personId)}/candidates`, {
      method: "POST",
      body: JSON.stringify({ vacancy_id: vacancyId, ...placement }),
    }),
  // `attempt` — одна метка на открытое окно: повтор после отказа вакансии
  // должен попасть в ту же строку резюме, а не завести второе.
  createManualResume: (
    fields: ManualResumeFields,
    vacancyId: string | null,
    attempt: string,
    placement?: CandidatePlacement,
  ) =>
    request<ManualResumeOut>("/resumes/manual", {
      method: "POST",
      body: JSON.stringify({ fields, vacancy_id: vacancyId, attempt, ...placement }),
    }),

  candidateDetail: (id: string) => request<CandidateDetail>(`/candidates/${id}`),
  /** «Удалить кандидата»: мягко помечает удалёнными ВСЕ кандидатуры этого
   *  человека (эталон: «пропадёт со всех этапов и вакансий»). Вернуть —
   *  `restoreCandidate`. */
  deleteCandidate: (id: string) =>
    request<CandidateDeleteOut>(`/candidates/${id}`, { method: "DELETE" }),
  restoreCandidate: (id: string) =>
    request<CandidateDeleteOut>(`/candidates/${id}/restore`, { method: "POST" }),
  resumeFull: (hhId: string) => request<ResumeFull>(`/resume/${hhId}`),
  // `person` — записи всех кандидатур этого человека (по всем его вакансиям), с
  // подписью вакансии и без дублей; по умолчанию — только этой кандидатуры.
  candidateRecordings: (id: string, scope: "candidate" | "person" = "candidate") =>
    request<{ items: RecordingBrief[] }>(
      `/candidates/${id}/recordings${scope === "person" ? "?scope=person" : ""}`,
    ),
  // Готовые записи всех кандидатур человека — без текста расшифровки: по ним
  // плашка видео встречи в чате узнаёт, что расшифровка кончилась.
  personRecordings: (personId: string) =>
    request<{ items: RecordingBrief[] }>(`/persons/${personId}/recordings`),
  recordingTranscript: (id: string) =>
    request<RecordingTranscript>(`/recordings/${id}/transcript`),
  // `speakers` given → exact count (null = auto) for this and later runs;
  // omitted → keep the stored count.
  transcribeRecording: (id: string, speakers?: number | null) =>
    request<{ status: string }>(`/recordings/${id}/transcribe`, {
      method: "POST",
      ...(speakers === undefined ? {} : { body: JSON.stringify({ speakers }) }),
    }),
  deleteRecording: (id: string) =>
    request<{ status: string }>(`/recordings/${id}`, { method: "DELETE" }),
  // `url` — готовый адрес из MediaItem.partsUrl (recordingPartsUrl или
  // publicRecordingPartsUrl), уже с /api.
  recordingParts: async (url: string, signal?: AbortSignal): Promise<{ parts: RecordingPartOut[] }> => {
    const res = await fetch(url, { credentials: "include", signal });
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return (await res.json()) as { parts: RecordingPartOut[] };
  },
  candidateAttachments: (id: string, scope: "candidate" | "person" = "candidate") =>
    request<{ items: AttachmentBrief[] }>(
      `/candidates/${id}/attachments${scope === "person" ? "?scope=person" : ""}`,
    ),
  deleteAttachment: (attachmentId: string) =>
    request<{ ok: boolean; noop?: boolean }>(`/attachments/${attachmentId}`, {
      method: "DELETE",
    }),
  attachChatMediaToCandidate: (
    candidateId: string,
    body: { key: string; name?: string; mime?: string },
  ) =>
    request<AttachmentBrief>(`/candidates/${candidateId}/attachments/from-chat`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  candidateManualNotes: (id: string) =>
    request<{ items: NoteBrief[] }>(`/candidates/${id}/manual-notes`),
  // Заказчику по ссылке — только отмеченные явно: по умолчанию комментарий
  // для команды (и комментарий из окна отказа — всегда).
  addManualNote: (id: string, text: string, visibleToCustomer = false) =>
    request<NoteBrief>(`/candidates/${id}/manual-notes`, {
      method: "POST",
      body: JSON.stringify({ text, visible_to_customer: visibleToCustomer }),
    }),
  // Автор или администратор, иначе 403 not_your_note. Ответ — комментарий целиком.
  updateManualNote: (noteId: string, patch: { text?: string; visible_to_customer?: boolean }) =>
    request<NoteBrief>(`/manual-notes/${noteId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteManualNote: (noteId: string) =>
    request<{ ok: boolean; noop?: boolean }>(`/manual-notes/${noteId}`, { method: "DELETE" }),
  candidateAiNotes: (id: string) =>
    request<{ summary: { text: string } | null; observations: AiNoteBrief[] }>(
      `/candidates/${id}/notes`,
    ),
  regenerateAiReview: (id: string) =>
    request<{ ai_review: AiReview }>(`/candidates/${id}/ai-review`, { method: "POST" }),
  runScreening: (id: string) =>
    request<ResumeScreeningOut>(`/candidates/${id}/resume-screening/run`, { method: "POST" }),

  // Client-facing share links (R6-S2). Server mints a tokenised /s/<token>
  // path; the modal composes the full URL as origin + link.path.
  listShareLinks: (candidateId: string) =>
    request<{ items: ShareLink[]; visits?: ShareVisit[] }>(`/candidates/${candidateId}/share-links`),
  // termDays: 0 — «Один раз», 1 / 3 / 10 / 30 — дней; не передан — без срока.
  createShareLink: (
    candidateId: string,
    visibility: Record<string, boolean | ContactsMode>,
    agency?: string,
    termDays?: number,
  ) =>
    request<ShareLink>(`/candidates/${candidateId}/share-links`, {
      method: "POST",
      body: JSON.stringify({ visibility, agency: agency || null, term_days: termDays ?? null }),
    }),
  revokeShareLink: (linkId: string) =>
    request<{ ok: boolean }>(`/share-links/${linkId}/revoke`, { method: "POST" }),
  publicCandidate: (token: string) =>
    request<PublicCandidate>(`/public/candidate/${token}`),
  // null takes the verdict back; `reason` only goes with a reject.
  publicCandidateDecision: (
    token: string,
    decision: ClientDecision | null,
    reason?: string | null,
    comment?: string | null,
  ) =>
    request<{
      decision: ClientDecision | null;
      decided_at: string | null;
      reason: string | null;
      comment: string | null;
    }>(`/public/candidate/${token}/decision`, {
      method: "POST",
      body: JSON.stringify({ decision, reason: reason ?? null, comment: comment ?? null }),
    }),
  // Комментарий заказчика без права решения: рекрутёру в ленту и в боты.
  publicCandidateComment: (token: string, comment: string) =>
    request<{ ok: boolean }>(`/public/candidate/${token}/comment`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    }),
  // Ready transcript of a recording shared on the link (text, segments, chapters).
  publicTranscript: (token: string, recordingId: string) =>
    request<RecordingTranscript>(`/public/candidate/${token}/recordings/${recordingId}/transcript`),
  // The client's page of a vacancy: one link, the candidates added to it
  // (ats-api vacancy_share_links.py).
  listVacancyShareLinks: (vacancyId: string) =>
    request<{ items: VacancyShareLink[]; visits?: ShareVisit[] }>(
      `/vacancies/${vacancyId}/share-links`,
    ),
  createVacancyShareLink: (
    vacancyId: string,
    visibility: Record<string, boolean | ContactsMode>,
    agency?: string,
    termDays?: number,
  ) =>
    request<VacancyShareLink>(`/vacancies/${vacancyId}/share-links`, {
      method: "POST",
      body: JSON.stringify({ visibility, agency: agency || null, term_days: termDays ?? null }),
    }),
  revokeVacancyShareLink: (linkId: string) =>
    request<{ ok: boolean }>(`/vacancy-share-links/${linkId}/revoke`, { method: "POST" }),
  addToVacancyShareLink: (linkId: string, candidateId: string) =>
    request<VacancyShareMember>(`/vacancy-share-links/${linkId}/candidates`, {
      method: "POST",
      body: JSON.stringify({ candidate_id: candidateId }),
    }),
  removeFromVacancyShareLink: (linkId: string, candidateId: string) =>
    request<{ ok: boolean }>(`/vacancy-share-links/${linkId}/candidates/${candidateId}`, {
      method: "DELETE",
    }),
  candidateVacancyShareLinks: (candidateId: string) =>
    request<{ items: CardVacancyShareLink[] }>(`/candidates/${candidateId}/vacancy-share-links`),
  publicVacancy: (token: string) => request<PublicVacancy>(`/public/vacancy/${token}`),
  // Страница заказчика открыта: визит для «Истории визитов» меню. `visitId` — тот,
  // что вкладка уже получила (повтор открытия не новый визит); `visit_id` null —
  // зашёл сотрудник, ничего не записано.
  publicVacancyVisit: (token: string, visitId?: string | null) =>
    request<{ visit_id: string | null }>(`/public/vacancy/${token}/visit`, {
      method: "POST",
      body: JSON.stringify({ visit_id: visitId ?? null }),
    }),
  // The client's visit to the public page (ats-api share_view_notify.py).
  // `view_id` is null for a signed-in employee: nothing is reported then.
  publicViewStart: (token: string) =>
    request<{ view_id: string | null }>(`/public/candidate/${token}/views`, { method: "POST" }),
  // `closed` — the server has ended this visit (the recruiter has already been
  // told about it) and took nothing from the batch: the page starts a new one.
  publicViewEvents: (token: string, viewId: string, events: ShareViewEvent[]) =>
    request<{ ok: boolean; closed?: boolean }>(`/public/candidate/${token}/views/${viewId}/events`, {
      method: "POST",
      body: JSON.stringify({ events }),
      // The tab may be on its way to the background — the request outlives it.
      keepalive: true,
    }),
  // The last batch, on pagehide.
  publicViewLeave: (token: string, viewId: string, events: ShareViewEvent[]) =>
    beacon(`/public/candidate/${token}/views/${viewId}/events`, { events }),


  // One keyset page of the inbox list (`cursor` = the previous page's
  // `next_cursor`). With `q`: only entries matched by name / phone /
  // username, in the same order.
  chatPeople: (
    filter: ChatListFilter,
    page: { limit?: number; cursor?: string | null } = {},
    signal?: AbortSignal,
  ) => {
    const p = chatFilterParams(filter);
    p.set("limit", String(page.limit ?? 30));
    if (page.cursor) p.set("cursor", page.cursor);
    return request<ChatPeopleOut>(`/chat/people?${p.toString()}`, { signal });
  },
  // `include` — the heavy parts (stage rows, clients) are computed only when
  // asked for: the panel asks while the «Этап воронки» / «Клиент» block is open.
  chatPeopleFacets: (filter: ChatListFilter, signal?: AbortSignal, include: ChatFacetPart[] = []) => {
    const p = chatFilterParams(filter);
    if (include.length) p.set("include", include.join(","));
    return request<ChatPeopleFacets>(`/chat/people/facets?${p.toString()}`, { signal });
  },
  // «Сообщения» section of the inbox search; `filter.q` is required (≥ 2 chars).
  chatSearchMessages: (
    filter: ChatListFilter,
    page: { limit?: number; cursor?: string | null } = {},
    signal?: AbortSignal,
  ) => {
    const p = chatFilterParams(filter);
    p.set("limit", String(page.limit ?? 30));
    if (page.cursor) p.set("cursor", page.cursor);
    return request<ChatMessageHitsOut>(`/chat/search/messages?${p.toString()}`, { signal });
  },
  // Transfer the chat; the server posts an «ответственный» notice into it.
  setChatOwner: (key: string, userId: string | null) =>
    request<{ owner_id: string | null }>(`/chat/people/${encodeURIComponent(key)}/owner`, {
      method: "PUT",
      body: JSON.stringify({ user_id: userId }),
    }),
  setChatActive: (key: string, active: boolean) =>
    request<{ active: boolean }>(`/chat/people/${encodeURIComponent(key)}/active`, {
      method: "PUT",
      body: JSON.stringify({ active }),
    }),
  // null = back to automatic (reference contact → ЛПР).
  setChatKind: (key: string, kind: ChatKind | null) =>
    request<{ kind: ChatKind; kind_manual: ChatKind | null }>(
      `/chat/people/${encodeURIComponent(key)}/kind`,
      { method: "PUT", body: JSON.stringify({ kind }) },
    ),
  // Участники и запросы решений / задачи (chat-service chat_collab).
  addChatParticipant: (key: string, userId: string) =>
    request<{ participants: ChatParticipant[] }>(
      `/chat/people/${encodeURIComponent(key)}/participants`,
      { method: "POST", body: JSON.stringify({ user_id: userId }) },
    ),
  chatRequests: (key: string) =>
    request<{ items: ChatRequest[] }>(`/chat/people/${encodeURIComponent(key)}/requests`),
  createChatRequest: (
    key: string,
    body: { kind: ChatRequestKind; type: string; assignee_id: string; note?: string | null },
  ) =>
    request<ChatRequest>(`/chat/people/${encodeURIComponent(key)}/requests`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  resolveChatRequest: (id: string) =>
    request<ChatRequest>(`/chat/requests/${encodeURIComponent(id)}/resolve`, { method: "POST" }),
  chatPersonThreads: (key: string) =>
    request<ChatPersonThreads>(`/chat/people/${encodeURIComponent(key)}`),
  // The chat is on screen: its unread counter drops to zero (for everyone).
  markChatRead: (key: string) =>
    request<{ ok: boolean }>(`/chat/people/${encodeURIComponent(key)}/read`, { method: "POST" }),
  chatBell: (personId: string) =>
    request<ChatBellState>(`/chat/people/${encodeURIComponent(personId)}/notify`),
  chatBellSet: (personId: string, enabled: boolean) =>
    request<ChatBellState>(`/chat/people/${encodeURIComponent(personId)}/notify`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),
  // Open (or reuse) an SMS thread. The only way to start a conversation with a
  // candidate who has nothing but a phone number — without a thread there is
  // no conversation to call from either.
  // `phone` — на какой из номеров человека (нет — первый по времени добавления).
  startSmsThread: (personId: string, phone?: string | null) =>
    request<{ thread_id: string; person_id: string | null; phone: string }>("/chat/sms/start", {
      method: "POST",
      body: JSON.stringify(phone ? { person_id: personId, phone } : { person_id: personId }),
    }),
  callConfig: () =>
    request<import("@/pages/Chats/softphone").CallConfig>("/chat/call/config"),
  callRecording: (id: string) => request<CallRecordingOut>(`/chat/call/recordings/${id}`),
  // «Повторить» in the call-recording modal: re-run a failed transcription.
  retryCallTranscript: (id: string) =>
    request<{ status: "processing" }>(`/chat/call/recordings/${id}/transcribe`, {
      method: "POST",
    }),
  // «Добавить запись кандидату» в окне записи звонка: сведённая дорожка
  // копируется в карточку кандидатуры аудиозаписью, с расшифровкой звонка.
  // Без `candidateId` сервер берёт самую свежую активную кандидатуру человека,
  // иначе самую свежую. Пару «звонок — кандидатура» не задваивает: повтор —
  // `created: false` и та же запись. Ошибки — callAttachErrorText.
  attachCallRecording: (callId: string, candidateId?: string | null) =>
    request<CallAttachOut>(`/call-recordings/${encodeURIComponent(callId)}/attach`, {
      method: "POST",
      body: JSON.stringify(candidateId ? { candidate_id: candidateId } : {}),
    }),
  // Каким кандидатурам человека этот звонок уже добавлен.
  callRecordingAttachments: (callId: string) =>
    request<CallAttachment[]>(`/call-recordings/${encodeURIComponent(callId)}/attachments`),
  // Входящие звонки: снимок на момент запроса. SSE не хранит историю, поэтому
  // вкладка, открытая (или переподключившаяся) посреди звонка, узнаёт о нём
  // только отсюда.
  inboundCalls: () => request<InboundCallsOut>("/chat/call/inbound"),
  // Разовый токен прослушки: браузер звонит на `sip:<target>@<domain>`, и АТС
  // подключает его к звонящему через ChanSpy. Ошибки — через inboundCallError.
  inboundListen: (id: string) =>
    request<{ target: string; token: string }>(
      `/chat/call/inbound/${encodeURIComponent(id)}/listen`,
      { method: "POST" },
    ),
  // Перехват: АТС перекидывает нашу линию прослушки в разговор со звонящим.
  // Повтор тем же токеном после победы — снова 200 (идемпотентно); 409 taken с
  // taken_by — успел другой (или наша же другая вкладка); 409 listen_lost —
  // бекенд этот токен не знает (перезапускался), слушать надо заново.
  inboundTake: (id: string, token: string) =>
    request<{ ok: boolean; call: InboundCall }>(
      `/chat/call/inbound/${encodeURIComponent(id)}/take`,
      { method: "POST", body: JSON.stringify({ token }) },
    ),
  // Исходящий: «этот номер набрал я». SIP-пользователь у всех рекрутёров один,
  // а запись разговора АТС присылает с одним номером — без метки звонок лёг бы
  // без владельца. Софтфон зовёт без ожидания и ошибки глотает: звонку она не
  // нужна. 400 bad_phone — номер не номер.
  markDial: (number: string) =>
    request<{ ok: boolean }>("/chat/calls/dial", {
      method: "POST",
      body: JSON.stringify({ number }),
    }),
  messageForm: (messageId: string) =>
    request<LandingFormOut>(`/chat/messages/${messageId}/form`),
  /** Визит на лендинг за строкой ленты (раскрытие «Открыл лендинг»). */
  messageLandingVisit: (messageId: string) =>
    request<LandingVisit>(`/chat/messages/${messageId}/landing-visit`),
  listTemplates: () => request<{ items: MessageTemplateItem[] }>("/templates"),
  createTemplate: (body: TemplateInput) =>
    request<MessageTemplateItem>("/templates", { method: "POST", body: JSON.stringify(body) }),
  updateTemplate: (id: string, body: TemplateInput) =>
    request<MessageTemplateItem>(`/templates/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteTemplate: (id: string) =>
    request<{ ok: boolean }>(`/templates/${id}`, { method: "DELETE" }),
  /** 422 `schedule_not_set` — в шаблоне есть {slots_link}, а расписание того,
   *  кто подставляет, не настроено: ссылке не на что вести. */
  renderTemplate: (
    id: string,
    ctx: { thread_id?: string; person_id?: string; candidate_id?: string; meeting_id?: string },
  ) =>
    request<{ text: string }>(`/templates/${id}/render`, {
      method: "POST",
      body: JSON.stringify(ctx),
    }),
  chatThreadMessages: (threadId: string, page?: ChatHistoryPage) => {
    const q = new URLSearchParams();
    if (page?.limit) q.set("limit", String(page.limit));
    if (page?.before) q.set("before", page.before);
    if (page?.beforeId) q.set("before_id", page.beforeId);
    if (page?.since) q.set("since", page.since);
    if (page?.sinceId) q.set("since_id", page.sinceId);
    if (page?.context != null) q.set("context", String(page.context));
    const qs = q.toString();
    return request<ChatThreadMessages>(
      `/chat/threads/${threadId}/messages${qs ? `?${qs}` : ""}`,
    );
  },
  // `signal`: the inbox gives up on a send that hangs (see Conversation).
  // `providerId` — написать через другое наше место (юзербот-аккаунт), а не
  // через привязанное к треду; удачная отправка перепривязывает тред к нему.
  // Не задан — как раньше, через привязанное.
  chatSend: (threadId: string, text: string, signal?: AbortSignal, providerId?: string | null) =>
    request<ChatSendResult>(`/chat/threads/${threadId}/send`, {
      method: "POST",
      body: JSON.stringify(providerId ? { text, provider_id: providerId } : { text }),
      signal,
    }),
  // Recruiter-attached file (Telegram / MAX bots): caption rides along.
  chatSendMedia: (
    threadId: string,
    file: File,
    caption?: string,
    signal?: AbortSignal,
    providerId?: string | null,
  ) => {
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    if (providerId) form.append("provider_id", providerId);
    return rawUpload<ChatSendResult>(`/chat/threads/${threadId}/send-media`, form, "POST", signal);
  },
  chatEditMessage: (threadId: string, messageId: string, text: string) =>
    request<{ ok: boolean }>(`/chat/threads/${threadId}/messages/${messageId}/edit`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  chatDeleteMessage: (threadId: string, messageId: string) =>
    request<{ ok: boolean }>(`/chat/threads/${threadId}/messages/${messageId}/delete`, {
      method: "POST",
    }),

  // ---- отложенная отправка (правая кнопка на стрелке «Отправить») ----
  // `when` — время отправки либо `{ beforeMeetingMin }`: за столько минут до
  // ближайшей встречи кандидата (60 — «За час», 1440 — «За день»). Отказ 409:
  // `no_upcoming_meeting` (встречи нет), `meeting_too_soon` (срок уже прошёл).
  chatScheduleText: (
    threadId: string,
    text: string,
    when: Date | { beforeMeetingMin: number },
    cancelOnReply = false,
  ) =>
    request<ScheduledChatMessage>(`/chat/threads/${threadId}/scheduled`, {
      method: "POST",
      body: JSON.stringify({
        text,
        ...(when instanceof Date
          ? { scheduled_at: when.toISOString() }
          : { before_meeting_min: when.beforeMeetingMin }),
        cancel_on_reply: cancelOnReply,
      }),
    }),
  chatScheduleMedia: (
    threadId: string,
    file: File,
    when: Date | { beforeMeetingMin: number },
    caption?: string,
    cancelOnReply = false,
  ) => {
    const form = new FormData();
    form.append("file", file);
    if (when instanceof Date) form.append("scheduled_at", when.toISOString());
    else form.append("before_meeting_min", String(when.beforeMeetingMin));
    if (caption) form.append("caption", caption);
    if (cancelOnReply) form.append("cancel_on_reply", "true");
    return rawUpload<ScheduledChatMessage>(
      `/chat/threads/${threadId}/scheduled-media`,
      form,
    );
  },
  chatScheduleUpdate: (
    scheduledId: string,
    body: { text?: string; scheduled_at?: string; cancel_on_reply?: boolean },
  ) =>
    request<ScheduledChatMessage>(`/chat/scheduled/${scheduledId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /** «Отправить срочно»: 502 (with the channel's error) leaves it queued. */
  chatScheduleSendNow: (scheduledId: string) =>
    request<ScheduledChatMessage>(`/chat/scheduled/${scheduledId}/send-now`, {
      method: "POST",
    }),
  chatScheduleCancel: (scheduledId: string) =>
    request<{ ok: boolean }>(`/chat/scheduled/${scheduledId}`, { method: "DELETE" }),

  // Прошедшие встречи человека без итога — карточки «Встреча прошла?» в ленте.
  // Список отдаётся голым массивом или под `items` — берём любой; чат без
  // человека и старый chat-service без ручки — пусто (ошибку ловит лента).
  meetingPrompts: (personKey: string) =>
    request<MeetingPrompt[] | { items?: MeetingPrompt[]; prompts?: MeetingPrompt[] }>(
      `/chat/people/${encodeURIComponent(personKey)}/meeting-prompts`,
    ).then((r) => (Array.isArray(r) ? r : (r?.items ?? r?.prompts ?? []))),

  // ---- напоминания в чате (карточка под звонком, «Напомнить», всплывашки) ----
  // Все напоминания человека, кроме superseded, новые сверху. Чат без
  // человека («thread:…») — пустой список.
  chatReminders: (personKey: string) =>
    request<{ reminders: ChatReminder[] }>(
      `/chat/people/${encodeURIComponent(personKey)}/reminders`,
    ),
  // Ручное: сразу active, уведомлять — меня. 409 orphan — у чата нет человека.
  chatReminderCreate: (
    personKey: string,
    body: { text: string; due_at: string; thread_id?: string },
  ) =>
    request<ChatReminder>(`/chat/people/${encodeURIComponent(personKey)}/reminders`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Принять предложение как есть (пустое тело) или с правкой — только
  // изменённые поля. 409 not_proposed — его уже разобрал кто-то другой.
  chatReminderAccept: (id: string, body: { text?: string; due_at?: string } = {}) =>
    request<ChatReminder>(`/chat/reminders/${id}/accept`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  chatReminderDismiss: (id: string) =>
    request<ChatReminder>(`/chat/reminders/${id}/dismiss`, { method: "POST" }),
  // Правка активного; новый срок — уведомление придёт снова. 409 not_active.
  chatReminderUpdate: (id: string, body: { text?: string; due_at?: string }) =>
    request<ChatReminder>(`/chat/reminders/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  chatReminderDone: (id: string) =>
    request<ChatReminder>(`/chat/reminders/${id}/done`, { method: "POST" }),
  chatReminderDelete: (id: string) =>
    request<ChatReminder>(`/chat/reminders/${id}`, { method: "DELETE" }),
  // Мои активные (`due` — только наступившие), с `person_name`: всплывашки
  // при загрузке и после переподключения потока.
  myReminders: (opts: { due?: boolean } = {}) =>
    request<{ reminders: ChatReminder[] }>(
      `/chat/reminders/mine${opts.due ? "?due=true" : ""}`,
    ),
  // Настройки → «Напоминания из звонков», только сисадмин (403 admin_only).
  getReminderSettings: () => request<ReminderSettings>("/chat/admin/reminder-settings"),
  putReminderSettings: (body: { auto_accept: boolean }) =>
    request<ReminderSettings>("/chat/admin/reminder-settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  // Настройки → «Автоматика: время и задержки», только сисадмин (403 admin_only).
  // PUT: ключ → число (задать) | null (сбросить к умолчанию); не прошло проверку —
  // 422 с `detail.errors` (ключ → что не так), ничего не записано.
  getAutomationTiming: () => request<AutomationTiming>("/settings/automation-timing"),
  putAutomationTiming: (values: Record<string, number | null>) =>
    request<AutomationTiming>("/settings/automation-timing", {
      method: "PUT",
      body: JSON.stringify({ values }),
    }),
  // Настройки → «Этапы по переписке», только сисадмин (403 admin_only).
  getExtensionSettings: () => request<ExtensionSettings>("/settings/extension"),
  putExtensionSettings: (values: Partial<ExtensionBehavior>) =>
    request<ExtensionSettings>("/settings/extension", {
      method: "PUT",
      body: JSON.stringify({ values }),
    }),

  getStageSuggestSettings: () =>
    request<StageSuggestSettings>("/chat/admin/stage-suggest-settings"),
  putStageSuggestSettings: (body: { auto_apply: boolean }) =>
    request<StageSuggestSettings>("/chat/admin/stage-suggest-settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // ---- Дева предлагает этап (карточка в ленте) ----
  // Предложения человека: ждущие решения и недавно разобранные, новые сверху.
  // Чат без человека («thread:…») — пустой список.
  chatStageSuggestions: (personKey: string) =>
    request<{ suggestions: StageSuggestion[] }>(
      `/chat/people/${encodeURIComponent(personKey)}/stage-suggestions`,
    ),
  // Пустое тело — как предложено; с правкой — обе части явно (applied_edited).
  // 409 not_proposed — уже разобрали; 409 stale — кандидатуру уже поменяли.
  chatStageSuggestionApply: (id: string, body: StageSuggestionApply = {}) =>
    request<StageSuggestion>(`/chat/stage-suggestions/${encodeURIComponent(id)}/apply`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  chatStageSuggestionDismiss: (id: string) =>
    request<StageSuggestion>(`/chat/stage-suggestions/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
    }),
  // «Вернуть» применённое само (auto_applied): этап и статус — как были до
  // Девы. 409 changed_since — кандидатуру с тех пор поменяли, вернуть нечего.
  chatStageSuggestionRevert: (id: string) =>
    request<StageSuggestion>(`/chat/stage-suggestions/${encodeURIComponent(id)}/revert`, {
      method: "POST",
    }),

  // Смена этапа / статуса для всплывашки владельцу чата (SSE `stage` — только id).
  stageEvent: (id: string) =>
    request<StageEventSnapshot>(`/chat/stage-events/${encodeURIComponent(id)}`),

  // Open (or reuse) a telegram_userbot thread the recruiter can type into.
  // Backend accepts EITHER tg_user_id (skip resolve) OR peer (username / +phone
  // / numeric id → resolved via tguser /internal/resolve).
  startTguserThread: (payload: {
    person_id?: string | null;
    peer?: string | null;
    tg_user_id?: number | null;
    username?: string | null;
    phone?: string | null;
  }) =>
    request<{ thread_id: string; person_id: string | null; tg_user_id: number }>(
      "/chat/threads/tguser/start",
      { method: "POST", body: JSON.stringify(payload) },
    ),

  // Начать переписку через гейтвейное место (MAX сегодня, WhatsApp тем же
  // путём завтра). Отдельно от `startTguserThread`: та ходит в наш же
  // Telethon-мост своим внутренним протоколом, эта — в контейнер по контракту.
  //
  // `found: false` — это НЕ ошибка, а ответ: человека может не быть в
  // мессенджере, и показать надо именно это.
  // `provider_id` — через какое наше место начать (нет — сервер берёт место
  // по умолчанию).
  startGatewayThread: (payload: {
    channel: string;
    identifier: string;
    person_id?: string | null;
    provider_id?: string | null;
  }) =>
    request<{
      found: boolean;
      reason?: string;
      thread_id?: string;
      person_id?: string | null;
      peer_id?: string;
    }>("/chat/threads/gateway/start", { method: "POST", body: JSON.stringify(payload) }),

  // Контакты прежних руководителей. По человеку, а не по кандидатуре: живут
  // они в переписке, а там известен собеседник.
  //
  // `includeDone` — и закрытые (обратную связь уже получили): лента кандидата
  // их не прячет, иначе прикреплённый звонок унёс бы с собой всю переписку с
  // ЛПР. `candidate_id` — кандидатура, к которой прикрепляются файлы из этой
  // переписки (самая свежая у человека); нет поля — старый ats-api.
  listReferences: (personId: string, opts: { includeDone?: boolean } = {}) =>
    request<{
      items: ReferenceContact[];
      experience: ReferenceExperience[];
      candidate_id?: string | null;
    }>(`/persons/${personId}/references${opts.includeDone ? "?include_done=1" : ""}`),
  createReference: (personId: string, body: ReferenceIn) =>
    request<ReferenceContact>(`/persons/${personId}/references`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateReference: (id: string, body: ReferenceIn) =>
    request<ReferenceContact>(`/references/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  // Отметить, что рекомендацию у этого человека взяли (`received=false` —
  // вернуть как было). Плашка в ленте остаётся, меняется только отметка.
  markReferenceFeedback: (id: string, received = true) =>
    request<ReferenceContact>(`/references/${id}/feedback?received=${received}`, {
      method: "POST",
    }),
  // Запись только что завершённого разговора по номеру. Пусто — ещё не
  // приехала от АТС; модалка на этом и ждёт. Иначе её не найти: id записи
  // приходит только внутри системного сообщения ленты, а оно в чужом треде.
  recentCallRecording: (number: string) =>
    request<{ recording: CallRecordingOut | null }>(
      `/chat/call/recordings/recent?number=${encodeURIComponent(number)}`,
    ),
  // Прикрепить запись разговора к кандидату как обратную связь. Контакт при
  // этом закрывается — рекомендацию у него взяли (`feedback_received_at` —
  // когда; дата первой рекомендации, повторный звонок её не сдвигает). Тот же
  // звонок второй раз не задваивается: `created: false` и та же запись. 404
  // `recording_not_found` — у записи ещё нет сведённой дорожки (АТС не
  // залила), 409 `no_candidate` — прикреплять не к кому.
  attachReferenceFeedback: (referenceId: string, callRecordingId: string) =>
    request<{
      recording_id: string;
      candidate_id: string;
      title: string;
      created?: boolean;
      feedback_received_at?: string | null;
    }>(
      `/references/${referenceId}/feedback-recording`,
      { method: "POST", body: JSON.stringify({ call_recording_id: callRecordingId }) },
    ),
  // Завести собеседника из контакта — в момент, когда рекрутёр раскрыл его
  // плашку в ленте. Лениво: `person_identifiers` уникальны на всю базу, и
  // заводить человека на каждый извлечённый контакт значило бы засорить
  // список людей. Идемпотентно: уже заведённый возвращается тот же.
  ensureReferencePerson: (id: string) =>
    request<{ person_id: string; name: string | null; phones: string[] }>(
      `/references/${id}/contact-person`,
      { method: "POST" },
    ),
  deleteReference: (id: string) =>
    request<{ ok: boolean }>(`/references/${id}`, { method: "DELETE" }),


  // Person card — only the contacts + socials fields the tguser start modal
  // needs for autofill. ats-api returns the full PersonCard; we type only
  // what's actually read to keep the surface small.
  getPersonCard: (id: string) =>
    request<PersonCardBrief>(`/persons/${id}`),

  // Раздел «Люди» (ats_api/people_page.py). Запрос строит pages/People/peopleLogic.
  listPeople: (query: URLSearchParams, signal?: AbortSignal) =>
    request<PeoplePageOut>(`/people?${query.toString()}`, { signal }),
  peopleFacets: () => request<PeopleFacets>("/people/facets"),
  getPeopleCard: (id: string, signal?: AbortSignal) =>
    request<PersonCardOut>(`/people/${encodeURIComponent(id)}`, { signal }),
  /** `value` is a link (its host names the platform) or a bare username under
   * `platform`. Re-adding a known profile returns the existing row. */
  addPersonSocial: (personId: string, platform: string, value: string) =>
    request<PersonSocial>(`/persons/${personId}/socials`, {
      method: "POST",
      body: JSON.stringify({ platform, value }),
    }),
  deletePersonSocial: (personId: string, socialId: string) =>
    request<{ ok: boolean }>(`/persons/${personId}/socials/${socialId}`, { method: "DELETE" }),


  // ---- Личное расписание рекрутёра (ats-api schedule.py) ----
  /** Расписание: без `userId` — своё; открыть и сохранить расписание коллеги
   *  может любой вошедший. */
  getSchedule: (userId?: string) => request<RecruiterSchedule>(schedulePath(userId)),
  /** Сохраняет расписание целиком. Встреч не отменяет: назначенные вне нового
   *  рабочего времени приходят в `conflicts`. 422 — недопустимое значение. */
  saveSchedule: (body: RecruiterScheduleIn, userId?: string) =>
    request<RecruiterSchedule>(schedulePath(userId), { method: "PUT", body: JSON.stringify(body) }),
  /** Расписание рекрутёра на вакансии: своё («Настраиваемое», `has_override`)
   *  или общее («Стандартное»). */
  getVacancySchedule: (vacancyId: string, userId?: string) =>
    request<RecruiterSchedule>(vacancySchedulePath(vacancyId, userId)),
  /** Сохраняет переопределение расписания на вакансии (полная замена недели,
   *  «доп. времени» и параметров; вырезы общего расписания действуют всегда). */
  saveVacancySchedule: (vacancyId: string, body: RecruiterScheduleIn, userId?: string) =>
    request<RecruiterSchedule>(vacancySchedulePath(vacancyId, userId), {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  /** Возврат на вакансии в «Стандартное»: переопределение удаляется. */
  resetVacancySchedule: (vacancyId: string, userId?: string) =>
    request<RecruiterSchedule>(vacancySchedulePath(vacancyId, userId), { method: "DELETE" }),
  /** Рекрутёры вакансии: чьё расписание даёт слоты записи. */
  vacancyRecruiters: (vacancyId: string) =>
    request<VacancyRecruiters>(`/vacancies/${vacancyId}/recruiters`),
  /** Заменяет список рекрутёров вакансии. Ответственный обязан быть в нём:
   *  иначе 422 `responsible_required`. */
  setVacancyRecruiters: (vacancyId: string, userIds: string[]) =>
    request<VacancyRecruiters>(`/vacancies/${vacancyId}/recruiters`, {
      method: "PUT",
      body: JSON.stringify({ user_ids: userIds }),
    }),
  /** Обзор «Расписание вакансии»: рекрутёры и объединённая неделя. */
  vacancyScheduleOverview: (vacancyId: string) =>
    request<VacancyScheduleOverview>(`/vacancies/${vacancyId}/schedule`),
  /** Тексты записи на встречу для вакансии: свои и общие. */
  vacancyScheduleMessages: (vacancyId: string) =>
    request<VacancyMessages>(`/vacancies/${vacancyId}/schedule-messages`),
  /** Сохранить тексты вакансии: ключ → текст; null или пустая строка — сбросить
   *  к общему. Ключей, которых нет в теле, правка не касается. */
  saveVacancyScheduleMessages: (vacancyId: string, messages: Record<string, string | null>) =>
    request<VacancyMessages>(`/vacancies/${vacancyId}/schedule-messages`, {
      method: "PUT",
      body: JSON.stringify({ messages }),
    }),
  /** Сотрудники для меню «Выбрать сотрудника». */
  schedulePeople: () => request<{ items: SchedulePerson[] }>("/schedule/people"),
  /** Слоты рекрутёра (без `userId` — свои) под кандидатуру. */
  scheduleSlots: (candidateId: string, userId?: string) => {
    const p = new URLSearchParams({ candidate_id: candidateId });
    if (userId) p.set("user_id", userId);
    return request<ScheduleSlots>(`/schedule/slots?${p.toString()}`);
  },
  /** Рекрутёр записывает кандидата в свой слот руками; у кандидатуры уже есть
   *  предстоящая встреча с ним — она переносится (`rescheduled`). 409
   *  `slot_taken` — слот заняли, 422 — его больше нет в расписании. */
  bookSlot: (body: { candidate_id: string; slot_id: string }) =>
    request<{ id: string; slot_start: string; slot_end: string; rescheduled: boolean }>(
      "/schedule/book",
      { method: "POST", body: JSON.stringify(body) },
    ),

  /** Итог прошедшей встречи из карточки «Встреча прошла?»: held — встреча
   *  проведена (этап «Встреча проведена», только вперёд), no_show — кандидат
   *  не пришёл (этап не трогается). 409 — встречу уже отметили или её слот
   *  ещё не начался. */
  meetingOutcome: (meetingId: string, outcome: MeetingOutcome) =>
    request<unknown>(`/meetings/${meetingId}/outcome`, {
      method: "POST",
      body: JSON.stringify({ outcome }),
    }),
  /** «Уже загружена» в той же карточке: запись встречи рекрутёр загрузил в
   *  карточку кандидата, а не кнопкой «Загрузить запись» — сервер привязывает
   *  новейшую такую (загруженную после начала встречи) к этой встрече: встреча
   *  проведена, этап «Встреча проведена» (только вперёд), видео в ленте — её.
   *  409 no_uploaded_recording — такой записи в карточке нет; остальные 409 —
   *  как у `meetingOutcome`. Повтор отдаёт ту же запись. */
  meetingLinkUploaded: (meetingId: string) =>
    request<MeetingLinkUploaded>(`/meetings/${meetingId}/link-uploaded`, { method: "POST" }),
  /** `vacancyId` сужает календарь до одной вакансии (её вкладка «Интервью»). */
  companyMeetings: (from: string, to: string, recruiterIds?: string[], vacancyId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (recruiterIds?.length) p.set("recruiter_ids", recruiterIds.join(","));
    if (vacancyId) p.set("vacancy_id", vacancyId);
    return request<{ meetings: CompanyMeeting[] }>(`/meetings?${p.toString()}`);
  },
  myMeetings: (from: string, to: string) =>
    request<{ meetings: CompanyMeeting[] }>(`/me/meetings?from=${from}&to=${to}`),
  // ---- Ящик встречи: подтвердить, отменить, перенести (ats-api meeting_actions.py) ----
  /** «Забрать встречу»: открытая встреча пула вакансии становится встречей
   *  вошедшего (ats-api pool_meetings.py). Кто первый. 409 `meeting_not_open` —
   *  уже забрали или отменили, `not_offered` — встреча ему не предлагалась,
   *  `recruiter_busy` — на это время у него уже встреча; 404 — встречи нет. */
  claimMeeting: (id: string) =>
    request<{
      id: string;
      status: string;
      pool_state: string | null;
      recruiter_id: string;
      recruiter_name: string;
      assigned: string[];
    }>(`/meetings/${id}/claim`, { method: "POST" }),
  /** «Подтвердить» запись, что ждала подтверждения. Повтор безвреден (`already`).
   *  409 — встреча уже не назначена. */
  confirmMeeting: (id: string) =>
    request<{ id: string; awaiting_confirmation: boolean; already: boolean }>(
      `/meetings/${id}/confirm`,
      { method: "POST" },
    ),
  /** «Отменить встречу»: этап кандидата не меняется, кандидату уходит уведомление
   *  без причины (`candidate_notice` — ушло ли). 409 `meeting_not_scheduled` /
   *  `meeting_started`. */
  cancelMeeting: (id: string, reason: MeetingCancelReason) =>
    request<{ id: string; status: string; reason: string; candidate_notice: CandidateNotice }>(
      `/meetings/${id}/cancel`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  /** «Перенести встречу»: эта встреча уходит в слот расписания её рекрутёра
   *  (`scheduleSlots(candidateId, recruiterId)`). Отказы — как у `bookSlot` плюс 409
   *  `meeting_not_scheduled` / `meeting_started`. */
  rescheduleMeeting: (id: string, slotId: string) =>
    request<{
      id: string;
      slot_start: string;
      slot_end: string;
      rescheduled: boolean;
      candidate_notice?: CandidateNotice;
    }>(`/meetings/${id}/reschedule`, { method: "POST", body: JSON.stringify({ slot_id: slotId }) }),
  /** Кандидат сейчас в комнате встречи (ждёт или уже говорит). `known: false` —
   *  узнать не вышло (комнаты нет, сервис не отвечает): плашку не показываем. */
  candidateInRoom: (id: string) =>
    request<{ here: boolean; known: boolean }>(`/meetings/${id}/video-room/candidate-here`),
  /** Флаг вакансии «подтверждать записи вручную» (по умолчанию выключен). */
  vacancyMeetingConfirmation: (vacancyId: string) =>
    request<{ confirm_bookings: boolean }>(`/vacancies/${vacancyId}/meeting-confirmation`),
  setVacancyMeetingConfirmation: (vacancyId: string, confirmBookings: boolean) =>
    request<{ confirm_bookings: boolean }>(`/vacancies/${vacancyId}/meeting-confirmation`, {
      method: "PUT",
      body: JSON.stringify({ confirm_bookings: confirmBookings }),
    }),

  /** «Начать встречу»: комната видеокабинета этой встречи и вход в неё.
   *  Первый вызов комнату заводит, следующие отдают ту же (ссылка кандидата
   *  не меняется) с новым токеном. Комната закончилась — заводится новая.
   *  409 — встреча отменена / не состоялась; 502 — сервис видеовстреч молчит.
   *
   *  Вызов без `linkOnly` — это сам кабинет: он объявляет, что умеет впускать,
   *  и ats-api приводит комнату ожидания к выбору рекрутёра. Кому нужна только
   *  ссылка (ящик встречи) — `linkOnly`: состояние ожидания комнаты, в которой
   *  кто-то сидит, такой вызов не трогает. */
  openVideoRoom: (meetingId: string, opts: { linkOnly?: boolean } = {}) =>
    request<VideoRoomJoin>(`/meetings/${meetingId}/video-room`, {
      method: "POST",
      body: JSON.stringify(opts.linkOnly ? { link_only: true } : { waiting_room_ui: true }),
    }),
  /** Впустить ждущего кандидата. `present: false` — его уже нет в комнате.
   *  422 — identity не кандидата этой комнаты. */
  admitToVideoRoom: (meetingId: string, identity: string) =>
    request<{ admitted: boolean; present: boolean }>(`/meetings/${meetingId}/video-room/admit`, {
      method: "POST",
      body: JSON.stringify({ identity }),
    }),
  /** Включить / выключить комнату ожидания; выбор запоминается за рекрутёром.
   *  Выключение впускает всех, кто ждёт. 409 `waiting_room_unsupported` —
   *  meet-service старый. */
  setVideoWaitingRoom: (meetingId: string, enabled: boolean) =>
    request<{ enabled: boolean }>(`/meetings/${meetingId}/video-room/waiting-room`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),
  /** Запись в кабинете. Начать можно после согласия кандидата: 409
   *  `candidate consent missing`; 409 `video_room_expired` — комнаты уже нет.
   *  `inPerson` — очная встреча без кандидата в комнате: согласие за него
   *  подтверждает рекрутёр (кабинет спрашивает его об этом до вызова). */
  videoRoomRecording: (meetingId: string, action: "start" | "stop", opts: { inPerson?: boolean } = {}) =>
    request<unknown>(`/meetings/${meetingId}/video-room/recording/${action}`, {
      method: "POST",
      ...(opts.inPerson ? { body: JSON.stringify({ in_person: true }) } : {}),
    }),
  /** Завершить встречу для всех: ссылка кандидата перестаёт работать. */
  endVideoRoom: (meetingId: string) =>
    request<unknown>(`/meetings/${meetingId}/video-room/end`, { method: "POST" }),
  /** Сисадмин: войти в идущую встречу слушателем. 403 `admin_only`; 409 —
   *  «room has no participants yet» (в кабинете никого), `listen_unsupported`
   *  (сервис видеовстреч ещё не умеет), `video_room_not_started` /
   *  `video_room_expired` (кабинета нет или он закончился); 502 — сервис молчит. */
  listenVideoRoom: (meetingId: string) =>
    request<VideoRoomListen>(`/meetings/${meetingId}/video-room/listen`, { method: "POST" }),
  /** Подсказки HRDeva этой встречи, старые первыми. */
  videoRoomHints: (meetingId: string) =>
    request<{ items: VideoRoomHint[] }>(`/meetings/${meetingId}/video-room/hints`),
  /** Попросить новые подсказки. `asked` — тексты уже заданных вопросов (отметки
   *  живут в браузере). 429 `hints_too_often` — рано; 502 `hints_unavailable` —
   *  модель недоступна или ответила не по форме. `new` — id новых подсказок. */
  requestVideoRoomHints: (meetingId: string, asked: string[]) =>
    request<{ items: VideoRoomHint[]; new: string[] }>(`/meetings/${meetingId}/video-room/hints`, {
      method: "POST",
      body: JSON.stringify({ asked }),
    }),
  /** Решение рекрутёра по подсказке: «спросил» (снимается) и «добавил в вопросы». */
  patchVideoRoomHint: (meetingId: string, hintId: string, patch: { asked?: boolean; added?: boolean }) =>
    request<VideoRoomHint>(`/meetings/${meetingId}/video-room/hints/${hintId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  /** Кто слушает встречу сейчас — по нему кабинет рекрутёра пишет «слушает …».
   *  Слушатель в комнате скрыт: из самой комнаты о нём не узнать. */
  videoRoomListeners: (meetingId: string) =>
    request<{ listeners: { name: string }[] }>(`/meetings/${meetingId}/video-room/listeners`),
  /** Сисадмин: какие встречи идут прямо сейчас (красный значок на карточке). */
  liveVideoRooms: () => request<{ items: LiveVideoRoom[] }>("/video-rooms/live"),
  /** Оценка встречи рекрутёром: 1–5 звёзд и заметка (до 1000 знаков). Своя
   *  оценка одна на встречу — повтор её меняет. Ответ — оценки встречи целиком,
   *  как в списках. 404 — встречи нет, 409 — это не встреча (запись звонка),
   *  403 — роли «Инструменты» оценки недоступны, 422 — оценка вне 1–5. */
  meetingFeedback: (meetingId: string, body: { rating: number; comment: string | null }) =>
    request<MeetingFeedback>(`/meetings/${meetingId}/feedback`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  interviewQuestions: (vacancyId: string) =>
    request<{ items: InterviewQuestion[] }>(`/vacancies/${vacancyId}/interview-questions`),
  /** Заменить список вопросов вакансии целиком; ответ — он же с id. */
  saveInterviewQuestions: (vacancyId: string, items: InterviewQuestionIn[]) =>
    request<{ items: InterviewQuestion[] }>(`/vacancies/${vacancyId}/interview-questions`, {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
  // Вопросы вакансии по каналам. Бот и интервью: PUT заменяет список канала
  // (id — обновить, без id — новый, нет в списке — убрать из списка). Анкета:
  // состав вопросов меняет `setLandingForm`, а сюда после его успеха уходят
  // только привязки {ext_key: имя поля, criterion_id, comment}; ошибки 409
  // landing_not_found, 422 unknown_form_field / criterion_not_found.
  // Тесты и дополнительные проверки (PRI-171): настройки вакансии, выдача
  // ссылки кандидату и отметки рекрутёра в карточке. 409 check_disabled /
  // check_empty / already_answered — у выдачи.
  vacancyChecks: (ref: string) => request<{ items: VacancyCheck[] }>(`/vacancies/${ref}/checks`),
  saveVacancyChecks: (ref: string, items: VacancyCheckIn[]) =>
    request<{ items: VacancyCheck[] }>(`/vacancies/${ref}/checks`, {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
  candidateChecks: (candidateId: string) =>
    request<{ items: CandidateCheck[] }>(`/candidates/${candidateId}/checks`),
  sendCandidateCheck: (candidateId: string, kind: "test" | "task", again = false) =>
    request<{ items: CandidateCheck[] }>(`/candidates/${candidateId}/checks/${kind}/send`, {
      method: "POST",
      body: JSON.stringify({ again }),
    }),
  decideCandidateCheck: (
    candidateId: string,
    kind: CheckKind,
    body: { status: CheckDecision | null; comment?: string | null },
  ) =>
    request<{ items: CandidateCheck[] }>(`/candidates/${candidateId}/checks/${kind}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  /** Страница кандидата `/t/<token>` — без входа. */
  publicCheck: (token: string) => request<PublicCheck>(`/public/checks/${encodeURIComponent(token)}`),
  answerPublicCheck: (token: string, body: { answers?: string[]; text?: string }) =>
    request<PublicCheck>(`/public/checks/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  vacancyQuestions: (ref: string) => request<VacancyQuestionsOut>(`/vacancies/${ref}/questions`),
  saveVacancyQuestions: (ref: string, channel: QuestionChannel, items: VacancyQuestionIn[]) =>
    request<{ items: VacancyQuestion[] }>(`/vacancies/${ref}/questions/${channel}`, {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
  /** Один вопрос в конец списка (окно «Добавить вопрос» у критерия). Вопрос
   *  анкеты так не добавить (409 form_field_via_landing): поле заводят в форме
   *  лендинга и затем привязывают через `saveVacancyQuestions(…, "form", …)`. */
  addVacancyQuestion: (
    ref: string,
    body: VacancyQuestionIn & { channel: Exclude<QuestionChannel, "form"> },
  ) =>
    request<VacancyQuestion>(`/vacancies/${ref}/questions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Сколько кандидатов уже ответили на вопрос — для окна «Удалить вопрос?». */
  vacancyQuestionAnswersCount: (ref: string, questionId: string) =>
    request<{ count: number }>(`/vacancies/${ref}/questions/${questionId}/answers-count`),

  /** `withActivity` — посчитать процент активности клиентов (тяжёлый расчёт,
   * нужен только странице «Клиенты»; выпадающим спискам не передавать). */
  listCompanies: (opts?: { withActivity?: boolean }) =>
    request<{ items: CompanyOut[] }>(`/companies${opts?.withActivity ? "?with_activity=1" : ""}`),
  listUsers: () => request<RecruiterBrief[]>("/users"),

  createCompany: (name: string) =>
    request<CompanyOut>("/companies", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  updateCompany: (
    id: string,
    payload: {
      name?: string;
      status?: "active" | "inactive";
      industry?: string;
      source?: string;
      city?: string;
      site?: string;
      description?: string;
      /** null снимает ответственного, пропуск поля не трогает. */
      manager_id?: string | null;
    },
  ) =>
    request<CompanyOut>(`/companies/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  listLegalEntities: () => request<{ items: LegalEntityOut[] }>("/legal-entities"),
  createLegalEntity: (payload: LegalEntityBody) =>
    request<LegalEntityOut>("/legal-entities", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateLegalEntity: (id: string, payload: LegalEntityBody) =>
    request<LegalEntityOut>(`/legal-entities/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteLegalEntity: (id: string) =>
    request<{ ok: boolean }>(`/legal-entities/${id}`, { method: "DELETE" }),

  listAllDocuments: () => request<DocumentListRow[]>("/documents"),
  listDocuments: (entityId: string) =>
    request<DocumentOut[]>(`/legal-entities/${entityId}/documents`),
  createDocument: (entityId: string, kind: DocumentKind, fields: Record<string, unknown>) =>
    request<DocumentDetailOut>(`/legal-entities/${entityId}/documents`, {
      method: "POST",
      body: JSON.stringify({ kind, fields }),
    }),
  getDocument: (docId: string) => request<DocumentDetailOut>(`/documents/${docId}`),
  updateDocument: (docId: string, fields: Record<string, unknown>) =>
    request<DocumentDetailOut>(`/documents/${docId}`, {
      method: "PATCH",
      body: JSON.stringify({ fields }),
    }),
  deleteDocument: (docId: string) =>
    request<{ status: string }>(`/documents/${docId}`, { method: "DELETE" }),
  getOrgSettings: () => request<OrgSettings>("/settings/org"),
  putOrgSettings: (value: OrgSettings) =>
    request<OrgSettings>("/settings/org", {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),

  createVacancy: (payload: VacancyCreatePayload) =>
    request<{ id: string }>("/vacancies", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  /** Поля формы допсоглашения (раздел «Документы») из вакансии: что рекрутёр
   *  ввёл при её создании — номер и дата ДС, суммы, гарантия, требования. */
  getVacancyAddendumDefaults: (ref: string) =>
    request<Record<string, unknown>>(`/vacancies/${encodeURIComponent(ref)}/addendum-defaults`),

  // Multipart uploads — no JSON content-type; browser sets the boundary.
  uploadCompanyLogo: (companyId: string, file: File) =>
    upload<CompanyOut>(`/companies/${companyId}/logo`, file),

  // Карточка клиента: контакты ЛПР, фото, видео и файлы (ats_api/company_card.py).
  listCompanyContacts: (companyId: string) =>
    request<{ items: CompanyContactOut[] }>(`/companies/${companyId}/contacts`),
  createCompanyContact: (companyId: string, body: CompanyContactBody) =>
    request<CompanyContactOut>(`/companies/${companyId}/contacts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateCompanyContact: (contactId: string, body: CompanyContactBody) =>
    request<CompanyContactOut>(`/company-contacts/${contactId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteCompanyContact: (contactId: string) =>
    request<{ ok: boolean }>(`/company-contacts/${contactId}`, { method: "DELETE" }),
  listCompanyMedia: (companyId: string) =>
    request<{ items: CompanyMediaOut[] }>(`/companies/${companyId}/media`),
  uploadCompanyMedia: (
    companyId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ) => xhrUpload<CompanyMediaOut>(`/companies/${companyId}/media`, fileForm(file), onProgress),
  deleteCompanyMedia: (mediaId: string) =>
    request<{ ok: boolean }>(`/company-media/${mediaId}`, { method: "DELETE" }),
  uploadMyAvatar: (file: File) => upload<RecruiterBrief>("/me/avatar", file),

  // Vacancy briefing («Вводные» + «Формализованные данные»).
  getBrief: (ref: string) => request<Brief>(`/vacancies/${ref}/brief`),
  /** Записка во «вводные». `question` — записка-вопрос (меню «+» шапки): её не
   *  разбирает модель, на стене она помечена как вопрос. */
  postBriefNote: (ref: string, text: string, opts: { question?: boolean } = {}) =>
    request<BriefNote>(`/vacancies/${ref}/brief/notes`, {
      method: "POST",
      body: JSON.stringify(opts.question ? { text, question: true } : { text }),
    }),
  uploadBriefMaterial: (ref: string, file: File, onProgress?: (pct: number) => void) =>
    xhrUpload<BriefNote>(`/vacancies/${ref}/brief/materials`, fileForm(file), onProgress),
  fieldHistory: (ref: string, key: string) =>
    request<FieldRevision[]>(`/vacancies/${ref}/brief/fields/${key}/history`),
  updateBriefField: (ref: string, key: string, value: string) =>
    request<Brief>(`/vacancies/${ref}/brief/fields/${encodeURIComponent(key)}`, {
      method: "PATCH",
      body: JSON.stringify({ value }),
    }),
  generateBrief: (ref: string) =>
    request<Brief>(`/vacancies/${ref}/brief/generate`, { method: "POST" }),
  /** Удалить сообщение или материал «Вводных» (только ответственный); ответ —
   *  пересчитанный бриф. */
  deleteBriefNote: (ref: string, noteId: string) =>
    request<Brief>(`/vacancies/${ref}/brief/notes/${noteId}`, { method: "DELETE" }),

  // Информация → «Рынок» и вкладка «Публикация».
  getVacancyMarket: (ref: string) => request<VacancyMarket>(`/vacancies/${ref}/market`),
  calculateVacancyMarket: (ref: string) =>
    request<VacancyMarket>(`/vacancies/${ref}/market/calculate`, { method: "POST" }),
  getPublication: (ref: string) => request<Publication>(`/vacancies/${ref}/publication`),
  putPublicationStyle: (ref: string, style: PublicationStyle) =>
    request<Publication>(`/vacancies/${ref}/publication/style`, {
      method: "PUT",
      body: JSON.stringify(style),
    }),
  // overwriteEdited — рекрутёр подтвердил замену блоков, которые правили руками
  // (без него сервер при правленных блоках отвечает 409).
  generatePublication: (ref: string, style?: PublicationStyle, overwriteEdited = false) =>
    request<Publication>(`/vacancies/${ref}/publication/generate`, {
      method: "POST",
      body: JSON.stringify({
        ...(style ? { style } : {}),
        ...(overwriteEdited ? { overwrite_edited: true } : {}),
      }),
    }),
  regeneratePublicationBlock: (ref: string, key: PublicationBlockKey, style?: PublicationStyle) =>
    request<Publication>(`/vacancies/${ref}/publication/blocks/${key}/regenerate`, {
      method: "POST",
      body: JSON.stringify(style ? { style } : {}),
    }),
  putPublicationBlock: (ref: string, key: PublicationBlockKey, text: string) =>
    request<Publication>(`/vacancies/${ref}/publication/blocks/${key}`, {
      method: "PUT",
      body: JSON.stringify({ text }),
    }),
  linkHhPublication: (ref: string, hhVacancyId: string) =>
    request<Publication>(`/vacancies/${ref}/hh-publications`, {
      method: "POST",
      body: JSON.stringify({ hh_vacancy_id: hhVacancyId }),
    }),
  unlinkHhPublication: (ref: string, mappingId: string) =>
    request<Publication>(`/vacancies/${ref}/hh-publications/${mappingId}`, { method: "DELETE" }),

  // Автоматизация → Отклики (4-stage funnel per vacancy).
  getAutomationReplies: (ref: string) =>
    request<AutomationRepliesOut>(`/vacancies/${ref}/automation/replies`),
  putAutomationReplies: (ref: string, cfg: AutomationRepliesConfig) =>
    request<AutomationRepliesConfig>(`/vacancies/${ref}/automation/replies`, {
      method: "PUT",
      body: JSON.stringify(cfg),
    }),
  // Бэкафилл застрявших кандидатов (automation никогда не ретроактивна).
  getBackfillAudit: (ref: string) =>
    request<BackfillAuditOut>(`/vacancies/${ref}/automation/backfill-audit`),
  runBackfill: (ref: string) =>
    request<BackfillRunOut>(`/vacancies/${ref}/automation/backfill`, { method: "POST" }),
  // Вакансия → Для кандидата (презентация вакансии + FAQ).
  getCandidateInfo: (ref: string) =>
    request<VacancyCandidateInfo>(`/vacancies/${ref}/candidate-info`),
  putCandidateInfo: (ref: string, info: VacancyCandidateInfoIn) =>
    request<VacancyCandidateInfo>(`/vacancies/${ref}/candidate-info`, {
      method: "PUT",
      body: JSON.stringify(info),
    }),
  /** Прогнать вопрос через тот же гейт, что стоит на автоответе. */
  testCandidateQuestion: (ref: string, messages: string[]) =>
    request<VacancyQuestionTest>(`/vacancies/${ref}/candidate-info/test-question`, {
      method: "POST",
      body: JSON.stringify({ messages }),
    }),
  // Вакансия → Отбор (сетка критериев) и «оценить кандидатов по критериям».
  getCriteria: (ref: string) =>
    request<{ items: VacancyCriterion[] }>(`/vacancies/${ref}/criteria`),
  putCriteria: (ref: string, items: VacancyCriterionIn[]) =>
    request<{ items: VacancyCriterion[] }>(`/vacancies/${ref}/criteria`, {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
  /** Разобрать неразобранные критерии; без `ids` — все такие. */
  formalizeCriteria: (ref: string, ids?: string[]) =>
    request<{ items: VacancyCriterion[] }>(`/vacancies/${ref}/criteria/formalize`, {
      method: "POST",
      body: JSON.stringify({ ids: ids ?? null }),
    }),
  generateCriteria: (ref: string) =>
    request<{ items: VacancyCriterionDraft[] }>(`/vacancies/${ref}/criteria/generate`, {
      method: "POST",
    }),
  /** `days` — кандидатуры за последние N дней; null — все. */
  getScreeningRun: (ref: string, days: number | null) =>
    request<ScreeningRunState>(
      `/vacancies/${ref}/screening-run${days == null ? "" : `?days=${days}`}`,
    ),
  startScreeningRun: (ref: string, days: number | null) =>
    request<ScreeningRunState>(`/vacancies/${ref}/screening-run`, {
      method: "POST",
      body: JSON.stringify({ days }),
    }),
  cancelScreeningRun: (ref: string) =>
    request<ScreeningRunState>(`/vacancies/${ref}/screening-run/cancel`, { method: "POST" }),
  // Inline template edit (SystemMessage.body) invoked from the «Изменить текст»
  // link next to each template dropdown. Returns the updated SystemMessage so
  // the caller can splice it back into the local templates list.
  updateSystemMessage: (key: string, body: string) =>
    request<{ key: string; body: string; label: string; category: string }>(
      `/system-messages/${encodeURIComponent(key)}`,
      { method: "PUT", body: JSON.stringify({ body }) },
    ),

  // Manual-review buttons («Подтвердить»/«Отказать») shown on the candidate
  // card when Candidate.manual_review_stage is non-null.
  postManualReview: (candidateId: string, decision: "confirm" | "reject") =>
    request<ManualReviewOut>(`/candidates/${candidateId}/manual-review`, {
      method: "POST",
      body: JSON.stringify({ decision }),
    }),

  // ---- Правая панель чатов (RP-CONTRACT §2–§3) ----
  // Всё о человеке одним запросом: кандидатуры с историей, встречи, файлы.
  personPanel: (personId: string) =>
    request<PersonPanelData>(`/persons/${encodeURIComponent(personId)}/panel`),
  // Карточка кандидата: звонки человека (панель «Звонки», значок пропущенных).
  personCalls: (personId: string) =>
    request<PersonCalls>(`/persons/${encodeURIComponent(personId)}/calls`),
  // «Редактировать»: части ФИО и ручные поля; PATCH отвечает тем же + `changed` —
  // строки «что было → что стало» (в карточке они же лягут комментарием).
  // Отказы с кодом: name_required, bad_birth_date, bad_experience, bad_salary,
  // *_too_long, candidate_not_found.
  personProfile: (personId: string) =>
    request<PersonProfile>(`/persons/${encodeURIComponent(personId)}/profile`),
  updatePersonProfile: (personId: string, body: PersonProfilePatch) =>
    request<PersonProfile & { changed: string[] }>(
      `/persons/${encodeURIComponent(personId)}/profile`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  // Дерево причин для диалога отказа: группы (с инициаторами), причины,
  // детали, подсказки. Сервер отдаёт с ETag, браузер каждый раз сверяется (304).
  rejectTree: (opts?: { includeInactive?: boolean }) =>
    request<RejectTree>(`/reject-reasons/tree${opts?.includeInactive ? "?include_inactive=1" : ""}`),
  // Справочник причин для редактора: все узлы, и выключенные, со счётчиками.
  // Каждая правка отвечает тем же телом.
  rejectDictionary: () => request<RejectDictionary>("/reject-dictionary"),
  createRejectNode: (body: RejectDictNodeCreate) =>
    request<RejectDictionary>("/reject-dictionary/nodes", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patchRejectNode: (id: string, body: RejectDictNodePatch) =>
    request<RejectDictionary>(`/reject-dictionary/nodes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteRejectNode: (id: string) =>
    request<RejectDictionary>(`/reject-dictionary/nodes/${id}`, { method: "DELETE" }),
  // Три стиля эталона (`kind: "style"`), за ними шаблоны «Системных сообщений».
  rejectTemplates: (candidateId: string) =>
    request<{ items: RejectTemplate[]; default_key: string | null }>(
      `/candidates/${candidateId}/reject-templates`,
    ),
  // Текст шаблона с подставленными именем и причинами — то, что уйдёт
  // кандидату (заметки и комментарий в него не попадают).
  rejectPreview: (candidateId: string, body: { template_key: string; reasons: RejectReasonIn[] }) =>
    request<{ text: string }>(`/candidates/${candidateId}/reject/preview`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // 409 phone_taken (номер у другого человека), 400 phone_not_a_number.
  addPersonPhone: (personId: string, body: { phone: string; label: string | null }) =>
    request<{ phone: string; label: string | null }>(
      `/persons/${encodeURIComponent(personId)}/phones`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  // Вложения переписки человека: корзина files|media, страницы по 100 от
  // новых к старым (`before` — next_before прошлой страницы). Счётчики — обеих
  // корзин сразу.
  personAttachments: (personKey: string, bucket: PersonAttachmentBucket, before?: string | null) => {
    const p = new URLSearchParams({ bucket, limit: "100" });
    if (before) p.set("before", before);
    return request<PersonAttachments>(
      `/chat/people/${encodeURIComponent(personKey)}/attachments?${p.toString()}`,
    );
  },
  // Якоря ленты для панели: полученные анкеты и строки о встречах.
  personEvents: (personKey: string) =>
    request<{ items: PersonEvent[] }>(`/chat/people/${encodeURIComponent(personKey)}/events`),

  // ---- Поиск под вакансию (ats-api sourcing.py, /api/sourcing/*) ----
  // Активные вакансии для экрана выбора: недавно искавшие — первыми.
  sourcingVacancies: () => request<{ items: SourcingVacancyRow[] }>("/sourcing/vacancies"),
  // `ref` — номер вакансии из адреса или её uuid.
  sourcingVacancy: (ref: string | number) =>
    request<SourcingVacancyState>(`/sourcing/vacancies/${encodeURIComponent(String(ref))}`),
  // Настройка поиска моделью — до минуты. `save` и у вакансии ещё нет поисков
  // — заводится «Автонастройка» (`search`); иначе параметры только отдаются.
  sourcingSetup: (vacancyId: string, save: boolean) =>
    request<SourcingSetupOut>(`/sourcing/vacancies/${encodeURIComponent(vacancyId)}/setup`, {
      method: "POST",
      body: JSON.stringify({ save }),
    }),
  // Имя — 1..120 символов, иначе 422. `is_default` снимает флаг с остальных.
  sourcingCreateSearch: (body: SourcingSearchBody) =>
    request<SourcingSavedSearch>("/sourcing/searches", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  sourcingUpdateSearch: (id: string, body: SourcingSearchPatch) =>
    request<SourcingSavedSearch>(`/sourcing/searches/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  sourcingDeleteSearch: (id: string) =>
    request<void>(`/sourcing/searches/${encodeURIComponent(id)}`, { method: "DELETE" }),
  // Один источник за запрос: медленный или упавший hh не держит свою базу.
  // Отказ источника — не исключение, а `error` в ответе 200.
  sourcingRun: (body: SourcingRunBody, signal?: AbortSignal) =>
    request<SourcingRunOut>("/sourcing/run", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),
  // Только числа под черновик параметров — ящик зовёт на каждую правку и
  // отменяет прошлый запрос.
  sourcingFacetCounts: (body: { params: SourcingParams }, signal?: AbortSignal) =>
    request<SourcingFacetCounts>("/sourcing/facet-counts", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),
  sourcingCount: (body: { vacancy_id: string; params: SourcingParams }, signal?: AbortSignal) =>
    request<SourcingCountOut>("/sourcing/count", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),
  // «Рекомендовать»: кандидатура на этапе «Рекомендовано», кандидату ничего не
  // уходит. У hh-резюме это тянет полное резюме и тратит просмотр. Отказы —
  // кодами attach: 400 vacancy_not_active, 422 person_not_resolved,
  // 404 resume_not_found.
  sourcingRecommend: (body: SourcingCardRef & { search_id?: string | null }) =>
    request<SourcingRecommendOut>("/sourcing/recommend", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // «Оценить показанных»: полный скрининг и скоринг Девой, до 20 резюме за
  // раз, в фоне по одному. 409 queue_full — очередь вакансии занята.
  sourcingEvaluate: (body: {
    vacancy_id: string;
    items: { source: SourcingSource; resume_id: string }[];
  }) =>
    request<SourcingEvaluateOut>("/sourcing/evaluate", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Состояние оценок — страница опрашивает, пока есть карточки в очереди.
  sourcingEvaluations: (
    body: { vacancy_id: string; items: { source: SourcingSource; resume_id: string }[] },
    signal?: AbortSignal,
  ) =>
    request<SourcingEvaluationsOut>("/sourcing/evaluations", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),
  // «Не подходит»: резюме скрыто из поиска этой вакансии у всей команды.
  // Повтор безвреден. `title` — что показать в списке скрытых.
  sourcingDismiss: (body: SourcingCardRef & { title?: string | null }) =>
    request<{ ok: boolean }>("/sourcing/dismiss", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  sourcingRestore: (body: SourcingCardRef) =>
    request<{ ok: boolean }>("/sourcing/restore", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Резюме своей базы для просмотра — только из кэша, в hh не ходит. Нет в
  // кэше — 404 resume_not_found.
  sourcingResume: (resumeId: string) =>
    request<{ view: ResumeView }>(`/sourcing/resume?resume_id=${encodeURIComponent(resumeId)}`),
  // Справочники hh для полей с подсказками. Регионы и навыки ищет сервер
  // (навыки — от двух символов), остальные отдаются целиком.
  sourcingAreas: (q: string, signal?: AbortSignal) =>
    request<{ items: SourcingDictItem[] }>(`/sourcing/dict/areas?q=${encodeURIComponent(q)}`, {
      signal,
    }),
  sourcingRoles: () => request<{ items: SourcingDictItem[] }>("/sourcing/dict/roles"),
  sourcingSkills: (q: string, signal?: AbortSignal) =>
    request<{ items: SourcingDictItem[] }>(`/sourcing/dict/skills?q=${encodeURIComponent(q)}`, {
      signal,
    }),
  sourcingUniversities: (q: string, signal?: AbortSignal) =>
    request<{ items: SourcingDictItem[] }>(
      `/sourcing/dict/universities?q=${encodeURIComponent(q)}`,
      { signal },
    ),
  sourcingCountries: () => request<{ items: SourcingDictItem[] }>("/sourcing/dict/countries"),
  sourcingLanguages: () => request<{ items: SourcingDictItem[] }>("/sourcing/dict/languages"),

  // ---- Поддержка (PRI-135) и обращения пользователей для администратора (PRI-136) ----
  // Числа для точек в меню: непрочитанные ответы поддержки и (у администратора)
  // обращения, ждущие ответа.
  supportSummary: () => request<SupportSummary>("/support/summary"),
  supportTickets: () => request<{ items: SupportTicketRow[] }>("/support/tickets"),
  supportCreate: (body: { kind: SupportKind; title: string; text: string }) =>
    request<SupportTicketDetail>("/support/tickets", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  supportTicket: (id: string) => request<SupportTicketDetail>(`/support/tickets/${id}`),
  // Автор открыл обращение — ответы прочитаны. `seen` — сколько непрочитанных
  // он увидел: пришедшие позже останутся непрочитанными. Отвечает числами меню.
  supportRead: (id: string, seen?: number) =>
    request<SupportSummary>(
      seen === undefined
        ? `/support/tickets/${id}/read`
        : `/support/tickets/${id}/read?seen=${seen}`,
      { method: "POST" },
    ),
  // Реплика автора: текст и/или картинка. 409 — обращение закрыто.
  supportWrite: (id: string, text: string, image?: File | null) =>
    rawUpload<SupportTicketDetail>(`/support/tickets/${id}/messages`, supportForm(text, image)),
  supportAdminList: (query: SupportAdminQuery, signal?: AbortSignal) => {
    const p = new URLSearchParams({ status: query.status });
    if (query.kind) p.set("kind", query.kind);
    if (query.company) p.set("company", query.company);
    if (query.user_id) p.set("user_id", query.user_id);
    if (query.q?.trim()) p.set("q", query.q.trim());
    return request<SupportAdminList>(`/support/admin/tickets?${p.toString()}`, { signal });
  },
  supportAdminTicket: (id: string) => request<SupportTicketDetail>(`/support/admin/tickets/${id}`),
  supportAdminReply: (id: string, text: string, image?: File | null) =>
    rawUpload<SupportTicketDetail>(
      `/support/admin/tickets/${id}/messages`,
      supportForm(text, image),
    ),
  supportAdminClose: (id: string) =>
    request<SupportTicketDetail>(`/support/admin/tickets/${id}/close`, { method: "POST" }),
};

// ---- Поддержка: типы ответов ats_api.support ----

export type SupportKind = "question" | "error" | "wish" | "access";
/** need — ждёт ответа поддержки; wait — ждёт пользователя; closed — закрыто. */
export type SupportStatus = "need" | "wait" | "closed";

export interface SupportTicketRow {
  id: string;
  kind: SupportKind;
  title: string;
  is_open: boolean;
  status: SupportStatus;
  /** Последняя активность (ISO) — по ней строка стоит в списке и подписана датой. */
  last_message_at: string;
  /** Хвост переписки одной строкой; у реплики без текста — «Изображение». */
  preview: string;
  /** Ответов поддержки, которые автор ещё не открыл. */
  unread: number;
  user_id: string;
  user_name: string;
  company: string | null;
}

export interface SupportMessage {
  id: string;
  /** Ответ поддержки (администратора), а не реплика автора обращения. */
  from_staff: boolean;
  text: string;
  /** Адрес картинки (с куками) или null. */
  image_url: string | null;
  created_at: string;
}

export interface SupportTicketDetail extends SupportTicketRow {
  messages: SupportMessage[];
}

export interface SupportSummary {
  unread: number;
  waiting: number;
}

export interface SupportAdminQuery {
  status: SupportStatus | "all";
  kind?: SupportKind | null;
  company?: string | null;
  user_id?: string | null;
  q?: string;
}

export interface SupportAdminList {
  items: SupportTicketRow[];
  /** Счётчики статусов по тем же фильтрам, но без самого статуса. */
  counts: { need: number; wait: number; closed: number; all: number };
  companies: { name: string; count: number }[];
}

function supportForm(text: string, image?: File | null): FormData {
  const form = new FormData();
  form.append("text", text);
  if (image) form.append("image", image);
  return form;
}

/** The one-file multipart body most upload endpoints take (field `file`). */
function fileForm(file: File): FormData {
  const form = new FormData();
  form.append("file", file);
  return form;
}

async function upload<T>(path: string, file: File): Promise<T> {
  return rawUpload<T>(path, fileForm(file));
}

// XHR upload with real upload-progress (fetch() can't report it) and no client
// timeout — for large bodies over a slow uplink: briefing materials (video/voice
// memos), a landing archive. The caller builds the FormData, as with rawUpload:
// field names vary per endpoint. `onProgress` reaches 100 once the body is sent,
// which is before the server has answered.
function xhrUpload<T>(
  path: string,
  form: FormData,
  onProgress?: (pct: number) => void,
  method = "POST",
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, API_BASE + path);
    xhr.withCredentials = true;
    xhr.timeout = 0;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.upload.onload = () => onProgress?.(100);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          resolve(undefined as T);
        }
      } else {
        let detail = xhr.statusText || `HTTP ${xhr.status}`;
        // The parsed body tells a server refusal from a proxy one: nginx
        // answers 413 with no JSON at all.
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText);
          const d = (body as { detail?: unknown } | null)?.detail;
          if (d && typeof d === "string") detail = d;
        } catch {
          /* non-JSON */
        }
        reject(new ApiError(xhr.status || 0, detail, body));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "соединение прервано при загрузке"));
    xhr.onabort = () => reject(new ApiError(0, "загрузка отменена"));
    xhr.send(form);
  });
}

// Multipart POST/PUT with a caller-built FormData (field names vary per
// endpoint). Method defaults to POST. No progress — for a body large enough to
// need one use xhrUpload above, as the landing uploads do.
async function rawUpload<T>(
  path: string,
  form: FormData,
  method = "POST",
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(API_BASE + path, {
    method,
    credentials: "include",
    body: form,
    signal,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.detail && typeof body.detail === "string") detail = body.detail;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

// Avatar/logo URLs are plain cookie-authed image endpoints.
export const companyLogoUrl = (companyId: string) =>
  `${API_BASE}/companies/${companyId}/logo`;
export const userAvatarUrl = (userId: string) => `${API_BASE}/users/${userId}/avatar`;
export const documentFileUrl = (docId: string) => `${API_BASE}/documents/${docId}/file`;
export const briefMaterialUrl = (noteId: string) => `${API_BASE}/brief/materials/${noteId}/raw`;
export const callRecordingAudioUrl = (id: string, leg: "mixed" | "rx" | "tx" = "mixed") =>
  `${API_BASE}/chat/call/recordings/${id}/audio?leg=${leg}`;
